import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Product,
  ProductCategory,
  ProductBaseDTO,
  PendantFields,
  PendantChainOption,
  PendantColorVariant,
  EngravingArea,
  EngravingChainByColor,
  EngravingColorVariant,
  EngravingFields,
  CupFields,
  LicensePlateFields,
  WalletFields,
  PhoneCoverFields,
  TshirtFields,
  TshirtColorVariant,
  GiftFields,
  ProductCreateOrUpdatePayload,
  Dimensions,
  VirtualTryOnConfig,
} from "../../types/products";
import { ProductsAPI } from "../../api/products";
import { assetUrl } from "../../api/http";
import EngravingCustomizationPreview, {
  type EngravingPreviewLayout,
} from "../orders/EngravingCustomizationPreview";
import {
  getContainedImageBounds,
  projectDisplayAreaToImageSpace,
  resolvePendantDisplayArea,
} from "../../utils/pendantPreview";
import VirtualTryOnAnchorEditor from "./VirtualTryOnAnchorEditor";

type EngravingObj = NonNullable<PendantFields["engraving"]>;
const withEngraving = (s: PendantFields): EngravingObj => ({
  enabled: s.engraving?.enabled ?? false,
  text: s.engraving?.text,
  font: s.engraving?.font,
  maxLength: s.engraving?.maxLength ?? 20,
  extraCost: s.engraving?.extraCost,
});
type PhotoCustomizationObj = NonNullable<PendantFields["photoCustomization"]>;
const withPhotoCustomization = (s: PendantFields): PhotoCustomizationObj => ({
  enabled: s.photoCustomization?.enabled ?? true,
  shape: s.photoCustomization?.shape ?? "circle",
  coordinateSpace: s.photoCustomization?.coordinateSpace ?? "container",
  xPct: s.photoCustomization?.xPct ?? 30,
  yPct: s.photoCustomization?.yPct ?? 30,
  widthPct: s.photoCustomization?.widthPct ?? 40,
  heightPct: s.photoCustomization?.heightPct ?? 40,
  centerXPct: s.photoCustomization?.centerXPct,
  centerYPct: s.photoCustomization?.centerYPct,
  radiusPct: s.photoCustomization?.radiusPct,
  cornerRadiusPct: s.photoCustomization?.cornerRadiusPct ?? 16,
  radiusPx: s.photoCustomization?.radiusPx,
  heartTopWidthPct: s.photoCustomization?.heartTopWidthPct ?? 34,
  heartNotchDepthPct: s.photoCustomization?.heartNotchDepthPct ?? 28,
  heartNotchWidthPct: s.photoCustomization?.heartNotchWidthPct ?? 8,
});
const withBackPhotoCustomization = (s: PendantFields): PhotoCustomizationObj => {
  const fallback = withPhotoCustomization(s);
  const back = s.backPhotoCustomization;
  return {
    enabled: back?.enabled ?? fallback.enabled,
    shape: back?.shape ?? fallback.shape,
    coordinateSpace: back?.coordinateSpace ?? fallback.coordinateSpace,
    xPct: back?.xPct ?? fallback.xPct,
    yPct: back?.yPct ?? fallback.yPct,
    widthPct: back?.widthPct ?? fallback.widthPct,
    heightPct: back?.heightPct ?? fallback.heightPct,
    centerXPct: back?.centerXPct ?? fallback.centerXPct,
    centerYPct: back?.centerYPct ?? fallback.centerYPct,
    radiusPct: back?.radiusPct ?? fallback.radiusPct,
    cornerRadiusPct: back?.cornerRadiusPct ?? fallback.cornerRadiusPct,
    radiusPx: back?.radiusPx ?? fallback.radiusPx,
    heartTopWidthPct: back?.heartTopWidthPct ?? fallback.heartTopWidthPct,
    heartNotchDepthPct: back?.heartNotchDepthPct ?? fallback.heartNotchDepthPct,
    heartNotchWidthPct: back?.heartNotchWidthPct ?? fallback.heartNotchWidthPct,
  };
};

const defaultVirtualTryOnConfig = (
  initial?: VirtualTryOnConfig
): VirtualTryOnConfig => ({
  enabled: Boolean(initial?.enabled),
  assetUrl: initial?.assetUrl,
  label: initial?.label,
  color: initial?.color,
  side:
    initial?.side === "front" || initial?.side === "back" || initial?.side === "default"
      ? initial.side
      : "default",
  anchors: {
    leftChain: initial?.anchors?.leftChain ?? { x: 0.12, y: 0.08 },
    rightChain: initial?.anchors?.rightChain ?? { x: 0.88, y: 0.08 },
    pendantTop: initial?.anchors?.pendantTop ?? { x: 0.5, y: 0.56 },
    pendantCenter: initial?.anchors?.pendantCenter ?? { x: 0.5, y: 0.72 },
    pendantBottom: initial?.anchors?.pendantBottom ?? { x: 0.5, y: 0.9 },
  },
  imageWidthPx: initial?.imageWidthPx,
  imageHeightPx: initial?.imageHeightPx,
  opaqueBounds: initial?.opaqueBounds,
  fullAssetBounds: initial?.fullAssetBounds,
  pendantBounds: initial?.pendantBounds ?? {
    x: 0.36,
    y: 0.56,
    width: 0.28,
    height: 0.32,
  },
  pendantBodyBounds: initial?.pendantBodyBounds ?? initial?.pendantBounds ?? {
    x: 0.36,
    y: 0.56,
    width: 0.28,
    height: 0.32,
  },
  photoRegion: initial?.photoRegion ?? {
    shape: "circle",
    x: 0.4,
    y: 0.64,
    width: 0.2,
    height: 0.2,
    rotation: 0,
  },
  physicalMeasurement: initial?.physicalMeasurement,
  photoCompositionMode: initial?.photoCompositionMode ?? "replace-region",
  photoClipping: initial?.photoClipping ?? true,
  frameOverlayUrl: initial?.frameOverlayUrl,
  assetPixelsPerMm: initial?.assetPixelsPerMm,
  physicalScaleCorrection:
    Number.isFinite(Number(initial?.physicalScaleCorrection)) &&
    Number(initial?.physicalScaleCorrection) > 0
      ? Number(initial?.physicalScaleCorrection)
      : 1,
  physicalScaleCalibratedAt: initial?.physicalScaleCalibratedAt,
  physicalScaleCalibrationNote: initial?.physicalScaleCalibrationNote,
  chainIncluded: initial?.chainIncluded ?? true,
  defaultScale: initial?.defaultScale ?? 1,
  verticalOffset: initial?.verticalOffset ?? 0,
  horizontalOffset: initial?.horizontalOffset ?? 0,
  rotationOffset: initial?.rotationOffset ?? 0,
  defaultVerticalOffset: initial?.defaultVerticalOffset ?? initial?.verticalOffset ?? 0,
  defaultHorizontalOffset:
    initial?.defaultHorizontalOffset ?? initial?.horizontalOffset ?? 0,
  defaultRotationOffset: initial?.defaultRotationOffset ?? initial?.rotationOffset ?? 0,
  pendantWidthMm: initial?.pendantWidthMm,
  pendantHeightMm: initial?.pendantHeightMm,
  pendantThicknessMm: initial?.pendantThicknessMm,
  chainLengthMm: initial?.chainLengthMm,
  chainLengthInches: initial?.chainLengthInches,
  wearOffsetXMm: initial?.wearOffsetXMm,
  wearOffsetYMm: initial?.wearOffsetYMm,
  wearDropMm: initial?.wearDropMm,
  wearRotationDeg: initial?.wearRotationDeg,
  variants: Array.isArray(initial?.variants)
    ? initial?.variants?.map((variant) => ({
        ...defaultVirtualTryOnConfig({ ...variant, variants: [] }),
        variants: undefined,
      })) || []
    : [],
});

const hasValidVirtualTryOnPhysicalMeasurement = (
  config?: VirtualTryOnConfig
) => {
  if (Number(config?.pendantWidthMm) > 0 || Number(config?.pendantHeightMm) > 0) {
    return true;
  }
  const measurement = config?.physicalMeasurement;
  if (!measurement) return false;
  const value = Number(measurement.value);
  if (!Number.isFinite(value) || value <= 0) return false;
  if (measurement.unit !== "mm" && measurement.unit !== "in") return false;
  const start = measurement.start;
  const end = measurement.end;
  if (!start || !end) return false;
  return Math.hypot(end.x - start.x, end.y - start.y) >= 0.001;
};

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|bmp|svg)$/i;
const THREE_EXT = /\.(glb|usdz)$/i;
const MAX_SEO_TITLE_LENGTH = 70;
const MAX_SEO_DESCRIPTION_LENGTH = 200;
const MAX_SEO_KEYWORDS = 100;
const MAX_SEO_KEYWORD_LENGTH = 50;
const MAX_SEO_SLUG_LENGTH = 120;

function isImageUrl(u?: string) {
  return !!u && IMAGE_EXT.test(u);
}
function isThreeUrl(u?: string) {
  return !!u && THREE_EXT.test(u);
}
function isImageFile(f?: File | null) {
  return !!f && f.type.startsWith("image/");
}

function getYoutubeEmbedUrl(url?: string) {
  const raw = String(url || "").trim();
  if (!raw) return "";
  try {
    const parsed = new URL(raw);
    const host = parsed.hostname.replace(/^www\./i, "").toLowerCase();
    let id = "";
    if (host === "youtu.be") {
      id = parsed.pathname.replace(/^\/+/, "").split("/")[0] || "";
    } else if (host === "youtube.com" || host.endsWith(".youtube.com")) {
      id =
        parsed.searchParams.get("v") ||
        parsed.pathname.match(/\/(?:embed|shorts)\/([^/?#]+)/)?.[1] ||
        "";
    }
    const cleanId = id.replace(/[^a-zA-Z0-9_-]/g, "");
    return cleanId ? `https://www.youtube-nocookie.com/embed/${cleanId}` : "";
  } catch {
    return "";
  }
}

function parseSeoKeywordsInput(value: string): string[] {
  const seen = new Set<string>();
  const keywords: string[] = [];

  for (const rawPart of value.split(/[\n,]+/)) {
    const keyword = String(rawPart || "")
      .replace(/\s+/g, " ")
      .trim()
      .slice(0, MAX_SEO_KEYWORD_LENGTH);
    if (!keyword) continue;
    const key = keyword.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    keywords.push(keyword);
    if (keywords.length >= MAX_SEO_KEYWORDS) break;
  }

  return keywords;
}

function normalizeSeoTextInput(value: string, maxLength: number): string {
  return value.replace(/\s+/g, " ").trim().slice(0, maxLength);
}

function normalizeSeoSlugInput(value: string): string {
  return normalizeSeoTextInput(value, MAX_SEO_SLUG_LENGTH)
    .toLowerCase()
    .replace(/['’]/g, "")
    .replace(/&/g, " and ")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

// ---------- styles ----------
const card: React.CSSProperties = {
  border: "1px solid #eee",
  borderRadius: 14,
  background: "#fff",
  padding: 16,
  display: "grid",
  gap: 12,
};
const grid2: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
};
const grid3: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "repeat(3,1fr)",
  gap: 12,
};
const row: React.CSSProperties = { display: "grid", gap: 8 };
const labelCss: React.CSSProperties = { fontWeight: 600, fontSize: 13 };
const inputCss: React.CSSProperties = {
  padding: "10px 12px",
  border: "1px solid #ddd",
  borderRadius: 10,
};
const selectCss = inputCss;
const switchRow: React.CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 8,
};
const sectionTitle: React.CSSProperties = { fontWeight: 700, fontSize: 14 };
const actions: React.CSSProperties = {
  display: "flex",
  gap: 8,
  justifyContent: "flex-end",
};
const primaryBtn: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  background: "#111",
  color: "#fff",
  border: "1px solid #111",
  fontWeight: 700,
  cursor: "pointer",
};
const outlineBtn: React.CSSProperties = {
  padding: "10px 14px",
  borderRadius: 10,
  background: "#fff",
  color: "#111",
  border: "1px solid #ddd",
  fontWeight: 600,
  cursor: "pointer",
};
const toolbarBtn: React.CSSProperties = {
  ...outlineBtn,
  padding: "7px 10px",
  borderRadius: 8,
  fontSize: 12,
};
const helperTextCss: React.CSSProperties = {
  color: "#64748b",
  fontSize: 12,
  lineHeight: 1.5,
};
const colorInputCss: React.CSSProperties = {
  width: 38,
  height: 32,
  borderRadius: 8,
  border: "1px solid #d1d5db",
  background: "#fff",
  padding: 2,
  cursor: "pointer",
};
const thumb: React.CSSProperties = {
  width: 96,
  height: 96,
  objectFit: "cover" as const,
  borderRadius: 10,
  border: "1px solid #eee",
  background: "#fafafa",
};
const galleryWrap: React.CSSProperties = {
  display: "flex",
  gap: 10,
  flexWrap: "wrap",
};

// ---------- helpers ----------
type Mode = "create" | "edit";

function num(v: any): number | undefined {
  if (v === "" || v === null || v === undefined) return undefined;
  const n = Number(v);
  return Number.isFinite(n) ? n : undefined;
}

const roundDownMoney = (value: number) =>
  Math.floor((Number(value) + Number.EPSILON) * 100) / 100;
const roundUpPercentValue = (value: number, precision = 2) => {
  const factor = 10 ** precision;
  return Math.ceil((Number(value) + Number.EPSILON) * factor) / factor;
};

const parseNumberCsv = (value: string): number[] =>
  String(value || "")
    .split(/,|\||\//)
    .map((part) => Number(part.trim()))
    .filter((n) => Number.isFinite(n) && n > 0);

const parseTextCsv = (value: string): string[] =>
  String(value || "")
    .split(",")
    .map((part) => part.trim())
    .filter(Boolean);

type CircleAreaDraft = {
  shape: "circle" | "heart";
  coordinateSpace: "container" | "image";
  xPct: number;
  yPct: number;
  widthPct: number;
  heightPct: number;
  centerXPct?: number;
  centerYPct?: number;
  radiusPct?: number;
  lockAspectRatio: boolean;
  heartTopWidthPct: number;
  heartNotchDepthPct: number;
  heartNotchWidthPct: number;
};

const clampPct = (value: number, min: number, max: number) =>
  Math.min(max, Math.max(min, value));

const CIRCLE_MATCH_TOLERANCE_PCT = 0.5;

const toPendantShape = (value: unknown): "circle" | "heart" => {
  const normalized = String(value || "").toLowerCase();
  return normalized === "heart" ? "heart" : "circle";
};

const hasCircleMetrics = (
  draft: Pick<Partial<CircleAreaDraft>, "centerXPct" | "centerYPct" | "radiusPct">
) =>
  Number.isFinite(Number(draft.centerXPct)) &&
  Number(draft.centerXPct) >= 0 &&
  Number(draft.centerXPct) <= 100 &&
  Number.isFinite(Number(draft.centerYPct)) &&
  Number(draft.centerYPct) >= 0 &&
  Number(draft.centerYPct) <= 100 &&
  Number.isFinite(Number(draft.radiusPct)) &&
  Number(draft.radiusPct) > 0 &&
  Number(draft.radiusPct) <= 50;

const looksLikeLockedCircle = (
  draft: Partial<
    Pick<
      CircleAreaDraft,
      "widthPct" | "heightPct" | "centerXPct" | "centerYPct" | "radiusPct"
    >
  >
) =>
  hasCircleMetrics(draft) ||
  Math.abs(Number(draft.widthPct ?? 40) - Number(draft.heightPct ?? 40)) <=
    CIRCLE_MATCH_TOLERANCE_PCT;

const resolveHeartDraftTuning = (draft: Partial<CircleAreaDraft>) => {
  const heartTopWidthPct = clampPct(
    Number(draft.heartTopWidthPct ?? 34),
    18,
    70
  );
  const heartNotchDepthPct = clampPct(
    Number(draft.heartNotchDepthPct ?? 28),
    8,
    55
  );
  const maxNotchWidth = Math.max(2, Math.min(24, heartTopWidthPct - 6));
  const heartNotchWidthPct = clampPct(
    Number(draft.heartNotchWidthPct ?? 8),
    2,
    maxNotchWidth
  );
  return {
    heartTopWidthPct: Number(heartTopWidthPct.toFixed(2)),
    heartNotchDepthPct: Number(heartNotchDepthPct.toFixed(2)),
    heartNotchWidthPct: Number(heartNotchWidthPct.toFixed(2)),
  };
};

const buildHeartEditorPath = (draft: Partial<CircleAreaDraft>) => {
  const { heartTopWidthPct, heartNotchDepthPct, heartNotchWidthPct } =
    resolveHeartDraftTuning(draft);

  const halfTop = heartTopWidthPct / 2;
  const leftPeakX = 50 - halfTop;
  const rightPeakX = 50 + halfTop;
  const notchHalf = heartNotchWidthPct / 2;
  const notchLeftX = 50 - notchHalf;
  const notchRightX = 50 + notchHalf;
  const notchY = heartNotchDepthPct;
  const peakY = clampPct(notchY - 17, 2, notchY - 2);
  const innerControlY = clampPct(notchY - 12, peakY + 2, notchY - 2);
  const shoulderY = clampPct(notchY - 4, peakY + 2, 36);
  const leftShoulderX = clampPct(leftPeakX - 13, 4, leftPeakX - 2);
  const rightShoulderX = 100 - leftShoulderX;

  return [
    "M 50 95",
    `C 8 70 2 42 ${leftShoulderX.toFixed(2)} ${shoulderY.toFixed(2)}`,
    `C ${leftPeakX.toFixed(2)} ${peakY.toFixed(2)} ${notchLeftX.toFixed(
      2
    )} ${innerControlY.toFixed(2)} 50 ${notchY.toFixed(2)}`,
    `C ${notchRightX.toFixed(2)} ${innerControlY.toFixed(2)} ${rightPeakX.toFixed(
      2
    )} ${peakY.toFixed(2)} ${rightShoulderX.toFixed(2)} ${shoulderY.toFixed(2)}`,
    "C 98 42 92 70 50 95",
    "Z",
  ].join(" ");
};

const normalizeCircleAreaDraft = (
  input: Partial<CircleAreaDraft>
): CircleAreaDraft => {
  const shape = toPendantShape(input.shape);
  const coordinateSpace =
    String(input.coordinateSpace || "").toLowerCase() === "image"
      ? "image"
      : "container";
  const rawWidth = Number(input.widthPct ?? 40);
  const rawHeight = Number(input.heightPct ?? 40);
  let widthPct = clampPct(Number.isFinite(rawWidth) ? rawWidth : 40, 5, 95);
  let heightPct = clampPct(Number.isFinite(rawHeight) ? rawHeight : 40, 5, 95);
  const lockAspectRatio =
    typeof input.lockAspectRatio === "boolean"
      ? input.lockAspectRatio
      : shape === "circle"
      ? looksLikeLockedCircle(input)
      : false;
  const hasExplicitCircleMetrics =
    shape === "circle" &&
    coordinateSpace === "image" &&
    hasCircleMetrics(input);
  const preserveExplicitCircleMetrics =
    hasExplicitCircleMetrics && lockAspectRatio;

  const rawX = Number(input.xPct ?? 30);
  const rawY = Number(input.yPct ?? 30);
  let xPct = clampPct(Number.isFinite(rawX) ? rawX : 30, 0, 100 - widthPct);
  let yPct = clampPct(Number.isFinite(rawY) ? rawY : 30, 0, 100 - heightPct);
  if (shape === "circle" && lockAspectRatio && !preserveExplicitCircleMetrics) {
    const centerXPct = xPct + widthPct / 2;
    const centerYPct = yPct + heightPct / 2;
    const diameterPct = clampPct((widthPct + heightPct) / 2, 5, 95);
    widthPct = diameterPct;
    heightPct = diameterPct;
    xPct = clampPct(centerXPct - diameterPct / 2, 0, 100 - diameterPct);
    yPct = clampPct(centerYPct - diameterPct / 2, 0, 100 - diameterPct);
  }
  const heartTuning = resolveHeartDraftTuning(input);
  const circleCenterXPct = preserveExplicitCircleMetrics
    ? Number(clampPct(Number(input.centerXPct), 0, 100).toFixed(2))
    : Number((xPct + widthPct / 2).toFixed(2));
  const circleCenterYPct = preserveExplicitCircleMetrics
    ? Number(clampPct(Number(input.centerYPct), 0, 100).toFixed(2))
    : Number((yPct + heightPct / 2).toFixed(2));
  const circleRadiusPct = preserveExplicitCircleMetrics
    ? Number(clampPct(Number(input.radiusPct), 2.5, 50).toFixed(2))
    : lockAspectRatio
    ? Number((Math.min(widthPct, heightPct) / 2).toFixed(2))
    : undefined;

  return {
    shape,
    coordinateSpace,
    xPct: Number(xPct.toFixed(2)),
    yPct: Number(yPct.toFixed(2)),
    widthPct: Number(widthPct.toFixed(2)),
    heightPct: Number(heightPct.toFixed(2)),
    centerXPct:
      shape === "circle"
        ? circleCenterXPct
        : Number.isFinite(Number(input.centerXPct)) && Number(input.centerXPct) >= 0
        ? Number(Number(input.centerXPct).toFixed(2))
        : undefined,
    centerYPct:
      shape === "circle"
        ? circleCenterYPct
        : Number.isFinite(Number(input.centerYPct)) && Number(input.centerYPct) >= 0
        ? Number(Number(input.centerYPct).toFixed(2))
        : undefined,
    radiusPct:
      shape === "circle"
        ? circleRadiusPct
        : Number.isFinite(Number(input.radiusPct)) && Number(input.radiusPct) > 0
        ? Number(Number(input.radiusPct).toFixed(2))
        : undefined,
    lockAspectRatio,
    heartTopWidthPct: heartTuning.heartTopWidthPct,
    heartNotchDepthPct: heartTuning.heartNotchDepthPct,
    heartNotchWidthPct: heartTuning.heartNotchWidthPct,
  };
};

const applyCircleAreaWidthValue = (
  draft: CircleAreaDraft,
  widthPct: number
) => {
  const nextWidth = Number(widthPct);
  if (draft.lockAspectRatio) {
    const ratio = draft.heightPct / Math.max(0.0001, draft.widthPct);
    if (
      draft.shape === "circle" &&
      draft.coordinateSpace === "image" &&
      hasCircleMetrics(draft)
    ) {
      const nextHeight = nextWidth * ratio;
      const centerXPct = Number(draft.centerXPct);
      const centerYPct = Number(draft.centerYPct);
      const scale = nextWidth / Math.max(0.0001, draft.widthPct);
      const nextRadius = Number(draft.radiusPct) * scale;
      return normalizeCircleAreaDraft({
        ...draft,
        xPct: centerXPct - nextWidth / 2,
        yPct: centerYPct - nextHeight / 2,
        widthPct: nextWidth,
        heightPct: nextHeight,
        centerXPct,
        centerYPct,
        radiusPct: nextRadius,
      });
    }
    return normalizeCircleAreaDraft({
      ...draft,
      widthPct: nextWidth,
      heightPct: nextWidth * ratio,
    });
  }
  return normalizeCircleAreaDraft({
    ...draft,
    widthPct: nextWidth,
  });
};

const applyCircleAreaHeightValue = (
  draft: CircleAreaDraft,
  heightPct: number
) => {
  const nextHeight = Number(heightPct);
  if (draft.lockAspectRatio) {
    const ratio = draft.widthPct / Math.max(0.0001, draft.heightPct);
    if (
      draft.shape === "circle" &&
      draft.coordinateSpace === "image" &&
      hasCircleMetrics(draft)
    ) {
      const nextWidth = nextHeight * ratio;
      const centerXPct = Number(draft.centerXPct);
      const centerYPct = Number(draft.centerYPct);
      const scale = nextHeight / Math.max(0.0001, draft.heightPct);
      const nextRadius = Number(draft.radiusPct) * scale;
      return normalizeCircleAreaDraft({
        ...draft,
        xPct: centerXPct - nextWidth / 2,
        yPct: centerYPct - nextHeight / 2,
        widthPct: nextWidth,
        heightPct: nextHeight,
        centerXPct,
        centerYPct,
        radiusPct: nextRadius,
      });
    }
    return normalizeCircleAreaDraft({
      ...draft,
      widthPct: nextHeight * ratio,
      heightPct: nextHeight,
    });
  }
  return normalizeCircleAreaDraft({
    ...draft,
    heightPct: nextHeight,
  });
};

const circleAreaDraftFromPendant = (source: PendantFields): CircleAreaDraft => {
  const pc = withPhotoCustomization(source);
  return normalizeCircleAreaDraft({
    shape: toPendantShape(pc.shape),
    coordinateSpace: pc.coordinateSpace ?? "container",
    xPct: Number(pc.xPct ?? 30),
    yPct: Number(pc.yPct ?? 30),
    widthPct: Number(pc.widthPct ?? 40),
    heightPct: Number(pc.heightPct ?? 40),
    centerXPct: Number(pc.centerXPct ?? NaN),
    centerYPct: Number(pc.centerYPct ?? NaN),
    radiusPct: Number(pc.radiusPct ?? NaN),
    lockAspectRatio: toPendantShape(pc.shape) === "circle" ? looksLikeLockedCircle(pc) : false,
    heartTopWidthPct: Number(pc.heartTopWidthPct ?? 34),
    heartNotchDepthPct: Number(pc.heartNotchDepthPct ?? 28),
    heartNotchWidthPct: Number(pc.heartNotchWidthPct ?? 8),
  });
};

const circleAreaDraftFromPendantBack = (source: PendantFields): CircleAreaDraft => {
  const pc = withBackPhotoCustomization(source);
  return normalizeCircleAreaDraft({
    shape: toPendantShape(pc.shape),
    coordinateSpace: pc.coordinateSpace ?? "container",
    xPct: Number(pc.xPct ?? 30),
    yPct: Number(pc.yPct ?? 30),
    widthPct: Number(pc.widthPct ?? 40),
    heightPct: Number(pc.heightPct ?? 40),
    centerXPct: Number(pc.centerXPct ?? NaN),
    centerYPct: Number(pc.centerYPct ?? NaN),
    radiusPct: Number(pc.radiusPct ?? NaN),
    lockAspectRatio: toPendantShape(pc.shape) === "circle" ? looksLikeLockedCircle(pc) : false,
    heartTopWidthPct: Number(pc.heartTopWidthPct ?? 34),
    heartNotchDepthPct: Number(pc.heartNotchDepthPct ?? 28),
    heartNotchWidthPct: Number(pc.heartNotchWidthPct ?? 8),
  });
};

const circleAreaDraftFromPhotoCustomization = (
  area: PendantFields["photoCustomization"] | undefined,
  fallback: PendantFields
): CircleAreaDraft => {
  if (!area) return circleAreaDraftFromPendant(fallback);
  return circleAreaDraftFromPendant({
    photoUploadAllowed: fallback.photoUploadAllowed,
    photoCustomization: area,
  });
};

const circleAreaDraftFromBackPhotoCustomization = (
  area: PendantFields["backPhotoCustomization"] | undefined,
  fallback: PendantFields
): CircleAreaDraft => {
  if (!area) return circleAreaDraftFromPendantBack(fallback);
  return circleAreaDraftFromPendantBack({
    photoUploadAllowed: fallback.photoUploadAllowed,
    photoCustomization: fallback.photoCustomization,
    backPhotoCustomization: area,
  });
};

const applyCircleAreaToPendant = (
  source: PendantFields,
  draft: CircleAreaDraft,
  target: "front" | "back" = "front"
): PhotoCustomizationObj => {
  const normalized = normalizeCircleAreaDraft(draft);
  const base =
    target === "back" ? withBackPhotoCustomization(source) : withPhotoCustomization(source);
  return {
    ...base,
    enabled: source.photoUploadAllowed !== false,
    shape: normalized.shape,
    coordinateSpace: normalized.coordinateSpace,
    xPct: normalized.xPct,
    yPct: normalized.yPct,
    widthPct: normalized.widthPct,
    heightPct: normalized.heightPct,
    centerXPct:
      normalized.shape === "circle" && normalized.lockAspectRatio
        ? normalized.centerXPct
        : undefined,
    centerYPct:
      normalized.shape === "circle" && normalized.lockAspectRatio
        ? normalized.centerYPct
        : undefined,
    radiusPct:
      normalized.shape === "circle" && normalized.lockAspectRatio
        ? normalized.radiusPct
        : undefined,
    cornerRadiusPct: normalized.shape === "circle" ? 50 : 16,
    heartTopWidthPct: normalized.heartTopWidthPct,
    heartNotchDepthPct: normalized.heartNotchDepthPct,
    heartNotchWidthPct: normalized.heartNotchWidthPct,
  };
};

const sanitizePhotoCustomizationArea = (
  input: unknown
): PendantFields["photoCustomization"] | undefined => {
  if (!input || typeof input !== "object") return undefined;
  const source = input as Record<string, unknown>;
  const base = withPhotoCustomization({
    photoCustomization: source as PendantFields["photoCustomization"],
  });
  return {
    ...base,
    enabled:
      typeof source.enabled === "boolean"
        ? source.enabled
        : typeof source.enabled === "string"
        ? source.enabled.toLowerCase() === "true"
        : base.enabled,
    shape: toPendantShape(source.shape),
    coordinateSpace:
      String(source.coordinateSpace || "").toLowerCase() === "image"
        ? "image"
        : base.coordinateSpace,
    centerXPct:
      source.centerXPct == null || source.centerXPct === ""
        ? base.centerXPct
        : Number.isFinite(Number(source.centerXPct))
        ? Number(source.centerXPct)
        : base.centerXPct,
    centerYPct:
      source.centerYPct == null || source.centerYPct === ""
        ? base.centerYPct
        : Number.isFinite(Number(source.centerYPct))
        ? Number(source.centerYPct)
        : base.centerYPct,
    radiusPct:
      source.radiusPct == null || source.radiusPct === ""
        ? base.radiusPct
        : Number.isFinite(Number(source.radiusPct))
        ? Number(source.radiusPct)
        : base.radiusPct,
    radiusPx:
      source.radiusPx == null || source.radiusPx === ""
        ? undefined
        : Number.isFinite(Number(source.radiusPx))
        ? Number(source.radiusPx)
        : undefined,
  };
};

type CircleAreaEditorProps = {
  imageUrl: string;
  value: CircleAreaDraft;
  onChange: (next: CircleAreaDraft) => void;
};

function CircleAreaEditor({ imageUrl, value, onChange }: CircleAreaEditorProps) {
  const boardRef = useRef<HTMLDivElement | null>(null);
  const circleMaskId = `pendant-circle-area-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  const migratedLegacyAreaRef = useRef(false);
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const gestureRef = useRef<{
    mode: "none" | "move" | "resize";
    pointerId: number;
    startX: number;
    startY: number;
    startValue: CircleAreaDraft;
  }>({
    mode: "none",
    pointerId: -1,
    startX: 0,
    startY: 0,
    startValue: normalizeCircleAreaDraft({}),
  });
  const debugPendantArea =
    process.env.REACT_APP_DEBUG_PENDANT_AREA === "true" &&
    process.env.NODE_ENV !== "production";

  useEffect(() => {
    migratedLegacyAreaRef.current = false;
    setImageNaturalSize(null);
  }, [imageUrl]);

  const getImageBounds = () => {
    const board = boardRef.current;
    if (!board) return null;
    const rect = board.getBoundingClientRect();
    return getContainedImageBounds(
      rect.width,
      rect.height,
      imageNaturalSize?.width,
      imageNaturalSize?.height
    );
  };

  const toDisplayArea = (draft: CircleAreaDraft) =>
    resolvePendantDisplayArea(draft, getImageBounds());

  const applyDisplayArea = (
    draft: CircleAreaDraft,
    nextDisplay: {
      xPct: number;
      yPct: number;
      widthPct: number;
      heightPct: number;
    }
  ) => {
    const projected = projectDisplayAreaToImageSpace(
      {
        shape: draft.shape,
        xPct: nextDisplay.xPct,
        yPct: nextDisplay.yPct,
        widthPct: nextDisplay.widthPct,
        heightPct: nextDisplay.heightPct,
      },
      getImageBounds(),
      {
        preserveCircleMetrics: draft.shape !== "circle" || draft.lockAspectRatio,
      }
    );

    onChange(
      normalizeCircleAreaDraft({
        ...draft,
        ...projected,
        shape: draft.shape,
        coordinateSpace: projected.coordinateSpace ?? draft.coordinateSpace,
      })
    );
  };

  const updateFromPointer = (
    event: React.PointerEvent<HTMLDivElement>,
    mode: "move" | "resize"
  ) => {
    const board = boardRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dxPct = ((event.clientX - gestureRef.current.startX) / rect.width) * 100;
    const dyPct = ((event.clientY - gestureRef.current.startY) / rect.height) * 100;
    const startValue = gestureRef.current.startValue;
    const startDisplayValue = toDisplayArea(startValue);

    if (mode === "move") {
      applyDisplayArea(startValue, {
        xPct: startDisplayValue.xPct + dxPct,
        yPct: startDisplayValue.yPct + dyPct,
        widthPct: startDisplayValue.widthPct,
        heightPct: startDisplayValue.heightPct,
      });
      return;
    }

    const maxWidth = Math.max(5, 100 - startDisplayValue.xPct);
    const maxHeight = Math.max(5, 100 - startDisplayValue.yPct);

    const forcePerfectAspect = event.shiftKey && startValue.shape === "circle";
    const lockAspectRatio = startValue.lockAspectRatio || forcePerfectAspect;

    if (lockAspectRatio) {
      const scaleX =
        (startDisplayValue.widthPct + dxPct) /
        Math.max(0.0001, startDisplayValue.widthPct);
      const scaleY =
        (startDisplayValue.heightPct + dyPct) /
        Math.max(0.0001, startDisplayValue.heightPct);
      const scale = Math.max(0.1, Math.max(scaleX, scaleY));
      let nextWidth = clampPct(startDisplayValue.widthPct * scale, 5, maxWidth);
      let nextHeight = clampPct(startDisplayValue.heightPct * scale, 5, maxHeight);
      const ratio =
        forcePerfectAspect
          ? 1
          : startDisplayValue.heightPct / Math.max(0.0001, startDisplayValue.widthPct);

      nextHeight = nextWidth * ratio;
      if (nextHeight > maxHeight) {
        nextHeight = maxHeight;
        nextWidth = nextHeight / Math.max(0.0001, ratio);
      }
      if (nextHeight < 5) {
        nextHeight = 5;
        nextWidth = nextHeight / Math.max(0.0001, ratio);
      }
      nextWidth = clampPct(nextWidth, 5, maxWidth);
      nextHeight = clampPct(nextHeight, 5, maxHeight);

      applyDisplayArea(startValue, {
        xPct: startDisplayValue.xPct,
        yPct: startDisplayValue.yPct,
        widthPct: nextWidth,
        heightPct: nextHeight,
      });
      return;
    }

    applyDisplayArea(startValue, {
      xPct: startDisplayValue.xPct,
      yPct: startDisplayValue.yPct,
      widthPct: clampPct(startDisplayValue.widthPct + dxPct, 5, maxWidth),
      heightPct: clampPct(startDisplayValue.heightPct + dyPct, 5, maxHeight),
    });
  };

  const beginGesture =
    (mode: "move" | "resize") =>
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const board = boardRef.current;
      if (!board) return;
      board.setPointerCapture(event.pointerId);
      gestureRef.current = {
        mode,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startValue: value,
      };
    };

  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (gestureRef.current.mode === "none") return;
    if (gestureRef.current.pointerId !== event.pointerId) return;
    updateFromPointer(event, gestureRef.current.mode);
  };

  const onPointerEnd: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (gestureRef.current.mode === "none") return;
    if (gestureRef.current.pointerId !== event.pointerId) return;
    const board = boardRef.current;
    if (board?.hasPointerCapture(event.pointerId)) {
      board.releasePointerCapture(event.pointerId);
    }
    gestureRef.current.mode = "none";
    gestureRef.current.pointerId = -1;
  };

  const onDoubleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    const board = boardRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const displayValue = toDisplayArea(value);
    const centerXPct = ((event.clientX - rect.left) / rect.width) * 100;
    const centerYPct = ((event.clientY - rect.top) / rect.height) * 100;
    applyDisplayArea(value, {
      xPct: centerXPct - displayValue.widthPct / 2,
      yPct: centerYPct - displayValue.heightPct / 2,
      widthPct: displayValue.widthPct,
      heightPct: displayValue.heightPct,
    });
  };

  const displayValue = toDisplayArea(value);
  const imageBounds = getImageBounds();
  const isCircleArea = value.shape === "circle";
  const isHeartArea = value.shape === "heart";

  return (
    <div
      ref={boardRef}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onDoubleClick={onDoubleClick}
      style={{
        position: "relative",
        width: "min(78vw, 460px)",
        aspectRatio: "1 / 1",
        borderRadius: 12,
        border: "1px solid #d1d5db",
        background: "#f3f4f6",
        overflow: "hidden",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <img
        src={assetUrl(imageUrl) || imageUrl}
        alt="Pendant area editor"
        onLoad={(event) => {
          const target = event.currentTarget;
          if (!target.naturalWidth || !target.naturalHeight) return;
          const board = boardRef.current;
          if (
            board &&
            value.coordinateSpace !== "image" &&
            !migratedLegacyAreaRef.current
          ) {
            const rect = board.getBoundingClientRect();
            const bounds = getContainedImageBounds(
              rect.width,
              rect.height,
              target.naturalWidth,
              target.naturalHeight
            );
            const projected = projectDisplayAreaToImageSpace(
              {
                shape: value.shape,
                xPct: value.xPct,
                yPct: value.yPct,
                widthPct: value.widthPct,
                heightPct: value.heightPct,
              },
              bounds
            );
            migratedLegacyAreaRef.current = true;
            onChange(
              normalizeCircleAreaDraft({
                ...value,
                ...projected,
                shape: value.shape,
                coordinateSpace: "image",
              })
            );
          }
          setImageNaturalSize({
            width: target.naturalWidth,
            height: target.naturalHeight,
          });
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />
      <div
        onPointerDown={beginGesture("move")}
        style={{
          position: "absolute",
          left: `${displayValue.xPct}%`,
          top: `${displayValue.yPct}%`,
          width: `${displayValue.widthPct}%`,
          height: `${displayValue.heightPct}%`,
          border: isHeartArea ? "1px dashed rgba(15, 23, 42, 0.65)" : "none",
          cursor: "grab",
        }}
      >
        {isCircleArea ? (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              overflow: "visible",
              pointerEvents: "none",
            }}
          >
            <defs>
              <mask id={circleMaskId}>
                <rect x="-4000" y="-4000" width="8100" height="8100" fill="white" />
                <circle cx="50" cy="50" r="50" fill="black" />
              </mask>
            </defs>
            <rect
              x="-4000"
              y="-4000"
              width="8100"
              height="8100"
              fill="rgba(17, 24, 39, 0.15)"
              mask={`url(#${circleMaskId})`}
            />
            <circle
              cx="50"
              cy="50"
              r="49"
              fill="rgba(255,255,255,0.08)"
              stroke="#111827"
              strokeWidth="2"
              strokeDasharray="5 3"
              vectorEffect="non-scaling-stroke"
            />
          </svg>
        ) : null}
        {isHeartArea ? (
          <svg
            viewBox="0 0 100 100"
            preserveAspectRatio="none"
            style={{
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
              pointerEvents: "none",
            }}
          >
            <path
              d={buildHeartEditorPath(value)}
              fill="rgba(255,255,255,0.18)"
              stroke="#111827"
              strokeWidth="2.5"
              strokeDasharray="6 4"
            />
          </svg>
        ) : null}
        <div
          onPointerDown={beginGesture("resize")}
          style={{
            position: "absolute",
            right: -8,
            bottom: -8,
            width: 16,
            height: 16,
            borderRadius: "9999px",
            border: "2px solid #fff",
            background: "#111827",
            cursor: "nwse-resize",
          }}
        />
      </div>
      {debugPendantArea && imageBounds ? (
        <>
          <div
            style={{
              position: "absolute",
              left: `${imageBounds.xPct}%`,
              top: `${imageBounds.yPct}%`,
              width: `${imageBounds.widthPct}%`,
              height: `${imageBounds.heightPct}%`,
              border: "1px solid rgba(14, 165, 233, 0.95)",
              pointerEvents: "none",
            }}
          />
          <div
            style={{
              position: "absolute",
              left: `${displayValue.xPct + displayValue.widthPct / 2}%`,
              top: `${displayValue.yPct + displayValue.heightPct / 2}%`,
              width: 10,
              height: 10,
              borderRadius: "9999px",
              background: "#22c55e",
              transform: "translate(-50%, -50%)",
              pointerEvents: "none",
            }}
          />
        </>
      ) : null}
    </div>
  );
}

type RectangleAreaEditorProps = {
  imageUrl: string;
  value: EngravingAreaDraft;
  onChange: (next: EngravingAreaDraft) => void;
};

function RectangleAreaEditor({
  imageUrl,
  value,
  onChange,
}: RectangleAreaEditorProps) {
  const boardRef = useRef<HTMLDivElement | null>(null);
  const [imageNaturalSize, setImageNaturalSize] = useState<{
    width: number;
    height: number;
  } | null>(null);
  const gestureRef = useRef<{
    mode: "none" | "move" | "resize";
    pointerId: number;
    startX: number;
    startY: number;
    startValue: EngravingAreaDraft;
  }>({
    mode: "none",
    pointerId: -1,
    startX: 0,
    startY: 0,
    startValue: normalizeEngravingAreaDraft(undefined, value.side),
  });
  const debugArea =
    process.env.REACT_APP_DEBUG_PENDANT_AREA === "true" &&
    process.env.NODE_ENV !== "production";

  useEffect(() => {
    setImageNaturalSize(null);
  }, [imageUrl]);

  const getImageBounds = () => {
    const board = boardRef.current;
    if (!board) return null;
    const rect = board.getBoundingClientRect();
    return getContainedImageBounds(
      rect.width,
      rect.height,
      imageNaturalSize?.width,
      imageNaturalSize?.height
    );
  };

  const toDisplayArea = (draft: EngravingAreaDraft) =>
    resolveEngravingDisplayArea(draft, getImageBounds());

  const applyDisplayArea = (
    draft: EngravingAreaDraft,
    nextDisplay: {
      xPct: number;
      yPct: number;
      widthPct: number;
      heightPct: number;
    }
  ) => {
    const projected = projectDisplayRectToImageSpace(nextDisplay, getImageBounds());
    onChange(
      normalizeEngravingAreaDraft(
        {
          ...draft,
          ...projected,
        },
        draft.side
      )
    );
  };

  const updateFromPointer = (
    event: React.PointerEvent<HTMLDivElement>,
    mode: "move" | "resize"
  ) => {
    const board = boardRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const dxPct = ((event.clientX - gestureRef.current.startX) / rect.width) * 100;
    const dyPct = ((event.clientY - gestureRef.current.startY) / rect.height) * 100;
    const startValue = gestureRef.current.startValue;
    const startDisplayValue = toDisplayArea(startValue);

    if (mode === "move") {
      applyDisplayArea(startValue, {
        xPct: startDisplayValue.xPct + dxPct,
        yPct: startDisplayValue.yPct + dyPct,
        widthPct: startDisplayValue.widthPct,
        heightPct: startDisplayValue.heightPct,
      });
      return;
    }

    const maxWidth = Math.max(5, 100 - startDisplayValue.xPct);
    const maxHeight = Math.max(5, 100 - startDisplayValue.yPct);
    const lockSquare = startValue.lockSquare || event.shiftKey;

    if (lockSquare) {
      const nextSize = clampPct(
        Math.max(startDisplayValue.widthPct + dxPct, startDisplayValue.heightPct + dyPct),
        5,
        Math.min(maxWidth, maxHeight)
      );
      applyDisplayArea(startValue, {
        xPct: startDisplayValue.xPct,
        yPct: startDisplayValue.yPct,
        widthPct: nextSize,
        heightPct: nextSize,
      });
      return;
    }

    applyDisplayArea(startValue, {
      xPct: startDisplayValue.xPct,
      yPct: startDisplayValue.yPct,
      widthPct: clampPct(startDisplayValue.widthPct + dxPct, 5, maxWidth),
      heightPct: clampPct(startDisplayValue.heightPct + dyPct, 5, maxHeight),
    });
  };

  const beginGesture =
    (mode: "move" | "resize") =>
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      const board = boardRef.current;
      if (!board) return;
      board.setPointerCapture(event.pointerId);
      gestureRef.current = {
        mode,
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        startValue: value,
      };
    };

  const onPointerMove: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (gestureRef.current.mode === "none") return;
    if (gestureRef.current.pointerId !== event.pointerId) return;
    updateFromPointer(event, gestureRef.current.mode);
  };

  const onPointerEnd: React.PointerEventHandler<HTMLDivElement> = (event) => {
    if (gestureRef.current.mode === "none") return;
    if (gestureRef.current.pointerId !== event.pointerId) return;
    const board = boardRef.current;
    if (board?.hasPointerCapture(event.pointerId)) {
      board.releasePointerCapture(event.pointerId);
    }
    gestureRef.current.mode = "none";
    gestureRef.current.pointerId = -1;
  };

  const onDoubleClick: React.MouseEventHandler<HTMLDivElement> = (event) => {
    const board = boardRef.current;
    if (!board) return;
    const rect = board.getBoundingClientRect();
    if (!rect.width || !rect.height) return;
    const displayValue = toDisplayArea(value);
    const centerXPct = ((event.clientX - rect.left) / rect.width) * 100;
    const centerYPct = ((event.clientY - rect.top) / rect.height) * 100;
    applyDisplayArea(value, {
      xPct: centerXPct - displayValue.widthPct / 2,
      yPct: centerYPct - displayValue.heightPct / 2,
      widthPct: displayValue.widthPct,
      heightPct: displayValue.heightPct,
    });
  };

  const displayValue = toDisplayArea(value);
  const imageBounds = getImageBounds();

  return (
    <div
      ref={boardRef}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      onDoubleClick={onDoubleClick}
      style={{
        position: "relative",
        width: "min(78vw, 460px)",
        aspectRatio: "1 / 1",
        borderRadius: 12,
        border: "1px solid #d1d5db",
        background: "#f3f4f6",
        overflow: "hidden",
        touchAction: "none",
        userSelect: "none",
      }}
    >
      <img
        src={assetUrl(imageUrl) || imageUrl}
        alt="Engraving area editor"
        onLoad={(event) => {
          const target = event.currentTarget;
          if (!target.naturalWidth || !target.naturalHeight) return;
          setImageNaturalSize({
            width: target.naturalWidth,
            height: target.naturalHeight,
          });
        }}
        style={{
          position: "absolute",
          inset: 0,
          width: "100%",
          height: "100%",
          objectFit: "contain",
          pointerEvents: "none",
        }}
      />
      <div
        onPointerDown={beginGesture("move")}
        style={{
          position: "absolute",
          left: `${displayValue.xPct}%`,
          top: `${displayValue.yPct}%`,
          width: `${displayValue.widthPct}%`,
          height: `${displayValue.heightPct}%`,
          borderRadius: 12,
          border: "2px dashed #111827",
          background: "rgba(255,255,255,0.15)",
          boxShadow: "0 0 0 9999px rgba(17, 24, 39, 0.15)",
          cursor: "grab",
        }}
      >
        <div
          onPointerDown={beginGesture("resize")}
          style={{
            position: "absolute",
            right: -8,
            bottom: -8,
            width: 16,
            height: 16,
            borderRadius: "9999px",
            border: "2px solid #fff",
            background: "#111827",
            cursor: "nwse-resize",
          }}
        />
      </div>
      {debugArea && imageBounds ? (
        <div
          style={{
            position: "absolute",
            left: `${imageBounds.xPct}%`,
            top: `${imageBounds.yPct}%`,
            width: `${imageBounds.widthPct}%`,
            height: `${imageBounds.heightPct}%`,
            border: "1px solid rgba(14, 165, 233, 0.95)",
            pointerEvents: "none",
          }}
        />
      ) : null}
    </div>
  );
}

function EngravingAdminPreviewFace({
  imageUrl,
  area,
  text,
  fontKey,
  fontSize,
  label,
  onSideLayout,
}: {
  imageUrl?: string;
  area?: EngravingArea;
  text?: string;
  fontKey?: string;
  fontSize?: number;
  label?: string;
  onSideLayout?: (layout: EngravingPreviewLayout) => void;
}) {
  const normalizedArea = sanitizeEngravingArea(
    area || sanitizeEngravingArea(undefined, "front"),
    area?.side === "back" ? "back" : "front"
  );
  const previewSelection = {
    activeSide: normalizedArea.side === "back" ? "back" : "front",
    front: {
      enabled: normalizedArea.side !== "back",
      text: normalizedArea.side === "back" ? "" : String(text || "").trim(),
      sanitizedText: normalizedArea.side === "back" ? "" : String(text || "").trim(),
      fontKey: String(fontKey || "serif"),
      fontSize: Number(fontSize || 18),
      textLimit: normalizedArea.side === "back" ? undefined : normalizedArea.textLimit,
      maxLines: normalizedArea.side === "back" ? undefined : normalizedArea.maxLines,
      contentPaddingPct: 4,
      area:
        normalizedArea.side === "back"
          ? sanitizeEngravingArea(undefined, "front")
          : normalizedArea,
      contentOffsetXPct: 0,
      contentOffsetYPct: 0,
      contentScale: 1,
    },
    back:
      normalizedArea.side === "back"
        ? {
            enabled: true,
            text: String(text || "").trim(),
            sanitizedText: String(text || "").trim(),
            fontKey: String(fontKey || "serif"),
            fontSize: Number(fontSize || 18),
            textLimit: normalizedArea.textLimit,
            maxLines: normalizedArea.maxLines,
            contentPaddingPct: 4,
            area: normalizedArea,
            contentOffsetXPct: 0,
            contentOffsetYPct: 0,
            contentScale: 1,
          }
        : undefined,
    preview: {
      frontImage: normalizedArea.side === "back" ? undefined : imageUrl,
      backImage: normalizedArea.side === "back" ? imageUrl : undefined,
    },
  } as const;

  return (
    <div style={{ display: "grid", gap: 6 }}>
      {label ? (
        <div style={{ fontSize: 11, fontWeight: 700, color: "#64748b" }}>{label}</div>
      ) : null}
      <EngravingCustomizationPreview
        selection={previewSelection}
        mode="single"
        activeSide={previewSelection.activeSide}
        onSideLayout={onSideLayout}
      />
    </div>
  );
}

type PendantChainOptionDraft = PendantChainOption & {
  lengthsInchDraft?: string;
  colorsDraft?: string;
  imageFile?: File | null;
};

const toChainOptionDraft = (
  input: PendantChainOption
): PendantChainOptionDraft => ({
  ...input,
  lengthsInchDraft: (input.lengthsInch || []).join(", "),
  colorsDraft: (input.colors || []).join(", "),
  imageFile: null,
});

const sanitizeTshirtSizeStocks = (
  input: TshirtFields["sizeStocks"]
): NonNullable<TshirtFields["sizeStocks"]> => {
  const rows = Array.isArray(input) ? input : [];
  const bySize = new Map<string, { size: string; stockCount: number }>();
  rows.forEach((row) => {
    const size = String(row?.size || "").trim().toUpperCase();
    if (!size) return;
    const stockRaw = Number(row?.stockCount);
    bySize.set(size.toLowerCase(), {
      size,
      stockCount:
        Number.isFinite(stockRaw) && stockRaw >= 0 ? Math.round(stockRaw) : 0,
    });
  });
  return Array.from(bySize.values());
};

const sanitizeTshirtSizeStockByColor = (
  input: TshirtFields["sizeStockByColor"]
): NonNullable<TshirtFields["sizeStockByColor"]> => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<
    string,
    {
      color: string;
      sizes: NonNullable<TshirtFields["sizeStocks"]>;
    }
  >();
  rows.forEach((row) => {
    const color = String((row as any)?.color || "").trim();
    if (!color) return;
    byColor.set(color.toLowerCase(), {
      color,
      sizes: sanitizeTshirtSizeStocks((row as any)?.sizes),
    });
  });
  return Array.from(byColor.values());
};

const buildSizeStockByColorFromLegacy = (input: TshirtFields) => {
  const legacyByColor = Array.isArray(input.colorStocks) ? input.colorStocks : [];
  const legacyBySize = sanitizeTshirtSizeStocks(input.sizeStocks);
  const colors = Array.from(
    new Set(
      [
        ...(Array.isArray(input.availableColors) ? input.availableColors : []),
        ...legacyByColor.map((row) => String(row?.color || "").trim()),
      ]
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
  const sizes = Array.from(
    new Set(
      [
        ...(Array.isArray(input.availableSizes) ? input.availableSizes : []),
        ...legacyBySize.map((row) => String(row.size || "").trim().toUpperCase()),
      ]
        .map((value) => String(value || "").trim().toUpperCase())
        .filter(Boolean)
    )
  );

  if (!colors.length || !sizes.length) return [];

  const sizeMap = new Map<string, number>();
  legacyBySize.forEach((row) => {
    sizeMap.set(String(row.size || "").trim().toLowerCase(), row.stockCount);
  });
  const colorMap = new Map<string, number>();
  legacyByColor.forEach((row) => {
    const color = String(row?.color || "").trim().toLowerCase();
    if (!color) return;
    const stock = Number(row?.stockCount);
    colorMap.set(
      color,
      Number.isFinite(stock) && stock >= 0 ? Math.round(stock) : 0
    );
  });

  return colors.map((color) => ({
    color,
    sizes: sizes.map((size, idx) => ({
      size,
      stockCount:
        colors.length === 1
          ? sizeMap.get(size.toLowerCase()) ?? 0
          : idx === 0
          ? colorMap.get(color.toLowerCase()) ?? 0
          : sizeMap.get(size.toLowerCase()) ?? 0,
    })),
  }));
};

const resolveTshirtSizeStockByColor = (
  input: Partial<TshirtFields> | undefined | null
) => {
  const fromMatrix = sanitizeTshirtSizeStockByColor(input?.sizeStockByColor);
  if (fromMatrix.length) return fromMatrix;
  return buildSizeStockByColorFromLegacy((input || {}) as TshirtFields);
};

const syncSizeStockByColorWithColorAndSize = (
  matrix: TshirtFields["sizeStockByColor"],
  colors: string[],
  sizes: string[]
): NonNullable<TshirtFields["sizeStockByColor"]> => {
  const cleanedColors = Array.from(
    new Set(
      (colors || [])
        .map((color) => String(color || "").trim())
        .filter(Boolean)
    )
  );
  const cleanedSizes = Array.from(
    new Set(
      (sizes || [])
        .map((size) => String(size || "").trim().toUpperCase())
        .filter(Boolean)
    )
  );
  const existing = sanitizeTshirtSizeStockByColor(matrix);
  const existingByColor = new Map<string, Record<string, number>>();
  existing.forEach((row) => {
    const bySize: Record<string, number> = {};
    (row.sizes || []).forEach((sizeRow) => {
      bySize[String(sizeRow.size || "").trim().toLowerCase()] = Math.max(
        0,
        Math.round(Number(sizeRow.stockCount || 0))
      );
    });
    existingByColor.set(row.color.toLowerCase(), bySize);
  });

  return cleanedColors.map((color) => {
    const bySize = existingByColor.get(color.toLowerCase()) || {};
    return {
      color,
      sizes: cleanedSizes.map((size) => ({
        size,
        stockCount: bySize[size.toLowerCase()] ?? 0,
      })),
    };
  });
};

const deriveLegacyStocksFromSizeStockByColor = (
  matrix: TshirtFields["sizeStockByColor"]
) => {
  const rows = sanitizeTshirtSizeStockByColor(matrix);
  const colorStocks = rows.map((row) => ({
    color: row.color,
    stockCount: (row.sizes || []).reduce(
      (sum, sizeRow) => sum + Math.max(0, Math.round(Number(sizeRow.stockCount || 0))),
      0
    ),
  }));
  const bySize = new Map<string, number>();
  rows.forEach((row) => {
    (row.sizes || []).forEach((sizeRow) => {
      const size = String(sizeRow.size || "").trim().toUpperCase();
      if (!size) return;
      bySize.set(
        size.toLowerCase(),
        (bySize.get(size.toLowerCase()) || 0) +
          Math.max(0, Math.round(Number(sizeRow.stockCount || 0)))
      );
    });
  });
  const sizeStocks = Array.from(bySize.entries()).map(([size, stockCount]) => ({
    size: size.toUpperCase(),
    stockCount,
  }));
  return { colorStocks, sizeStocks };
};

const isHexColor = (value: string) =>
  /^#(?:[0-9a-fA-F]{3}|[0-9a-fA-F]{6})$/.test(String(value || "").trim());

type TshirtColorVariantDraft = TshirtColorVariant & {
  frontImageFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles?: File[];
};

const sanitizeTshirtColorVariants = (
  input: TshirtFields["colorVariants"]
): TshirtColorVariantDraft[] => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<string, TshirtColorVariantDraft>();
  rows.forEach((row) => {
    const color = String(row?.color || "").trim();
    if (!color) return;
    byColor.set(color.toLowerCase(), {
      color,
      frontImage: String(row?.frontImage || "").trim() || undefined,
      backImage: String(row?.backImage || "").trim() || undefined,
      moreImages: Array.isArray(row?.moreImages)
        ? row.moreImages
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        : [],
      frontImageFile:
        (row as TshirtColorVariantDraft)?.frontImageFile ?? null,
      backImageFile:
        (row as TshirtColorVariantDraft)?.backImageFile ?? null,
      moreImageFiles: Array.isArray(
        (row as TshirtColorVariantDraft)?.moreImageFiles
      )
        ? ((row as TshirtColorVariantDraft).moreImageFiles as File[])
        : [],
    });
  });
  return Array.from(byColor.values());
};

const syncColorVariantsWithColors = (
  variants: TshirtColorVariantDraft[],
  colors: string[]
): TshirtColorVariantDraft[] => {
  const cleanedColors = Array.from(
    new Set(
      (colors || [])
        .map((color) => String(color || "").trim())
        .filter(Boolean)
    )
  );
  const byColor = new Map<string, TshirtColorVariantDraft>();
  (variants || []).forEach((variant) => {
    byColor.set(String(variant.color || "").trim().toLowerCase(), {
      color: String(variant.color || "").trim(),
      frontImage: String(variant.frontImage || "").trim() || undefined,
      backImage: String(variant.backImage || "").trim() || undefined,
      moreImages: Array.isArray(variant.moreImages)
        ? variant.moreImages
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        : [],
      frontImageFile: variant.frontImageFile ?? null,
      backImageFile: variant.backImageFile ?? null,
      moreImageFiles: Array.isArray(variant.moreImageFiles)
        ? variant.moreImageFiles
        : [],
    });
  });
  return cleanedColors.map((color) => {
    const existing = byColor.get(color.toLowerCase());
    return (
      existing || {
        color,
        frontImage: undefined,
        backImage: undefined,
        moreImages: [],
        frontImageFile: null,
        backImageFile: null,
        moreImageFiles: [],
      }
    );
  });
};

const sanitizeChainOption = (
  input: Partial<PendantChainOptionDraft>
): PendantChainOption | null => {
  const chainType = String(input.chainType || "").trim();
  const lengthsInch =
    typeof input.lengthsInchDraft === "string"
      ? parseNumberCsv(input.lengthsInchDraft)
      : Array.isArray(input.lengthsInch)
      ? input.lengthsInch
          .map((v) => Number(v))
          .filter((n) => Number.isFinite(n) && n > 0)
      : [];
  if (!chainType || !lengthsInch.length) return null;
  const colors =
    typeof input.colorsDraft === "string"
      ? parseTextCsv(input.colorsDraft)
      : Array.isArray(input.colors)
      ? input.colors.map((c) => String(c || "").trim()).filter(Boolean)
      : [];
  const upgradePriceRaw = Number(input.upgradePrice);
  const standalonePriceRaw = Number(input.standalonePrice);
  return {
    chainType,
    lengthsInch,
    colors,
    image: String(input.image || "").trim() || undefined,
    defaultIncluded: Boolean(input.defaultIncluded),
    upgradePrice:
      Number.isFinite(upgradePriceRaw) && upgradePriceRaw >= 0
        ? upgradePriceRaw
        : 0,
    standalonePrice:
      Number.isFinite(standalonePriceRaw) && standalonePriceRaw >= 0
        ? standalonePriceRaw
        : 0,
  };
};

const withDefaultChain = (
  options: PendantChainOptionDraft[]
): PendantChainOptionDraft[] => {
  if (!options.length) return [];
  if (options.some((opt) => opt.defaultIncluded)) return options;
  return options.map((opt, idx) =>
    idx === 0 ? { ...opt, defaultIncluded: true } : { ...opt, defaultIncluded: false }
  );
};

const sanitizePendantColorStocks = (
  input: PendantFields["colorStocks"]
): NonNullable<PendantFields["colorStocks"]> => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<string, { color: string; stockCount: number }>();
  rows.forEach((row) => {
    const color = String(row?.color || "").trim();
    if (!color) return;
    const stockRaw = Number(row?.stockCount);
    byColor.set(color.toLowerCase(), {
      color,
      stockCount:
        Number.isFinite(stockRaw) && stockRaw >= 0 ? Math.round(stockRaw) : 0,
    });
  });
  return Array.from(byColor.values());
};

const derivePendantTotalStock = (
  input: PendantFields["colorStocks"]
): number =>
  sanitizePendantColorStocks(input).reduce(
    (sum, row) => sum + Math.max(0, Math.round(Number(row.stockCount || 0))),
    0
  );

const sanitizePendantColorPrices = (
  input: PendantFields["colorPrices"]
): NonNullable<PendantFields["colorPrices"]> => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<string, { color: string; price: number }>();
  rows.forEach((row) => {
    const color = String(row?.color || "").trim();
    if (!color) return;
    const priceRaw = Number(row?.price);
    if (!Number.isFinite(priceRaw) || priceRaw < 0) return;
    byColor.set(color.toLowerCase(), {
      color,
      price: Number(priceRaw.toFixed(2)),
    });
  });
  return Array.from(byColor.values());
};

const syncPendantColorStocksWithColors = (
  stocks: PendantFields["colorStocks"],
  colors: string[]
): NonNullable<PendantFields["colorStocks"]> => {
  const cleanedColors = Array.from(
    new Set(
      (colors || [])
        .map((value) => String(value || "").trim())
        .filter(Boolean)
    )
  );
  const existing = sanitizePendantColorStocks(stocks);
  const byColor = new Map(
    existing.map((row) => [row.color.toLowerCase(), row.stockCount])
  );
  return cleanedColors.map((color) => ({
    color,
    stockCount: byColor.get(color.toLowerCase()) ?? 0,
  }));
};

const prunePendantColorPricesByColors = (
  prices: PendantFields["colorPrices"],
  colors: string[]
): NonNullable<PendantFields["colorPrices"]> => {
  const cleanedColors = new Set(
    (colors || []).map((value) => String(value || "").trim().toLowerCase())
  );
  return sanitizePendantColorPrices(prices).filter((row) =>
    cleanedColors.has(row.color.toLowerCase())
  );
};

type PendantColorVariantDraft = PendantColorVariant & {
  frontImageFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles?: File[];
};

const sanitizePendantColorVariants = (
  input: PendantFields["pendantColorVariants"]
): PendantColorVariantDraft[] => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<string, PendantColorVariantDraft>();
  rows.forEach((row) => {
    const color = String((row as any)?.color || "").trim();
    if (!color) return;
    byColor.set(color.toLowerCase(), {
      color,
      frontImage: String((row as any)?.frontImage || "").trim() || undefined,
      backImage: String((row as any)?.backImage || "").trim() || undefined,
      moreImages: Array.isArray((row as any)?.moreImages)
        ? ((row as any).moreImages as string[])
            .map((value) => String(value || "").trim())
            .filter(Boolean)
        : [],
      photoCustomization: sanitizePhotoCustomizationArea(
        (row as any)?.photoCustomization
      ),
      backPhotoCustomization: sanitizePhotoCustomizationArea(
        (row as any)?.backPhotoCustomization
      ),
      frontImageFile: (row as any)?.frontImageFile ?? null,
      backImageFile: (row as any)?.backImageFile ?? null,
      moreImageFiles: Array.isArray((row as any)?.moreImageFiles)
        ? ((row as any).moreImageFiles as File[])
        : [],
    });
  });
  return Array.from(byColor.values());
};

const syncPendantColorVariantsWithColors = (
  variants: PendantFields["pendantColorVariants"],
  colors: string[]
): PendantColorVariantDraft[] => {
  const cleanedColors = Array.from(
    new Set(
      (colors || [])
        .map((color) => String(color || "").trim())
        .filter(Boolean)
    )
  );
  const byColor = new Map<string, PendantColorVariantDraft>();
  sanitizePendantColorVariants(variants).forEach((variant) => {
    byColor.set(String(variant.color || "").trim().toLowerCase(), variant);
  });
  return cleanedColors.map((color) => {
    const existing = byColor.get(color.toLowerCase());
    return (
      existing || {
        color,
        frontImage: undefined,
        backImage: undefined,
        moreImages: [],
        photoCustomization: undefined,
        backPhotoCustomization: undefined,
        frontImageFile: null,
        backImageFile: null,
        moreImageFiles: [],
      }
    );
  });
};

const DEFAULT_ENGRAVING_FONTS = [
  "serif",
  "sans",
  "script",
  "handwritten",
  "bold-classic",
];
const DEFAULT_ENGRAVING_SYMBOLS = ["❤️", "♥", "★", "∞"];

type EngravingColorVariantDraft = EngravingColorVariant & {
  frontImageFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles?: File[];
};

type EngravingAreaDraft = EngravingArea & {
  lockSquare: boolean;
};

const sanitizeEngravingArea = (
  input: Partial<EngravingArea> | undefined,
  side: "front" | "back"
): EngravingArea => {
  const widthPct = clampPct(Number(input?.widthPct ?? 70), 1, 100);
  const heightPct = clampPct(Number(input?.heightPct ?? 22), 1, 100);
  return {
    side,
    shape: "rectangle",
    enabled: input?.enabled ?? true,
    xPct: clampPct(Number(input?.xPct ?? 15), 0, 100 - widthPct),
    yPct: clampPct(Number(input?.yPct ?? 30), 0, 100 - heightPct),
    widthPct,
    heightPct,
    rotation: Number.isFinite(Number(input?.rotation))
      ? Number(input?.rotation)
      : 0,
    maxLines:
      Number.isFinite(Number(input?.maxLines)) && Number(input?.maxLines) > 0
        ? Math.round(Number(input?.maxLines))
        : undefined,
    textLimit:
      Number.isFinite(Number(input?.textLimit)) && Number(input?.textLimit) >= 0
        ? Math.round(Number(input?.textLimit))
        : undefined,
  };
};

const normalizeEngravingAreaDraft = (
  input: Partial<EngravingAreaDraft> | undefined,
  side: "front" | "back"
): EngravingAreaDraft => ({
  ...sanitizeEngravingArea(input, side),
  lockSquare: typeof input?.lockSquare === "boolean" ? input.lockSquare : false,
});

const engravingAreaDraftFromArea = (
  area: Partial<EngravingArea> | undefined,
  side: "front" | "back"
): EngravingAreaDraft => normalizeEngravingAreaDraft(area, side);

const applyEngravingAreaWidthValue = (
  draft: EngravingAreaDraft,
  widthPct: number
) => {
  const nextWidth = Number(widthPct);
  if (draft.lockSquare) {
    return normalizeEngravingAreaDraft(
      {
        ...draft,
        widthPct: nextWidth,
        heightPct: nextWidth,
      },
      draft.side
    );
  }
  return normalizeEngravingAreaDraft(
    {
      ...draft,
      widthPct: nextWidth,
    },
    draft.side
  );
};

const applyEngravingAreaHeightValue = (
  draft: EngravingAreaDraft,
  heightPct: number
) => {
  const nextHeight = Number(heightPct);
  if (draft.lockSquare) {
    return normalizeEngravingAreaDraft(
      {
        ...draft,
        widthPct: nextHeight,
        heightPct: nextHeight,
      },
      draft.side
    );
  }
  return normalizeEngravingAreaDraft(
    {
      ...draft,
      heightPct: nextHeight,
    },
    draft.side
  );
};

const resolveEngravingDisplayArea = (
  area: Partial<EngravingArea>,
  imageBounds?: {
    xPct: number;
    yPct: number;
    widthPct: number;
    heightPct: number;
  } | null
) => {
  const normalized = sanitizeEngravingArea(area, area.side === "back" ? "back" : "front");
  if (!imageBounds) {
    return {
      xPct: normalized.xPct ?? 0,
      yPct: normalized.yPct ?? 0,
      widthPct: normalized.widthPct ?? 0,
      heightPct: normalized.heightPct ?? 0,
    };
  }
  return {
    xPct: imageBounds.xPct + ((normalized.xPct ?? 0) / 100) * imageBounds.widthPct,
    yPct: imageBounds.yPct + ((normalized.yPct ?? 0) / 100) * imageBounds.heightPct,
    widthPct: ((normalized.widthPct ?? 0) / 100) * imageBounds.widthPct,
    heightPct: ((normalized.heightPct ?? 0) / 100) * imageBounds.heightPct,
  };
};

const projectDisplayRectToImageSpace = (
  area: {
    xPct: number;
    yPct: number;
    widthPct: number;
    heightPct: number;
  },
  imageBounds?: {
    xPct: number;
    yPct: number;
    widthPct: number;
    heightPct: number;
  } | null
) => {
  if (!imageBounds) {
    return {
      xPct: area.xPct,
      yPct: area.yPct,
      widthPct: area.widthPct,
      heightPct: area.heightPct,
    };
  }
  const rawWidthPct = (area.widthPct / Math.max(0.0001, imageBounds.widthPct)) * 100;
  const rawHeightPct =
    (area.heightPct / Math.max(0.0001, imageBounds.heightPct)) * 100;
  const widthPct = clampPct(rawWidthPct, 1, 100);
  const heightPct = clampPct(rawHeightPct, 1, 100);
  return {
    xPct: clampPct(
      ((area.xPct - imageBounds.xPct) / Math.max(0.0001, imageBounds.widthPct)) *
        100,
      0,
      100 - widthPct
    ),
    yPct: clampPct(
      ((area.yPct - imageBounds.yPct) / Math.max(0.0001, imageBounds.heightPct)) *
        100,
      0,
      100 - heightPct
    ),
    widthPct,
    heightPct,
  };
};

const sanitizeEngravingChainByColor = (
  input: EngravingFields["defaultChainByColor"]
): NonNullable<EngravingFields["defaultChainByColor"]> => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<string, EngravingChainByColor>();
  rows.forEach((row) => {
    const color = String(row?.color || "").trim();
    if (!color) return;
    byColor.set(color.toLowerCase(), {
      color,
      chainName: String(row?.chainName || "").trim() || `${color} chain`,
      chainColor: String(row?.chainColor || "").trim() || color,
      chainSku: String(row?.chainSku || "").trim() || undefined,
      chainIncluded:
        typeof row?.chainIncluded === "boolean" ? row.chainIncluded : true,
      image: String(row?.image || "").trim() || undefined,
    });
  });
  return Array.from(byColor.values());
};

const syncEngravingChainByColorWithColors = (
  rows: EngravingFields["defaultChainByColor"],
  colors: string[]
): NonNullable<EngravingFields["defaultChainByColor"]> => {
  const cleanedColors = Array.from(
    new Set(
      (colors || [])
        .map((color) => String(color || "").trim())
        .filter(Boolean)
    )
  );
  const byColor = new Map(
    sanitizeEngravingChainByColor(rows).map((row) => [row.color.toLowerCase(), row])
  );
  return cleanedColors.map((color) => {
    const existing = byColor.get(color.toLowerCase());
    return (
      existing || {
        color,
        chainName: `${color} chain`,
        chainColor: color,
        chainIncluded: true,
      }
    );
  });
};

const sanitizeEngravingColorVariants = (
  input: EngravingFields["engravingColorVariants"]
): EngravingColorVariantDraft[] => {
  const rows = Array.isArray(input) ? input : [];
  const byColor = new Map<string, EngravingColorVariantDraft>();
  rows.forEach((row) => {
    const color = String(row?.color || "").trim();
    if (!color) return;
    byColor.set(color.toLowerCase(), {
      color,
      sku: String(row?.sku || "").trim() || undefined,
      stockCount:
        Number.isFinite(Number(row?.stockCount)) && Number(row?.stockCount) >= 0
          ? Math.round(Number(row?.stockCount))
          : undefined,
      price:
        Number.isFinite(Number(row?.price)) && Number(row?.price) >= 0
          ? Number(Number(row?.price).toFixed(2))
          : undefined,
      swatchHex: String(row?.swatchHex || "").trim() || undefined,
      frontImage: String(row?.frontImage || "").trim() || undefined,
      backImage: String(row?.backImage || "").trim() || undefined,
      moreImages: Array.isArray(row?.moreImages)
        ? row.moreImages.map((value) => String(value || "").trim()).filter(Boolean)
        : [],
      matchingChain: row?.matchingChain
        ? sanitizeEngravingChainByColor([row.matchingChain])[0]
        : undefined,
      frontImageFile: (row as any)?.frontImageFile ?? null,
      backImageFile: (row as any)?.backImageFile ?? null,
      moreImageFiles: Array.isArray((row as any)?.moreImageFiles)
        ? ((row as any).moreImageFiles as File[])
        : [],
    });
  });
  return Array.from(byColor.values());
};

const syncEngravingColorVariantsWithColors = (
  variants: EngravingFields["engravingColorVariants"],
  colors: string[],
  chainRows?: EngravingFields["defaultChainByColor"]
): EngravingColorVariantDraft[] => {
  const cleanedColors = Array.from(
    new Set(
      (colors || [])
        .map((color) => String(color || "").trim())
        .filter(Boolean)
    )
  );
  const byColor = new Map<string, EngravingColorVariantDraft>();
  sanitizeEngravingColorVariants(variants).forEach((variant) => {
    byColor.set(String(variant.color || "").trim().toLowerCase(), variant);
  });
  const chainByColor = new Map(
    syncEngravingChainByColorWithColors(chainRows, cleanedColors).map((row) => [
      row.color.toLowerCase(),
      row,
    ])
  );
  return cleanedColors.map((color) => {
    const existing = byColor.get(color.toLowerCase());
    return (
      existing || {
        color,
        frontImage: undefined,
        backImage: undefined,
        moreImages: [],
        matchingChain: chainByColor.get(color.toLowerCase()),
        frontImageFile: null,
        backImageFile: null,
        moreImageFiles: [],
      }
    );
  });
};

const withEngravingProductDefaults = (
  input?: Partial<EngravingFields> | null
): EngravingFields => {
  const normalizedColors = Array.isArray(input?.availableColors)
    ? [...(input?.availableColors || [])]
    : Array.isArray(input?.engravingColorVariants)
    ? (input?.engravingColorVariants || [])
        .map((row) => String(row?.color || "").trim())
        .filter(Boolean)
    : [];
  const defaultChainByColor = syncEngravingChainByColorWithColors(
    input?.defaultChainByColor,
    normalizedColors
  );
  const engravingColorVariants = syncEngravingColorVariantsWithColors(
    input?.engravingColorVariants,
    normalizedColors,
    defaultChainByColor
  );
  const frontTextLimit =
    Number.isFinite(Number(input?.frontTextLimit)) && Number(input?.frontTextLimit) >= 0
      ? Math.round(Number(input?.frontTextLimit))
      : 20;
  const backTextLimit =
    Number.isFinite(Number(input?.backTextLimit)) && Number(input?.backTextLimit) >= 0
      ? Math.round(Number(input?.backTextLimit))
      : 20;
  const frontMaxLines =
    Number.isFinite(Number(input?.frontMaxLines)) && Number(input?.frontMaxLines) > 0
      ? Math.round(Number(input?.frontMaxLines))
      : 2;
  const backMaxLines =
    Number.isFinite(Number(input?.backMaxLines)) && Number(input?.backMaxLines) > 0
      ? Math.round(Number(input?.backMaxLines))
      : 2;
  const supportsBackEngraving = Boolean(input?.supportsBackEngraving);
  const frontEngravingArea = sanitizeEngravingArea(
    input?.frontEngravingArea || input?.engravingAreas?.find((area) => area.side === "front"),
    "front"
  );
  frontEngravingArea.textLimit = frontTextLimit;
  frontEngravingArea.maxLines = frontMaxLines;
  const backAreaSource =
    input?.backEngravingArea ||
    input?.engravingAreas?.find((area) => area.side === "back");
  const backEngravingArea = supportsBackEngraving
    ? sanitizeEngravingArea(backAreaSource, "back")
    : undefined;
  if (backEngravingArea) {
    backEngravingArea.textLimit = backTextLimit;
    backEngravingArea.maxLines = backMaxLines;
  }
  return {
    engravingSubtype: String(input?.engravingSubtype || "").trim() || "necklace",
    baseItem: input?.baseItem,
    availableColors: normalizedColors,
    colorStocks: syncPendantColorStocksWithColors(input?.colorStocks, normalizedColors),
    colorPrices: prunePendantColorPricesByColors(input?.colorPrices, normalizedColors),
    engravingColorVariants,
    defaultChainByColor,
    engravingAreas: [
      frontEngravingArea,
      ...(backEngravingArea ? [backEngravingArea] : []),
    ],
    frontEngravingArea,
    backEngravingArea,
    supportsBackEngraving,
    backEngravingExtraCost:
      Number.isFinite(Number(input?.backEngravingExtraCost)) &&
      Number(input?.backEngravingExtraCost) >= 0
        ? Number(Number(input?.backEngravingExtraCost).toFixed(2))
        : 0,
    frontTextLimit,
    backTextLimit: supportsBackEngraving ? backTextLimit : undefined,
    frontMaxLines,
    backMaxLines: supportsBackEngraving ? backMaxLines : undefined,
    allowedFonts:
      Array.isArray(input?.allowedFonts) && input?.allowedFonts.length
        ? input.allowedFonts.map((font) => String(font || "").trim()).filter(Boolean)
        : DEFAULT_ENGRAVING_FONTS,
    defaultFont: String(input?.defaultFont || "").trim() || "serif",
    defaultFontSize:
      Number.isFinite(Number(input?.defaultFontSize)) &&
      Number(input?.defaultFontSize) > 0
        ? Number(input?.defaultFontSize)
        : 18,
    allowedSymbols:
      Array.isArray(input?.allowedSymbols) && input?.allowedSymbols.length
        ? input.allowedSymbols.map((symbol) => String(symbol || "").trim()).filter(Boolean)
        : DEFAULT_ENGRAVING_SYMBOLS,
    emojiSupportMode:
      String(input?.emojiSupportMode || "").trim().toLowerCase() === "unicode"
        ? "unicode"
        : "controlled",
  };
};

const syncEngravingFieldsWithColors = (
  input: Partial<EngravingFields>,
  colors: string[]
): EngravingFields => {
  const base = withEngravingProductDefaults(input);
  const nextColors = Array.from(
    new Set(
      (colors || [])
        .map((color) => String(color || "").trim())
        .filter(Boolean)
    )
  );
  const defaultChainByColor = syncEngravingChainByColorWithColors(
    base.defaultChainByColor,
    nextColors
  );
  const engravingColorVariants = syncEngravingColorVariantsWithColors(
    base.engravingColorVariants,
    nextColors,
    defaultChainByColor
  ).map((variant) => ({
    ...variant,
    matchingChain:
      variant.matchingChain ||
      defaultChainByColor.find(
        (row) => row.color.toLowerCase() === variant.color.toLowerCase()
      ),
  }));
  return {
    ...base,
    availableColors: nextColors,
    colorStocks: syncPendantColorStocksWithColors(base.colorStocks, nextColors),
    colorPrices: prunePendantColorPricesByColors(base.colorPrices, nextColors),
    defaultChainByColor,
    engravingColorVariants,
  };
};

const buildInitialChainOptions = (
  initial?: Product | null
): PendantChainOptionDraft[] => {
  const raw = Array.isArray((initial as any)?.chainOptions)
    ? ((initial as any).chainOptions as PendantChainOptionDraft[])
    : [];
  const normalized = raw
    .map((item) => sanitizeChainOption(item))
    .filter((item): item is PendantChainOption => item !== null);
  if (normalized.length) {
    return withDefaultChain(normalized.map((item) => toChainOptionDraft(item)));
  }

  const legacyType = String((initial as any)?.chainType || "").trim();
  const legacyLengths = Array.isArray((initial as any)?.chainLengthInch)
    ? ((initial as any).chainLengthInch as number[])
        .map((v) => Number(v))
        .filter((n) => Number.isFinite(n) && n > 0)
    : [];
  if (!legacyType || !legacyLengths.length) return [];

  return [
    {
      chainType: legacyType,
      lengthsInch: legacyLengths,
      colors: [],
      defaultIncluded: true,
      upgradePrice: 0,
      standalonePrice: 0,
      lengthsInchDraft: legacyLengths.join(", "),
      colorsDraft: "",
    },
  ];
};

// function _pct(v: any): number | undefined {
//   const n = num(v);
//   if (n === undefined) return undefined;
//   if (n < 0) return 0;
//   if (n > 100) return 100;
//   return n;
// }

// /** derive the third value when any two of (price, compareAtPrice, offerPercent) change */
// function _deriveTriple(opts: {
//   price?: number;
//   compareAtPrice?: number;
//   offerPercent?: number; // 0..100
//   changedA: "price" | "compareAtPrice" | "offerPercent";
//   changedB: "price" | "compareAtPrice" | "offerPercent";
// }) {
//   const { price, compareAtPrice, offerPercent, changedA, changedB } = opts;
//   const changed = new Set([changedA, changedB]);

//   let P = price;
//   let C = compareAtPrice;
//   let D = offerPercent;

//   // price + discount => compareAtPrice
//   if (changed.has("price") && changed.has("offerPercent")) {
//     if (P !== undefined && D !== undefined && D < 100) {
//       C = +(P / (1 - D / 100)).toFixed(2);
//     }
//   }
//   // compareAtPrice + discount => price
//   else if (changed.has("compareAtPrice") && changed.has("offerPercent")) {
//     if (C !== undefined && D !== undefined) {
//       P = +(C * (1 - D / 100)).toFixed(2);
//     }
//   }
//   // price + compareAtPrice => discount
//   else if (changed.has("price") && changed.has("compareAtPrice")) {
//     if (P !== undefined && C !== undefined && C > 0) {
//       const d = (1 - P / C) * 100;
//       D = +Math.max(0, Math.min(100, d)).toFixed(2);
//     }
//   }
//   return { price: P, compareAtPrice: C, offerPercent: D };
// }

type ImagesState = {
  // existing URLs from DB
  heroImage?: string;
  previewImage?: string;
  arImage?: string;
  backImage?: string;
  moreImages: string[];
  youtubeVideoUrl?: string;
  useYoutubeAsThumbnail: boolean;
  // files chosen now
  heroImageFile?: File | null;
  previewImageFile?: File | null;
  arImageFile?: File | null;
  virtualTryOnAssetFile?: File | null;
  backImageFile?: File | null;
  moreImageFiles: File[];
  // remove flags
  clearHeroImage: boolean;
  clearPreviewImage: boolean;
  clearArImage: boolean;
  clearBackImage: boolean;
  removeMoreImages: string[]; // send raw stored URLs
};

// ---------- component ----------
type Props = {
  mode: Mode;
  initial?: Product | null;
  onSaved?: (p: Product) => void;
};

export default function ProductForm({ mode, initial, onSaved }: Props) {
  // base fields
  const [category, setCategory] = useState<ProductCategory | "">(
    initial?.category ?? ""
  );
  const [name, setName] = useState<string>(initial?.name ?? "");
  const [slug, setSlug] = useState<string>(initial?.slug ?? "");
  const [description, setDescription] = useState<string>(
    initial?.description ?? ""
  );
  const [jewelryCareHtml, setJewelryCareHtml] = useState<string>(
    (initial as any)?.jewelryCareHtml ?? ""
  );
  const [color, setColor] = useState<string>(
    initial?.category === ProductCategory.Pendant &&
      Array.isArray((initial as any)?.availableColors) &&
      (initial as any).availableColors.length
      ? String((initial as any).availableColors[0] || "")
      : initial?.color ?? ""
  );
  const [material, setMaterial] = useState<string>(initial?.material ?? "");
  const [isActive, setIsActive] = useState<boolean>(initial?.isActive ?? true);
  const [featured, setFeatured] = useState<boolean>(initial?.featured ?? false);
  const [topSeller, setTopSeller] = useState<boolean>(
    Boolean((initial as any)?.topSeller)
  );
  const [isCustomizable, setIsCustomizable] = useState<boolean>(
    initial?.isCustomizable ??
      (initial?.category === ProductCategory.Gift ? false : true)
  );
  const [giftWrapEnabled, setGiftWrapEnabled] = useState<boolean>(
    Boolean((initial as any)?.giftWrapEnabled ?? true)
  );
  const [stockCount, setStockCount] = useState<number>(
    initial?.category === ProductCategory.Pendant
      ? derivePendantTotalStock((initial as any)?.colorStocks)
      : initial?.stockCount ?? 0
  );
  const [tags] = useState<string[]>((initial?.tags as any) ?? []);
  const [seoTitle, setSeoTitle] = useState<string>(initial?.seoTitle ?? "");
  const [seoDescription, setSeoDescription] = useState<string>(
    initial?.seoDescription ?? ""
  );
  const [seoKeywordsStr, setSeoKeywordsStr] = useState(
    (initial?.seoKeywords ?? []).join(", ")
  );
  const [seoSlug, setSeoSlug] = useState<string>(initial?.seoSlug ?? "");
  const [sku, setSku] = useState<string>(initial?.sku ?? "");
  const jewelryCareHtmlInputRef = useRef<HTMLTextAreaElement | null>(null);
  const [jewelryCareTextColor, setJewelryCareTextColor] = useState("#475569");
  const setDim = (k: keyof Dimensions, v: string) =>
    setDimensions((d) => ({
      ...d,
      [k]: v === "" ? undefined : Number(v),
    }));

  const applyJewelryCareHtmlEdit = (
    buildNext: (context: {
      value: string;
      selectionStart: number;
      selectionEnd: number;
      selectedText: string;
    }) => {
      value: string;
      selectionStart: number;
      selectionEnd: number;
    }
  ) => {
    const textarea = jewelryCareHtmlInputRef.current;
    const currentValue = jewelryCareHtml;
    const selectionStart = textarea?.selectionStart ?? currentValue.length;
    const selectionEnd = textarea?.selectionEnd ?? currentValue.length;
    const next = buildNext({
      value: currentValue,
      selectionStart,
      selectionEnd,
      selectedText: currentValue.slice(selectionStart, selectionEnd),
    });
    setJewelryCareHtml(next.value);
    const restoreSelection = () => {
      const node = jewelryCareHtmlInputRef.current;
      if (!node) return;
      node.focus();
      node.setSelectionRange(next.selectionStart, next.selectionEnd);
    };
    if (typeof window !== "undefined" && typeof window.requestAnimationFrame === "function") {
      window.requestAnimationFrame(restoreSelection);
    } else {
      setTimeout(restoreSelection, 0);
    }
  };

  const wrapJewelryCareSelection = (
    before: string,
    after: string,
    placeholder: string
  ) => {
    applyJewelryCareHtmlEdit(
      ({ value, selectionStart, selectionEnd, selectedText }) => {
        const content = selectedText || placeholder;
        const nextValue =
          value.slice(0, selectionStart) +
          before +
          content +
          after +
          value.slice(selectionEnd);
        const nextSelectionStart = selectionStart + before.length;
        const nextSelectionEnd = nextSelectionStart + content.length;
        return {
          value: nextValue,
          selectionStart: nextSelectionStart,
          selectionEnd: nextSelectionEnd,
        };
      }
    );
  };

  const insertJewelryCareBulletList = () => {
    applyJewelryCareHtmlEdit(
      ({ value, selectionStart, selectionEnd, selectedText }) => {
        const source =
          selectedText ||
          "Store in a dry place\nClean with a soft cloth\nAvoid harsh chemicals";
        const items = source
          .split(/\r?\n/)
          .map((line) => line.trim())
          .filter(Boolean);
        const listHtml = items.length
          ? `<ul>\n${items.map((item) => `  <li>${item}</li>`).join("\n")}\n</ul>`
          : "<ul>\n  <li>Care item</li>\n</ul>";
        const nextValue =
          value.slice(0, selectionStart) +
          listHtml +
          value.slice(selectionEnd);
        return {
          value: nextValue,
          selectionStart,
          selectionEnd: selectionStart + listHtml.length,
        };
      }
    );
  };
  // price/discount interplay
  const [price, setPrice] = useState<number | undefined>(initial?.price ?? 0);
  const [compareAtPrice, setCompareAtPrice] = useState<number | undefined>(
    initial?.compareAtPrice
  );
  const [offerPercent, setOfferPercent] = useState<number | undefined>(
    initial?.offerPercent
  );

  const [dimensions, setDimensions] = useState<Dimensions>({
    width: initial?.dimensions?.width,
    height: initial?.dimensions?.height,
    depth: initial?.dimensions?.depth,
    diameter: initial?.dimensions?.diameter,
  });
  // category-specific
  const [pendant, setPendant] = useState<PendantFields>({
    chainType: (initial as any)?.chainType,
    chainLengthInch: (initial as any)?.chainLengthInch,
    chainOptions: buildInitialChainOptions(initial),
    availableColors: Array.isArray((initial as any)?.availableColors)
      ? (initial as any).availableColors
      : [],
    colorStocks: sanitizePendantColorStocks((initial as any)?.colorStocks),
    colorPrices: sanitizePendantColorPrices((initial as any)?.colorPrices),
    pendantColorVariants: sanitizePendantColorVariants(
      (initial as any)?.pendantColorVariants
    ),
    isDoubleSidedPendant: Boolean((initial as any)?.isDoubleSidedPendant),
    photoUploadAllowed: (initial as any)?.photoUploadAllowed ?? true,
    backImage: (initial as any)?.backImage,
    photoCustomization: withPhotoCustomization((initial as any) || {}),
    backPhotoCustomization: withBackPhotoCustomization((initial as any) || {}),
    engraving: (initial as any)?.engraving ?? { enabled: false, maxLength: 20 },
  });
  const [model3dFile, setModel3dFile] = useState<File | undefined>();
  const [model3dUrl, setModel3dUrl] = useState(initial?.model3dUrl);
  const [clearModel3d, setClearModel3d] = useState(false);
  const [model3dPhotoMaterial, setModel3dPhotoMaterial] = useState(initial?.model3dPhotoMaterial || "Photo");
  const [model3dEngravingMaterial, setModel3dEngravingMaterial] = useState(initial?.model3dEngravingMaterial || "Engraving");
  const [model3dWidthMm, setModel3dWidthMm] = useState<number | undefined>(initial?.model3dWidthMm ?? undefined);
  const [model3dError, setModel3dError] = useState("");
  const [virtualTryOn, setVirtualTryOn] = useState<VirtualTryOnConfig>(() =>
    defaultVirtualTryOnConfig((initial as any)?.virtualTryOn)
  );
  const [activeVirtualTryOnIndex, setActiveVirtualTryOnIndex] = useState(-1);
  const [engraving, setEngraving] = useState<EngravingFields>(() =>
    withEngravingProductDefaults((initial as any) || {})
  );
  const [cup, setCup] = useState<CupFields>({
    size: (initial as any)?.size,
    photoUploadAllowed: (initial as any)?.photoUploadAllowed,
  });
  const [plate, setPlate] = useState<LicensePlateFields>({
    plateType: (initial as any)?.plateType ?? "standard_car_12x6",
  });
  const [wallet, setWallet] = useState<WalletFields>({
    slots: (initial as any)?.slots,
    hasCoinPocket: (initial as any)?.hasCoinPocket,
    photoEmbedAllowed: (initial as any)?.photoEmbedAllowed,
  });
  const [cover, setCover] = useState<PhoneCoverFields>({
    phoneModel: (initial as any)?.phoneModel,
    caseSize: (initial as any)?.caseSize,
    cameraCutout: (initial as any)?.cameraCutout ?? {
      xPct: 7.2,
      yPct: 5.2,
      widthPct: 26,
      heightPct: 18,
      cornerRadiusPct: 16,
    },
    finish: (initial as any)?.finish,
    photoUploadAllowed: (initial as any)?.photoUploadAllowed,
  });
  const [tee, setTee] = useState<TshirtFields>({
    size: (initial as any)?.size,
    tshirtType: (initial as any)?.tshirtType ?? "Round",
    backCustomizationExtraCost: (initial as any)?.backCustomizationExtraCost,
    photoUploadAllowed: (initial as any)?.photoUploadAllowed,
    availableColors: Array.isArray((initial as any)?.availableColors)
      ? (initial as any).availableColors
      : [],
    availableSizes: Array.isArray((initial as any)?.availableSizes)
      ? (initial as any).availableSizes
      : [],
    sizeStockByColor: resolveTshirtSizeStockByColor((initial as any) || {}),
    colorVariants: sanitizeTshirtColorVariants((initial as any)?.colorVariants),
  });
  const [gift, setGift] = useState<GiftFields>({
    giftType: (initial as any)?.giftType,
  });
  const [pendantColorsStr, setPendantColorsStr] = useState(() =>
    Array.isArray((initial as any)?.availableColors)
      ? ((initial as any).availableColors as string[]).join(", ")
      : Array.isArray((initial as any)?.pendantColorVariants)
      ? ((initial as any).pendantColorVariants as Array<{ color?: string }>)
          .map((row) => String(row?.color || "").trim())
          .filter(Boolean)
          .join(", ")
      : ""
  );
  const [engravingColorsStr, setEngravingColorsStr] = useState(() =>
    Array.isArray((initial as any)?.availableColors)
      ? ((initial as any).availableColors as string[]).join(", ")
      : Array.isArray((initial as any)?.engravingColorVariants)
      ? ((initial as any).engravingColorVariants as Array<{ color?: string }>)
          .map((row) => String(row?.color || "").trim())
          .filter(Boolean)
          .join(", ")
      : ""
  );
  const [tshirtColorsStr, setTshirtColorsStr] = useState(() =>
    Array.isArray((initial as any)?.availableColors)
      ? ((initial as any).availableColors as string[]).join(", ")
      : ""
  );
  const [tshirtSizesStr, setTshirtSizesStr] = useState(() =>
    Array.isArray((initial as any)?.availableSizes)
      ? ((initial as any).availableSizes as string[]).join(", ")
      : ""
  );
  // images
  const [images, setImages] = useState<ImagesState>({
    heroImage: (initial as any)?.heroImage,
    previewImage: initial?.previewImage,
    arImage: initial?.arImage,
    backImage: (initial as any)?.backImage,
    moreImages: initial?.moreImages ?? [],
    youtubeVideoUrl: (initial as any)?.youtubeVideoUrl || "",
    useYoutubeAsThumbnail: Boolean((initial as any)?.useYoutubeAsThumbnail),
    heroImageFile: null,
    previewImageFile: null,
    arImageFile: null,
    virtualTryOnAssetFile: null,
    backImageFile: null,
    moreImageFiles: [],
    clearHeroImage: false,
    clearPreviewImage: false,
    clearArImage: false,
    clearBackImage: false,
    removeMoreImages: [],
  });
  const [maskEditorOpen, setMaskEditorOpen] = useState(false);
  const [maskEditorImageUrl, setMaskEditorImageUrl] = useState("");
  const [maskEditorTargetColor, setMaskEditorTargetColor] = useState("");
  const [maskEditorTargetSide, setMaskEditorTargetSide] = useState<
    "front" | "back"
  >("front");
  const [maskEditorDraft, setMaskEditorDraft] = useState<CircleAreaDraft>(() =>
    circleAreaDraftFromPendant((initial as any) || {})
  );
  const maskEditorObjectUrlRef = useRef<string | null>(null);
  const [engravingAreaEditorOpen, setEngravingAreaEditorOpen] = useState(false);
  const [engravingAreaEditorImageUrl, setEngravingAreaEditorImageUrl] = useState("");
  const [engravingAreaEditorTargetSide, setEngravingAreaEditorTargetSide] =
    useState<"front" | "back">("front");
  const [engravingAreaEditorDraft, setEngravingAreaEditorDraft] =
    useState<EngravingAreaDraft>(() =>
      engravingAreaDraftFromArea((initial as any)?.frontEngravingArea, "front")
    );
  const engravingAreaEditorObjectUrlRef = useRef<string | null>(null);
  const [adminEngravingPreviewSide, setAdminEngravingPreviewSide] = useState<
    "front" | "back"
  >("front");
  const [adminEngravingPreviewFrontText, setAdminEngravingPreviewFrontText] =
    useState("Your text");
  const [adminEngravingPreviewBackText, setAdminEngravingPreviewBackText] =
    useState("Back text");
  const [adminEngravingPreviewLayoutBySide, setAdminEngravingPreviewLayoutBySide] =
    useState<Partial<Record<"front" | "back", EngravingPreviewLayout>>>({});
  const [engravingAreaEditorSampleText, setEngravingAreaEditorSampleText] =
    useState("Your text");
  const [engravingAreaEditorLayout, setEngravingAreaEditorLayout] =
    useState<EngravingPreviewLayout | null>(null);

  const releaseMaskEditorObjectUrl = () => {
    if (!maskEditorObjectUrlRef.current) return;
    URL.revokeObjectURL(maskEditorObjectUrlRef.current);
    maskEditorObjectUrlRef.current = null;
  };

  const releaseEngravingAreaEditorObjectUrl = () => {
    if (!engravingAreaEditorObjectUrlRef.current) return;
    URL.revokeObjectURL(engravingAreaEditorObjectUrlRef.current);
    engravingAreaEditorObjectUrlRef.current = null;
  };

  const openPendantAreaEditor = (
    imageUrl: string,
    options?: {
      objectUrlToRevoke?: string;
      variantColor?: string;
      side?: "front" | "back";
      initialDraft?: CircleAreaDraft;
    }
  ) => {
    if (!imageUrl) return;
    releaseMaskEditorObjectUrl();
    if (options?.objectUrlToRevoke) {
      maskEditorObjectUrlRef.current = options.objectUrlToRevoke;
    }
    setMaskEditorTargetColor(String(options?.variantColor || "").trim());
    setMaskEditorTargetSide(options?.side === "back" ? "back" : "front");
    setMaskEditorDraft(
      options?.initialDraft ||
        (options?.side === "back"
          ? circleAreaDraftFromPendantBack(pendant)
          : circleAreaDraftFromPendant(pendant))
    );
    setMaskEditorImageUrl(imageUrl);
    setMaskEditorOpen(true);
  };

  const closePendantAreaEditor = () => {
    setMaskEditorOpen(false);
    setMaskEditorTargetColor("");
    setMaskEditorTargetSide("front");
    setMaskEditorImageUrl("");
    releaseMaskEditorObjectUrl();
  };

  const openEngravingAreaEditor = (
    imageUrl: string,
    options?: {
      objectUrlToRevoke?: string;
      side?: "front" | "back";
      initialDraft?: EngravingAreaDraft;
    }
  ) => {
    if (!imageUrl) return;
    releaseEngravingAreaEditorObjectUrl();
    if (options?.objectUrlToRevoke) {
      engravingAreaEditorObjectUrlRef.current = options.objectUrlToRevoke;
    }
    const side = options?.side === "back" ? "back" : "front";
    setEngravingAreaEditorTargetSide(side);
    setEngravingAreaEditorDraft(
      options?.initialDraft || engravingAreaDraftFromArea(undefined, side)
    );
    setEngravingAreaEditorSampleText(
      side === "back" ? adminEngravingPreviewBackText : adminEngravingPreviewFrontText
    );
    setEngravingAreaEditorLayout(null);
    setEngravingAreaEditorImageUrl(imageUrl);
    setEngravingAreaEditorOpen(true);
  };

  const closeEngravingAreaEditor = () => {
    setEngravingAreaEditorOpen(false);
    setEngravingAreaEditorTargetSide("front");
    setEngravingAreaEditorImageUrl("");
    setEngravingAreaEditorSampleText("Your text");
    setEngravingAreaEditorLayout(null);
    releaseEngravingAreaEditorObjectUrl();
  };

  const applyPendantAreaEditor = () => {
    setPendant((prev) => {
      const targetColor = String(maskEditorTargetColor || "")
        .trim()
        .toLowerCase();
      const targetSide = maskEditorTargetSide === "back" ? "back" : "front";
      const defaultArea =
        targetSide === "back"
          ? applyCircleAreaToPendant(prev, maskEditorDraft, "back")
          : applyCircleAreaToPendant(prev, maskEditorDraft, "front");
      if (!targetColor) {
        return {
          ...prev,
          ...(targetSide === "back"
            ? { backPhotoCustomization: defaultArea }
            : { photoCustomization: defaultArea }),
        };
      }

      let matched = false;
      const nextVariants = sanitizePendantColorVariants(prev.pendantColorVariants).map(
        (variant) => {
          if (String(variant.color || "").trim().toLowerCase() !== targetColor) {
            return variant;
          }
          matched = true;
          const baseSource: PendantFields = {
            photoUploadAllowed: prev.photoUploadAllowed,
            photoCustomization: variant.photoCustomization ?? prev.photoCustomization,
            backPhotoCustomization:
              variant.backPhotoCustomization ??
              prev.backPhotoCustomization ??
              prev.photoCustomization,
          };
          return {
            ...variant,
            ...(targetSide === "back"
              ? {
                  backPhotoCustomization: applyCircleAreaToPendant(
                    baseSource,
                    maskEditorDraft,
                    "back"
                  ),
                }
              : {
                  photoCustomization: applyCircleAreaToPendant(
                    baseSource,
                    maskEditorDraft,
                    "front"
                  ),
                }),
          };
        }
      );

      if (!matched) {
        return {
          ...prev,
          ...(targetSide === "back"
            ? { backPhotoCustomization: defaultArea }
            : { photoCustomization: defaultArea }),
        };
      }

      return {
        ...prev,
        pendantColorVariants: nextVariants,
      };
    });
    closePendantAreaEditor();
  };

  const applyEngravingAreaEditor = () => {
    setEngraving((prev) => {
      const side = engravingAreaEditorTargetSide === "back" ? "back" : "front";
      const nextArea = sanitizeEngravingArea(engravingAreaEditorDraft, side);
      return {
        ...prev,
        ...(side === "back"
          ? { backEngravingArea: nextArea }
          : { frontEngravingArea: nextArea }),
      };
    });
    closeEngravingAreaEditor();
  };

  const openPendantAreaEditorFromVariant = (
    variant: PendantColorVariantDraft,
    side: "front" | "back" = "front"
  ) => {
    const initialDraft =
      side === "back"
        ? circleAreaDraftFromBackPhotoCustomization(
            variant.backPhotoCustomization,
            pendant
          )
        : circleAreaDraftFromPhotoCustomization(
            variant.photoCustomization,
            pendant
          );
    const useFrontImageForBack = side === "back" && pendant.isDoubleSidedPendant;
    const variantImageFile =
      side === "back" && !useFrontImageForBack
        ? variant.backImageFile
        : variant.frontImageFile;
    const variantImage =
      side === "back" && !useFrontImageForBack
        ? variant.backImage
        : variant.frontImage;
    if (variantImageFile) {
      const objectUrl = URL.createObjectURL(variantImageFile);
      openPendantAreaEditor(objectUrl, {
        objectUrlToRevoke: objectUrl,
        variantColor: variant.color,
        side,
        initialDraft,
      });
      return;
    }
    if (variantImage) {
      const resolved = assetUrl(variantImage);
      if (resolved) {
        openPendantAreaEditor(resolved, {
          variantColor: variant.color,
          side,
          initialDraft,
        });
      }
    }
  };

  useEffect(
    () => () => {
      releaseMaskEditorObjectUrl();
      releaseEngravingAreaEditorObjectUrl();
    },
    []
  );

  useEffect(() => {
    if (!category) return;
    if (category === ProductCategory.Gift) {
      setIsCustomizable(false);
      return;
    }
    if ((initial?.category || "") !== category && !initial?._id) {
      setIsCustomizable(true);
    }
  }, [category, initial?.category, initial?._id]);

  // ------------- auto-calc logic -------------
  const clampPct = (n?: number) =>
    n === undefined ? undefined : Math.max(0, Math.min(100, +(+n).toFixed(2)));

  const handleNumberFieldFocusCapture: React.FocusEventHandler<HTMLFormElement> = (
    event
  ) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement) || target.type !== "number") return;
    const value = String(target.value || "").trim();
    if (!/^0(?:\.0+)?$/.test(value)) return;
    requestAnimationFrame(() => {
      if (document.activeElement === target) {
        target.select();
      }
    });
  };

  const updatePrice = (v: string) => {
    setPrice(v === "" ? undefined : Number(v));
  };

  const updateCompare = (v: string) => {
    const C = num(v);
    setCompareAtPrice(C);

    if (C === undefined) return;

    if (offerPercent !== undefined) {
      // compareAt + discount -> price
      const P = C * (1 - offerPercent / 100);
      setPrice(roundDownMoney(P));
    } else if (price !== undefined && C > 0) {
      // price + compareAt -> discount
      const D = (1 - price / C) * 100;
      setOfferPercent(clampPct(roundUpPercentValue(D)));
    }
  };

  useEffect(() => {
    if (
      typeof price === "number" &&
      typeof offerPercent === "number" &&
      offerPercent >= 0 &&
      offerPercent < 100
    ) {
      const computed = price / (1 - offerPercent / 100);
      setCompareAtPrice(+computed.toFixed(2));
    } else {
      // if either missing (or 100%), don't auto-set
      setCompareAtPrice(undefined);
    }
  }, [price, offerPercent]);

  const updateDiscount = (v: string) => {
    const n = v === "" ? undefined : Number(v);
    setOfferPercent(
      n === undefined ? undefined : clampPct(roundUpPercentValue(n))
    );
  };

  // ------------- image handlers -------------
  const onPickHero: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    setImages((s) => ({ ...s, heroImageFile: f, clearHeroImage: false }));
    e.target.value = "";
  };
  const onRemoveHero = () => {
    setImages((s) => ({
      ...s,
      heroImageFile: null,
      clearHeroImage: true,
      heroImage: undefined,
    }));
  };
  const onPickPreview: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    setImages((s) => ({ ...s, previewImageFile: f, clearPreviewImage: false }));
    e.target.value = "";
  };
  const onRemovePreview = () => {
    setImages((s) => ({
      ...s,
      previewImageFile: null,
      clearPreviewImage: true,
      previewImage: undefined,
    }));
  };
  const onPickAR: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    setImages((s) => ({ ...s, arImageFile: f, clearArImage: false }));
    e.target.value = "";
  };
  const onRemoveAR = () => {
    setImages((s) => ({
      ...s,
      arImageFile: null,
      clearArImage: true,
      arImage: undefined,
    }));
  };
  const onPickVirtualTryOnAsset: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    setImages((s) => ({ ...s, virtualTryOnAssetFile: f }));
    setVirtualTryOn((current) => ({
      ...defaultVirtualTryOnConfig(current),
      enabled: true,
    }));
    e.target.value = "";
  };
  const onRemoveVirtualTryOnAsset = () => {
    setImages((s) => ({
      ...s,
      virtualTryOnAssetFile: null,
    }));
    setVirtualTryOn((current) => ({
      ...defaultVirtualTryOnConfig(current),
      enabled: false,
      assetUrl: undefined,
    }));
  };
  const onPickBack: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.target.files?.[0] ?? null;
    setImages((s) => ({ ...s, backImageFile: f, clearBackImage: false }));
    e.target.value = "";
  };
  const onRemoveBack = () => {
    setImages((s) => ({
      ...s,
      backImageFile: null,
      clearBackImage: true,
      backImage: undefined,
    }));
  };
  const onPickMore: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const files = Array.from(e.target.files ?? []);
    if (!files.length) return;
    setImages((s) => ({
      ...s,
      moreImageFiles: [...s.moreImageFiles, ...files],
    }));
  };
  const removeExistingGallery = (url: string) => {
    // push raw stored URL for removal, and hide immediately
    setImages((s) => ({
      ...s,
      removeMoreImages: s.removeMoreImages.includes(url)
        ? s.removeMoreImages
        : [...s.removeMoreImages, url],
      moreImages: s.moreImages.filter((u) => u !== url),
    }));
  };
  const removePickedGallery = (idx: number) => {
    setImages((s) => {
      const copy = [...s.moreImageFiles];
      copy.splice(idx, 1);
      return { ...s, moreImageFiles: copy };
    });
  };

  // ------------- payload build -------------
  const buildPayload = (): ProductCreateOrUpdatePayload => {
    const base: ProductBaseDTO = {
      category: category as ProductCategory,
      name,
      slug,
      description: description || undefined,
      jewelryCareHtml,
      color: color || undefined,
      material: material || undefined,
      ...(dimensions &&
        (dimensions.width != null ||
          dimensions.height != null ||
          dimensions.depth != null ||
          dimensions.diameter != null) && { dimensions }),
      price: price ?? 0,
      compareAtPrice,
      offerPercent,
      isActive,
      featured,
      topSeller,
      stockCount: stockCount ?? 0,
      isCustomizable,
      giftWrapEnabled,
      tags,
      seoTitle: normalizeSeoTextInput(seoTitle, MAX_SEO_TITLE_LENGTH) || undefined,
      seoDescription:
        normalizeSeoTextInput(seoDescription, MAX_SEO_DESCRIPTION_LENGTH) ||
        undefined,
      seoKeywords: parseSeoKeywordsInput(seoKeywordsStr),
      seoSlug: normalizeSeoSlugInput(seoSlug) || undefined,
      sku: sku || undefined,
      // DEPRECATED gallery kept to satisfy BE (we still send it)
      images: initial?.images ?? [],
      // new image URLs will be set by BE after upload
      heroImage: images.heroImage,
      previewImage: images.previewImage,
      arImage: images.arImage,
      moreImages: images.moreImages,
      youtubeVideoUrl: images.youtubeVideoUrl?.trim() || undefined,
      useYoutubeAsThumbnail: Boolean(
        images.useYoutubeAsThumbnail && images.youtubeVideoUrl?.trim(),
      ),
      virtualTryOn:
        category === ProductCategory.Pendant
          ? (() => {
              const variants = (Array.isArray(virtualTryOn.variants)
                ? virtualTryOn.variants
                : []
              )
                .map((variant) => ({
                  ...defaultVirtualTryOnConfig(variant),
                  variants: undefined,
                  enabled: Boolean(variant.enabled && variant.assetUrl),
                }))
                .filter((variant) => variant.assetUrl || variant.enabled);
              const defaultConfig = defaultVirtualTryOnConfig(virtualTryOn);
              const defaultEnabled = Boolean(
                virtualTryOn.enabled &&
                  (virtualTryOn.assetUrl || images.virtualTryOnAssetFile)
              );
              return {
                ...defaultConfig,
                assetUrl: virtualTryOn.assetUrl,
                enabled:
                  defaultEnabled ||
                  variants.some((variant) =>
                    Boolean(variant.enabled && variant.assetUrl)
                  ),
                variants,
              };
            })()
          : undefined,
    };

    // category fields merged in
    const catFields: Partial<
      PendantFields &
        EngravingFields &
        CupFields &
        LicensePlateFields &
        WalletFields &
        PhoneCoverFields &
        TshirtFields
    > = {};
    switch (category) {
      case ProductCategory.Pendant:
        {
          const cleanedOptionDraftsInput = (pendant.chainOptions || []).reduce<
            PendantChainOptionDraft[]
          >((acc, opt) => {
              const draftOpt = opt as PendantChainOptionDraft;
              const sanitized = sanitizeChainOption(draftOpt);
              if (!sanitized) return acc;
              acc.push({
                ...sanitized,
                imageFile: draftOpt.imageFile ?? null,
              });
              return acc;
            }, []);
          const cleanedOptionDrafts = withDefaultChain(
            cleanedOptionDraftsInput
          );
          const cleanedOptions = cleanedOptionDrafts.map(
            ({ imageFile: _imageFile, ...rest }) => rest
          );
          const defaultChain = cleanedOptions.find((opt) => opt.defaultIncluded);
          const mergedLengths = Array.from(
            new Set(
              cleanedOptions
                .flatMap((opt) => opt.lengthsInch || [])
                .map((n) => Number(n))
                .filter((n) => Number.isFinite(n) && n > 0)
            )
          );
          const normalizedEngraving = pendant.engraving
            ? {
                ...withEngraving(pendant),
                enabled: pendant.isDoubleSidedPendant
                  ? false
                  : withEngraving(pendant).enabled,
                text: undefined,
                font: undefined,
                extraCost: pendant.isDoubleSidedPendant
                  ? 0
                  : withEngraving(pendant).extraCost,
              }
            : undefined;
          const normalizedPendantColors = parseTextCsv(pendantColorsStr);
          const normalizedPendantColorStocks = syncPendantColorStocksWithColors(
            pendant.colorStocks,
            normalizedPendantColors
          );
          const normalizedPendantColorPrices = prunePendantColorPricesByColors(
            pendant.colorPrices,
            normalizedPendantColors
          );
          const normalizedPendantColorVariants = syncPendantColorVariantsWithColors(
            pendant.pendantColorVariants,
            normalizedPendantColors
          ).map((variant) =>
            pendant.isDoubleSidedPendant
              ? {
                  ...variant,
                  backImage: undefined,
                  backImageFile: null,
                }
              : variant
          );
          const derivedPendantColor = normalizedPendantColors[0] || undefined;
          const derivedPendantStockCount = derivePendantTotalStock(
            normalizedPendantColorStocks
          );
          base.color = derivedPendantColor;
          base.stockCount = derivedPendantStockCount;
          Object.assign(catFields, {
            ...pendant,
            isDoubleSidedPendant: Boolean(pendant.isDoubleSidedPendant),
            backImage: pendant.isDoubleSidedPendant
              ? undefined
              : images.backImage || pendant.backImage,
            photoCustomization: {
              ...withPhotoCustomization(pendant),
              enabled: pendant.photoUploadAllowed !== false,
            },
            backPhotoCustomization: {
              ...withBackPhotoCustomization(pendant),
              enabled: pendant.photoUploadAllowed !== false,
            },
            chainOptions: cleanedOptionDrafts as any,
            chainType: defaultChain?.chainType || pendant.chainType,
            chainLengthInch: mergedLengths.length
              ? mergedLengths
              : pendant.chainLengthInch,
            availableColors: normalizedPendantColors,
            colorStocks: normalizedPendantColorStocks,
            colorPrices: normalizedPendantColorPrices,
            pendantColorVariants: normalizedPendantColorVariants,
            engraving: normalizedEngraving,
          } as PendantFields);
        }
        break;
      case ProductCategory.Engraving:
        {
          const normalizedColors = parseTextCsv(engravingColorsStr);
          const syncedEngraving = syncEngravingFieldsWithColors(
            engraving,
            normalizedColors
          );
          const frontEngravingArea = sanitizeEngravingArea(
            {
              ...syncedEngraving.frontEngravingArea,
              textLimit: syncedEngraving.frontTextLimit,
              maxLines: syncedEngraving.frontMaxLines,
            },
            "front"
          );
          const backEngravingArea =
            syncedEngraving.supportsBackEngraving &&
            syncedEngraving.backEngravingArea
              ? sanitizeEngravingArea(
                  {
                    ...syncedEngraving.backEngravingArea,
                    textLimit: syncedEngraving.backTextLimit,
                    maxLines: syncedEngraving.backMaxLines,
                  },
                  "back"
                )
              : undefined;
          const engravingAreas = [
            frontEngravingArea,
            ...(backEngravingArea ? [backEngravingArea] : []),
          ];
          const derivedEngravingColor = normalizedColors[0] || undefined;
          const derivedEngravingStockCount = derivePendantTotalStock(
            syncedEngraving.colorStocks
          );
          base.color = derivedEngravingColor;
          base.stockCount = derivedEngravingStockCount;
          Object.assign(catFields, {
            ...syncedEngraving,
            availableColors: normalizedColors,
            frontEngravingArea,
            backEngravingArea,
            engravingAreas,
            backImage: images.backImage || undefined,
          } as EngravingFields & { backImage?: string });
        }
        break;
      case ProductCategory.Cup:
        Object.assign(catFields, cup);
        break;
      case ProductCategory.LicensePlate:
        Object.assign(catFields, plate);
        break;
      case ProductCategory.Wallet:
        Object.assign(catFields, wallet);
        break;
      case ProductCategory.PhoneCover:
        Object.assign(catFields, cover);
        break;
      case ProductCategory.Tshirt:
        Object.assign(catFields, tee);
        break;
      case ProductCategory.Gift:
        Object.assign(catFields, gift);
        break;
    }

    const payload = {
      ...base,
      ...catFields,
      model3dFile, clearModel3d, model3dPhotoMaterial, model3dEngravingMaterial, model3dWidthMm,
      // files + flags
      heroImageFile: images.heroImageFile ?? undefined,
      previewImageFile: images.previewImageFile ?? undefined,
      arImageFile: images.arImageFile ?? undefined,
      virtualTryOnAssetFile: images.virtualTryOnAssetFile ?? undefined,
      backImageFile:
        category === ProductCategory.Pendant && pendant.isDoubleSidedPendant
          ? undefined
          : images.backImageFile ?? undefined,
      moreImageFiles: images.moreImageFiles,
      clearHeroImage: images.clearHeroImage,
      clearPreviewImage: images.clearPreviewImage,
      clearArImage: images.clearArImage,
      clearBackImage: images.clearBackImage,
      removeMoreImages: images.removeMoreImages,
    } as ProductCreateOrUpdatePayload;

    return payload;
  };

  // ------------- submit -------------
  const [saving, setSaving] = useState(false);
  const onSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    if (!category || !name || !slug) {
      alert("Category, name and slug are required.");
      return;
    }
    if (
      category === ProductCategory.Pendant &&
      activeVirtualTryOn.enabled &&
      (activeVirtualTryOn.assetUrl ||
        (activeVirtualTryOnIndex < 0 && images.virtualTryOnAssetFile)) &&
      !hasValidVirtualTryOnPhysicalMeasurement(activeVirtualTryOn)
    ) {
      alert(
        "Please draw a physical measurement line on the pendant and enter the real-world length it represents."
      );
      return;
    }
    try {
      setSaving(true);

      const parsedTags = parseTags(tagsStr);
      const parsedSeoKeywords = parseSeoKeywordsInput(seoKeywordsStr);
      const parsedTshirtColors = parseTags(tshirtColorsStr);
      const parsedTshirtSizes = parseSizeTags(tshirtSizesStr);
      const parsedSizeStockByColor = syncSizeStockByColorWithColorAndSize(
        tee.sizeStockByColor,
        parsedTshirtColors,
        parsedTshirtSizes
      );
      const parsedLegacyStocks = deriveLegacyStocksFromSizeStockByColor(
        parsedSizeStockByColor
      );
      const parsedTshirtColorVariants = syncColorVariantsWithColors(
        sanitizeTshirtColorVariants(tee.colorVariants),
        parsedTshirtColors
      );

      const payload0 = buildPayload();

      const payload: typeof payload0 = {
        ...payload0,
        tags: parsedTags,
        seoKeywords: parsedSeoKeywords,
        ...(category === ProductCategory.Tshirt
          ? {
              availableColors: parsedTshirtColors,
              colorStocks: parsedLegacyStocks.colorStocks,
              availableSizes: parsedTshirtSizes,
              sizeStocks: parsedLegacyStocks.sizeStocks,
              sizeStockByColor: parsedSizeStockByColor,
              colorVariants: parsedTshirtColorVariants,
            }
          : {}),
      };

      const saved =
        mode === "edit" && initial?._id
          ? await ProductsAPI.update(initial._id, payload)
          : await ProductsAPI.create(payload);

      // sync image state if staying on page
      setImages({
        heroImage: (saved as any).heroImage,
        previewImage: saved.previewImage,
        arImage: saved.arImage,
        backImage: (saved as any).backImage,
        moreImages: saved.moreImages ?? [],
        youtubeVideoUrl: (saved as any).youtubeVideoUrl || "",
        useYoutubeAsThumbnail: Boolean((saved as any).useYoutubeAsThumbnail),
        heroImageFile: null,
        previewImageFile: null,
        arImageFile: null,
        virtualTryOnAssetFile: null,
        backImageFile: null,
        moreImageFiles: [],
        clearHeroImage: false,
        clearPreviewImage: false,
        clearArImage: false,
        clearBackImage: false,
        removeMoreImages: [],
      });
      setJewelryCareHtml((saved as any).jewelryCareHtml ?? "");
      setSeoTitle(saved.seoTitle ?? "");
      setSeoDescription(saved.seoDescription ?? "");
      setSeoKeywordsStr((saved.seoKeywords ?? []).join(", "));
      setSeoSlug(saved.seoSlug ?? "");
      setModel3dUrl(saved.model3dUrl);
      setModel3dFile(undefined);
      setClearModel3d(false);
      setVirtualTryOn(defaultVirtualTryOnConfig((saved as any).virtualTryOn));
      setActiveVirtualTryOnIndex(-1);
      if (saved.category === ProductCategory.Tshirt) {
        const nextColors = Array.isArray((saved as any).availableColors)
          ? ((saved as any).availableColors as string[])
          : [];
        const nextSizes = Array.isArray((saved as any).availableSizes)
          ? ((saved as any).availableSizes as string[])
          : [];
        setTshirtColorsStr(nextColors.join(", "));
        setTshirtSizesStr(nextSizes.join(", "));
        setTee((prev) => ({
          ...prev,
          size: (saved as any).size,
          tshirtType: (saved as any).tshirtType ?? prev.tshirtType,
          backCustomizationExtraCost:
            (saved as any).backCustomizationExtraCost ??
            prev.backCustomizationExtraCost,
          photoUploadAllowed:
            (saved as any).photoUploadAllowed ?? prev.photoUploadAllowed,
          availableColors: nextColors,
          availableSizes: nextSizes,
          sizeStockByColor: resolveTshirtSizeStockByColor((saved as any) || {}),
          colorVariants: sanitizeTshirtColorVariants(
            (saved as any).colorVariants
          ),
        }));
      }
      if (saved.category === ProductCategory.Pendant) {
        const nextColors = Array.isArray((saved as any).availableColors)
          ? ((saved as any).availableColors as string[])
          : Array.isArray((saved as any).pendantColorVariants)
          ? ((saved as any).pendantColorVariants as Array<{ color?: string }>)
              .map((row) => String(row?.color || "").trim())
              .filter(Boolean)
          : [];
        setColor(nextColors[0] || "");
        setPendantColorsStr(nextColors.join(", "));
        setStockCount(derivePendantTotalStock((saved as any).colorStocks));
        setPendant((prev) => ({
          ...prev,
          chainType: (saved as any).chainType,
          chainLengthInch: Array.isArray((saved as any).chainLengthInch)
            ? (saved as any).chainLengthInch
            : prev.chainLengthInch,
          chainOptions: buildInitialChainOptions(saved as Product),
          isDoubleSidedPendant: Boolean((saved as any).isDoubleSidedPendant),
          photoUploadAllowed:
            (saved as any).photoUploadAllowed ?? prev.photoUploadAllowed,
          backImage: (saved as any).backImage ?? prev.backImage,
          photoCustomization:
            (saved as any).photoCustomization ?? prev.photoCustomization,
          backPhotoCustomization:
            (saved as any).backPhotoCustomization ?? prev.backPhotoCustomization,
          engraving: (saved as any).isDoubleSidedPendant
            ? {
                ...withEngraving({
                  ...prev,
                  engraving: (saved as any).engraving ?? prev.engraving,
                }),
                enabled: false,
                extraCost: 0,
              }
            : (saved as any).engraving ?? prev.engraving,
          availableColors: nextColors,
          colorStocks: sanitizePendantColorStocks((saved as any).colorStocks),
          colorPrices: sanitizePendantColorPrices((saved as any).colorPrices),
          pendantColorVariants: sanitizePendantColorVariants(
            (saved as any).pendantColorVariants
          ),
        }));
      }
      if (saved.category === ProductCategory.Engraving) {
        const nextColors = Array.isArray((saved as any).availableColors)
          ? ((saved as any).availableColors as string[])
          : Array.isArray((saved as any).engravingColorVariants)
          ? ((saved as any).engravingColorVariants as Array<{ color?: string }>)
              .map((row) => String(row?.color || "").trim())
              .filter(Boolean)
          : [];
        setColor(nextColors[0] || "");
        setEngravingColorsStr(nextColors.join(", "));
        setStockCount(derivePendantTotalStock((saved as any).colorStocks));
        setEngraving(
          withEngravingProductDefaults({
            ...(saved as any),
            availableColors: nextColors,
          })
        );
      }

      onSaved?.(saved);
    } catch (err: any) {
      const message =
        err?.response?.data?.message || err?.message || "Failed to save";
      alert(message);
    } finally {
      setSaving(false);
    }
  };

  // ------------- UI helpers -------------
  const heroSrc = useMemo(
    () =>
      images.heroImageFile
        ? URL.createObjectURL(images.heroImageFile)
        : images.heroImage
        ? assetUrl(images.heroImage)
        : "",
    [images.heroImage, images.heroImageFile]
  );

  const previewSrc = useMemo(
    () =>
      images.previewImageFile
        ? URL.createObjectURL(images.previewImageFile)
        : images.previewImage
        ? assetUrl(images.previewImage)
        : "",
    [images.previewImageFile, images.previewImage]
  );

  const arSrc = useMemo(() => {
    if (isImageFile(images.arImageFile))
      return URL.createObjectURL(images.arImageFile as File);
    if (images.arImage && isImageUrl(images.arImage))
      return assetUrl(images.arImage);
    return ""; // not an image (probably .glb/.usdz) – we’ll show a chip instead
  }, [images.arImageFile, images.arImage]);

  const virtualTryOnVariants = useMemo(
    () =>
      Array.isArray(virtualTryOn.variants)
        ? virtualTryOn.variants.map((variant) => defaultVirtualTryOnConfig(variant))
        : [],
    [virtualTryOn.variants]
  );

  const activeVirtualTryOn = useMemo(
    () =>
      activeVirtualTryOnIndex >= 0
        ? virtualTryOnVariants[activeVirtualTryOnIndex] ||
          defaultVirtualTryOnConfig()
        : virtualTryOn,
    [activeVirtualTryOnIndex, virtualTryOn, virtualTryOnVariants]
  );

  const updateActiveVirtualTryOn = (next: VirtualTryOnConfig) => {
    setVirtualTryOn((current) => {
      if (activeVirtualTryOnIndex < 0) {
        return {
          ...defaultVirtualTryOnConfig(next),
          variants: Array.isArray(current.variants) ? current.variants : [],
        };
      }
      const variants = Array.isArray(current.variants)
        ? current.variants.map((variant) => defaultVirtualTryOnConfig(variant))
        : [];
      variants[activeVirtualTryOnIndex] = {
        ...defaultVirtualTryOnConfig(next),
        variants: undefined,
      };
      return {
        ...defaultVirtualTryOnConfig(current),
        variants,
      };
    });
  };

  const addVirtualTryOnVariant = () => {
    const nextColor =
      pendant.availableColors?.[0] ||
      pendant.pendantColorVariants?.[0]?.color ||
      color ||
      "";
    setVirtualTryOn((current) => {
      const variants = Array.isArray(current.variants)
        ? current.variants.map((variant) => defaultVirtualTryOnConfig(variant))
        : [];
      const nextVariant: VirtualTryOnConfig = {
        ...defaultVirtualTryOnConfig(current),
        enabled: false,
        assetUrl: undefined,
        label: nextColor ? `${nextColor} front` : "Front try-on",
        color: nextColor || undefined,
        side: "front",
        variants: undefined,
      };
      const nextIndex = variants.length;
      setActiveVirtualTryOnIndex(nextIndex);
      return {
        ...defaultVirtualTryOnConfig(current),
        variants: [...variants, nextVariant],
      };
    });
  };

  const removeActiveVirtualTryOnVariant = () => {
    if (activeVirtualTryOnIndex < 0) return;
    setVirtualTryOn((current) => {
      const variants = (Array.isArray(current.variants) ? current.variants : [])
        .map((variant) => defaultVirtualTryOnConfig(variant))
        .filter((_, index) => index !== activeVirtualTryOnIndex);
      setActiveVirtualTryOnIndex(-1);
      return {
        ...defaultVirtualTryOnConfig(current),
        variants,
      };
    });
  };

  const virtualTryOnAssetSrc = useMemo(() => {
    if (activeVirtualTryOnIndex >= 0) {
      return activeVirtualTryOn.assetUrl && isImageUrl(activeVirtualTryOn.assetUrl)
        ? assetUrl(activeVirtualTryOn.assetUrl)
        : "";
    }
    if (isImageFile(images.virtualTryOnAssetFile)) {
      return URL.createObjectURL(images.virtualTryOnAssetFile as File);
    }
    if (activeVirtualTryOn.assetUrl && isImageUrl(activeVirtualTryOn.assetUrl)) {
      return assetUrl(activeVirtualTryOn.assetUrl);
    }
    return "";
  }, [
    activeVirtualTryOn.assetUrl,
    activeVirtualTryOnIndex,
    images.virtualTryOnAssetFile,
  ]);

  const existingVirtualTryOnImageOptions = useMemo(() => {
    const options: Array<{
      label: string;
      value: string;
      src: string;
      side: "front" | "back";
      color?: string;
    }> = [];
    const seen = new Set<string>();
    const add = (
      label: string,
      value: string | undefined,
      side: "front" | "back",
      optionColor?: string
    ) => {
      const trimmed = String(value || "").trim();
      if (!trimmed || !isImageUrl(trimmed)) return;
      const key = trimmed.toLowerCase();
      if (seen.has(key)) return;
      seen.add(key);
      options.push({
        label,
        value: trimmed,
        src: assetUrl(trimmed) || trimmed,
        side,
        color: optionColor,
      });
    };

    const allowBack =
      Boolean(pendant.isDoubleSidedPendant) || Boolean(withEngraving(pendant).enabled);
    add("Front image", images.previewImage, "front", color || undefined);
    if (allowBack) add("Back image", images.backImage, "back", color || undefined);
    sanitizePendantColorVariants(pendant.pendantColorVariants).forEach((variant) => {
      const colorLabel = String(variant.color || "").trim() || "Color";
      add(`${colorLabel} front image`, variant.frontImage, "front", colorLabel);
      if (allowBack) add(`${colorLabel} back image`, variant.backImage, "back", colorLabel);
    });
    return options;
  }, [
    images.backImage,
    images.previewImage,
    color,
    pendant,
  ]);

  const backSrc = useMemo(
    () =>
      images.backImageFile
        ? URL.createObjectURL(images.backImageFile)
        : images.backImage
        ? assetUrl(images.backImage)
        : "",
    [images.backImage, images.backImageFile]
  );
  const adminEngravingPreviewColors = useMemo(
    () => parseTextCsv(engravingColorsStr),
    [engravingColorsStr]
  );
  const adminSyncedEngraving = useMemo(
    () => syncEngravingFieldsWithColors(engraving, adminEngravingPreviewColors),
    [adminEngravingPreviewColors, engraving]
  );
  const adminEngravingPreviewVariants = useMemo(
    () =>
      syncEngravingColorVariantsWithColors(
        adminSyncedEngraving.engravingColorVariants,
        adminEngravingPreviewColors,
        adminSyncedEngraving.defaultChainByColor
      ),
    [adminEngravingPreviewColors, adminSyncedEngraving]
  );

  const getEngravingAreaPreviewImage = (
    side: "front" | "back",
    variants: EngravingColorVariantDraft[]
  ) => {
    const baseSrc = side === "back" ? backSrc : previewSrc;
    if (baseSrc) return baseSrc;
    const storedVariant = variants.find((variant) =>
      side === "back"
        ? Boolean(String(variant.backImage || "").trim())
        : Boolean(String(variant.frontImage || "").trim())
    );
    const variantImage =
      side === "back" ? storedVariant?.backImage : storedVariant?.frontImage;
    return variantImage ? assetUrl(variantImage) || variantImage : "";
  };

  const resolveEngravingAreaEditorSource = (
    side: "front" | "back",
    variants: EngravingColorVariantDraft[]
  ) => {
    const previewImage = getEngravingAreaPreviewImage(side, variants);
    if (previewImage) return { imageUrl: previewImage };

    const variantWithFile = variants.find((variant) =>
      side === "back" ? Boolean(variant.backImageFile) : Boolean(variant.frontImageFile)
    );
    const imageFile =
      side === "back" ? variantWithFile?.backImageFile : variantWithFile?.frontImageFile;
    if (imageFile) {
      const objectUrl = URL.createObjectURL(imageFile);
      return {
        imageUrl: objectUrl,
        objectUrlToRevoke: objectUrl,
      };
    }

    return null;
  };

  // const _toNumberArray = (s: string): number[] =>
  //   s
  //     .split(",")
  //     .map((v) => Number(v.trim()))
  //     .filter((n) => Number.isFinite(n) && n > 0);

  // ------------- render category fields -------------
  const renderCategoryFields = () => {
    switch (category) {
      case ProductCategory.Pendant: {
        const chainOptions = Array.isArray(pendant.chainOptions)
          ? (pendant.chainOptions as PendantChainOptionDraft[])
          : [];
        const setChainOptions = (next: PendantChainOptionDraft[]) =>
          setPendant((prev) => ({ ...prev, chainOptions: next }));
        const parsedPendantColors = parseTextCsv(pendantColorsStr);
        const pendantColorStocks = syncPendantColorStocksWithColors(
          pendant.colorStocks,
          parsedPendantColors
        );
        const pendantColorPrices = prunePendantColorPricesByColors(
          pendant.colorPrices,
          parsedPendantColors
        );
        const pendantColorVariants = syncPendantColorVariantsWithColors(
          pendant.pendantColorVariants,
          parsedPendantColors
        );
        const pendantColorPriceMap = new Map(
          pendantColorPrices.map((row) => [row.color.toLowerCase(), row.price])
        );

        return (
          <div style={card}>
            <div style={sectionTitle}>Pendant</div>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={switchRow}>
                <input
                  type="checkbox"
                  checked={!!pendant.isDoubleSidedPendant}
                  onChange={(e) =>
                    setPendant((s) => ({
                      ...s,
                      isDoubleSidedPendant: e.target.checked,
                      photoUploadAllowed: e.target.checked
                        ? true
                        : s.photoUploadAllowed,
                      photoCustomization: {
                        ...withPhotoCustomization(s),
                        enabled: e.target.checked
                          ? true
                          : s.photoUploadAllowed !== false,
                      },
                      backPhotoCustomization: {
                        ...withBackPhotoCustomization(s),
                        enabled: e.target.checked
                          ? true
                          : s.photoUploadAllowed !== false,
                      },
                      pendantColorVariants: e.target.checked
                        ? sanitizePendantColorVariants(s.pendantColorVariants).map(
                            (variant) => ({
                              ...variant,
                              backImage: undefined,
                              backImageFile: null,
                            })
                          )
                        : s.pendantColorVariants,
                      engraving: e.target.checked
                        ? {
                            ...withEngraving(s),
                            enabled: false,
                            extraCost: 0,
                          }
                        : s.engraving,
                    }))
                  }
                />
                <span>Double sided pendant (front and back photos, no engraving)</span>
              </div>
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
                  gap: 12,
                  alignItems: "end",
                  padding: 12,
                  border: "1px solid #dbeafe",
                  borderRadius: 12,
                  background: "#eff6ff",
                }}
              >
                <div style={row}>
                  <label style={labelCss}>Pendant thickness (cm)</label>
                  <input
                    style={inputCss}
                    type="number"
                    min={0}
                    step="0.01"
                    value={dimensions.depth ?? ""}
                    onChange={(e) => setDim("depth", e.target.value)}
                    placeholder="e.g., 0.35"
                  />
                </div>
                <div style={{ color: "#1e40af", fontSize: 12, lineHeight: 1.5 }}>
                  Used as the storefront thickness/spec value for pendants. Double
                  sided pendants reuse the front color image for the back side.
                </div>
              </div>
              <div style={row}>
                <label style={labelCss}>
                  Pendant colors (comma separated)
                </label>
                <input
                  style={inputCss}
                  value={pendantColorsStr}
                  onChange={(e) => setPendantColorsStr(e.target.value)}
                  placeholder="Gold, Silver, Rose Gold"
                  onBlur={() => {
                    const parsed = parseTextCsv(pendantColorsStr);
                    setPendant((prev) => ({
                      ...prev,
                      availableColors: parsed,
                      colorStocks: syncPendantColorStocksWithColors(
                        prev.colorStocks,
                        parsed
                      ),
                      colorPrices: prunePendantColorPricesByColors(
                        prev.colorPrices,
                        parsed
                      ),
                      pendantColorVariants: syncPendantColorVariantsWithColors(
                        prev.pendantColorVariants,
                        parsed
                      ),
                    }));
                    if (parsed.length && !parsed.includes(color)) {
                      setColor(parsed[0]);
                    }
                  }}
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Stock and optional price by color</label>
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  {parsedPendantColors.length ? (
                    <table
                      style={{
                        borderCollapse: "collapse",
                        minWidth: 420,
                      }}
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              border: "1px solid #e5e7eb",
                              background: "#f8fafc",
                              padding: "8px 10px",
                              textAlign: "left",
                              fontSize: 12,
                            }}
                          >
                            Color
                          </th>
                          <th
                            style={{
                              border: "1px solid #e5e7eb",
                              background: "#f8fafc",
                              padding: "8px 10px",
                              textAlign: "center",
                              fontSize: 12,
                            }}
                          >
                            Stock
                          </th>
                          <th
                            style={{
                              border: "1px solid #e5e7eb",
                              background: "#f8fafc",
                              padding: "8px 10px",
                              textAlign: "center",
                              fontSize: 12,
                            }}
                          >
                            Price Override (optional)
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {parsedPendantColors.map((rowColor) => {
                          const stockValue =
                            pendantColorStocks.find(
                              (rowItem) =>
                                rowItem.color.toLowerCase() ===
                                rowColor.toLowerCase()
                            )?.stockCount ?? 0;
                          const colorPrice = pendantColorPriceMap.get(
                            rowColor.toLowerCase()
                          );
                          return (
                            <tr key={rowColor}>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "8px 10px",
                                  fontWeight: 600,
                                  fontSize: 13,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  {isHexColor(rowColor) ? (
                                    <span
                                      style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: "999px",
                                        border: "1px solid #cbd5e1",
                                        background: rowColor,
                                        display: "inline-block",
                                      }}
                                    />
                                  ) : null}
                                  <span>{rowColor}</span>
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: 6,
                                  minWidth: 110,
                                }}
                              >
                                <input
                                  style={{
                                    ...inputCss,
                                    width: "100%",
                                    textAlign: "center",
                                  }}
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={stockValue}
                                  onChange={(e) => {
                                    const nextStock = Math.max(
                                      0,
                                      Math.round(Number(e.target.value || 0))
                                    );
                                    const next = pendantColorStocks.map((stockRow) =>
                                      stockRow.color.toLowerCase() ===
                                      rowColor.toLowerCase()
                                        ? { ...stockRow, stockCount: nextStock }
                                        : stockRow
                                    );
                                    setPendant((prev) => ({
                                      ...prev,
                                      colorStocks: next,
                                    }));
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: 6,
                                  minWidth: 150,
                                }}
                              >
                                <input
                                  style={{
                                    ...inputCss,
                                    width: "100%",
                                    textAlign: "center",
                                  }}
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={colorPrice ?? ""}
                                  placeholder="Use base price"
                                  onChange={(e) => {
                                    const raw = e.target.value.trim();
                                    setPendant((prev) => {
                                      const existing = prunePendantColorPricesByColors(
                                        prev.colorPrices,
                                        parsedPendantColors
                                      );
                                      if (!raw) {
                                        return {
                                          ...prev,
                                          colorPrices: existing.filter(
                                            (rowItem) =>
                                              rowItem.color.toLowerCase() !==
                                              rowColor.toLowerCase()
                                          ),
                                        };
                                      }
                                      const priceValue = Number(raw);
                                      if (
                                        !Number.isFinite(priceValue) ||
                                        priceValue < 0
                                      ) {
                                        return prev;
                                      }
                                      const nextRows = existing.filter(
                                        (rowItem) =>
                                          rowItem.color.toLowerCase() !==
                                          rowColor.toLowerCase()
                                      );
                                      nextRows.push({
                                        color: rowColor,
                                        price: Number(priceValue.toFixed(2)),
                                      });
                                      return { ...prev, colorPrices: nextRows };
                                    });
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Add pendant colors first, then set stock and optional price
                      override for each color.
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div style={row}>
              <label style={labelCss}>
                {pendant.isDoubleSidedPendant
                  ? "Images by color (same image for front and back)"
                  : "Images by color (front, back, more images)"}
              </label>
              <div
                style={{
                  display: "grid",
                  gap: 12,
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                }}
              >
                {pendantColorVariants.length ? (
                  pendantColorVariants.map((variantRow) => {
                    const frontPreview = variantRow.frontImageFile
                      ? URL.createObjectURL(variantRow.frontImageFile)
                      : variantRow.frontImage
                      ? assetUrl(variantRow.frontImage)
                      : "";
                    const backPreview = variantRow.backImageFile
                      ? URL.createObjectURL(variantRow.backImageFile)
                      : variantRow.backImage
                      ? assetUrl(variantRow.backImage)
                      : "";
                    const existingMore = Array.isArray(variantRow.moreImages)
                      ? variantRow.moreImages
                      : [];
                    const newMore = Array.isArray(variantRow.moreImageFiles)
                      ? variantRow.moreImageFiles
                      : [];
                    const variantFrontCircleDraft = circleAreaDraftFromPhotoCustomization(
                      variantRow.photoCustomization,
                      pendant
                    );
                    const variantBackCircleDraft =
                      circleAreaDraftFromBackPhotoCustomization(
                        variantRow.backPhotoCustomization,
                        pendant
                      );

                    return (
                      <div
                        key={variantRow.color}
                        style={{
                          border: "1px solid #e5e7eb",
                          borderRadius: 10,
                          padding: 10,
                          display: "grid",
                          gap: 10,
                        }}
                      >
                        <div style={{ fontWeight: 700, fontSize: 13 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                            {isHexColor(variantRow.color) ? (
                              <span
                                style={{
                                  width: 16,
                                  height: 16,
                                  borderRadius: "999px",
                                  border: "1px solid #cbd5e1",
                                  background: variantRow.color,
                                  display: "inline-block",
                                }}
                              />
                            ) : null}
                            <span>{variantRow.color}</span>
                          </div>
                          <div style={{ marginTop: 4, color: "#6b7280", fontSize: 12 }}>
                            Front{" "}
                            {variantFrontCircleDraft.shape === "heart"
                              ? "Heart"
                              : "Circle"}
                            : X {variantFrontCircleDraft.xPct.toFixed(2)}% | Y{" "}
                            {variantFrontCircleDraft.yPct.toFixed(2)}% | W{" "}
                            {variantFrontCircleDraft.widthPct.toFixed(2)}% | H{" "}
                            {variantFrontCircleDraft.heightPct.toFixed(2)}%
                          </div>
                          <div style={{ marginTop: 2, color: "#6b7280", fontSize: 12 }}>
                            Back{" "}
                            {variantBackCircleDraft.shape === "heart"
                              ? "Heart"
                              : "Circle"}
                            : X {variantBackCircleDraft.xPct.toFixed(2)}% | Y{" "}
                            {variantBackCircleDraft.yPct.toFixed(2)}% | W{" "}
                            {variantBackCircleDraft.widthPct.toFixed(2)}% | H{" "}
                            {variantBackCircleDraft.heightPct.toFixed(2)}%
                          </div>
                        </div>
                        <div style={grid2}>
                          <div style={row}>
                            <label style={labelCss}>Front image</label>
                            <input
                              type="file"
                              accept="image/*"
                              onChange={(e) => {
                                const file = e.target.files?.[0] || null;
                                const next = pendantColorVariants.map((variant) =>
                                  variant.color === variantRow.color
                                    ? {
                                        ...variant,
                                        frontImageFile: file,
                                      }
                                    : variant
                                );
                                setPendant((prev) => ({
                                  ...prev,
                                  pendantColorVariants: next,
                                }));
                                if (file) {
                                  const objectUrl = URL.createObjectURL(file);
                                  openPendantAreaEditor(objectUrl, {
                                    objectUrlToRevoke: objectUrl,
                                    variantColor: variantRow.color,
                                    side: "front",
                                    initialDraft: variantFrontCircleDraft,
                                  });
                                }
                                e.target.value = "";
                              }}
                            />
                            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                              {frontPreview ? (
                                <img src={frontPreview} alt="" style={thumb} />
                              ) : (
                                <div
                                  style={{
                                    ...thumb,
                                    display: "grid",
                                    placeItems: "center",
                                    color: "#bbb",
                                  }}
                                >
                                  —
                                </div>
                              )}
                              {(variantRow.frontImage || variantRow.frontImageFile) ? (
                                <>
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() =>
                                      openPendantAreaEditorFromVariant(
                                        variantRow,
                                        "front"
                                      )
                                    }
                                  >
                                    Draw Print Area
                                  </button>
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = pendantColorVariants.map((variant) =>
                                        variant.color === variantRow.color
                                          ? {
                                              ...variant,
                                              frontImage: undefined,
                                              frontImageFile: null,
                                            }
                                          : variant
                                      );
                                      setPendant((prev) => ({
                                        ...prev,
                                        pendantColorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </>
                              ) : null}
                            </div>
                          </div>

                          {pendant.isDoubleSidedPendant ? (
                            <div style={row}>
                              <label style={labelCss}>Back side</label>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                  flexWrap: "wrap",
                                }}
                              >
                                {frontPreview ? (
                                  <img src={frontPreview} alt="" style={thumb} />
                                ) : (
                                  <div
                                    style={{
                                      ...thumb,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    —
                                  </div>
                                )}
                                {(variantRow.frontImage || variantRow.frontImageFile) ? (
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() =>
                                      openPendantAreaEditorFromVariant(
                                        variantRow,
                                        "back"
                                      )
                                    }
                                  >
                                    Draw Back Print Area
                                  </button>
                                ) : null}
                              </div>
                              <div style={{ fontSize: 12, color: "#6b7280" }}>
                                Double sided pendants reuse the front color image for
                                the back side. Back photo upload is disabled.
                              </div>
                            </div>
                          ) : (
                            <div style={row}>
                              <label style={labelCss}>Back image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  const next = pendantColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          backImageFile: file,
                                        }
                                      : variant
                                  );
                                  setPendant((prev) => ({
                                    ...prev,
                                    pendantColorVariants: next,
                                  }));
                                  if (file) {
                                    const objectUrl = URL.createObjectURL(file);
                                    openPendantAreaEditor(objectUrl, {
                                      objectUrlToRevoke: objectUrl,
                                      variantColor: variantRow.color,
                                      side: "back",
                                      initialDraft: variantBackCircleDraft,
                                    });
                                  }
                                  e.target.value = "";
                                }}
                              />
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                {backPreview ? (
                                  <img src={backPreview} alt="" style={thumb} />
                                ) : (
                                  <div
                                    style={{
                                      ...thumb,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    —
                                  </div>
                                )}
                                {(variantRow.backImage || variantRow.backImageFile) ? (
                                  <>
                                    <button
                                      type="button"
                                      style={outlineBtn}
                                      onClick={() =>
                                        openPendantAreaEditorFromVariant(
                                          variantRow,
                                          "back"
                                        )
                                      }
                                    >
                                      Draw Engraving Area
                                    </button>
                                    <button
                                      type="button"
                                      style={outlineBtn}
                                      onClick={() => {
                                        const next = pendantColorVariants.map((variant) =>
                                          variant.color === variantRow.color
                                            ? {
                                                ...variant,
                                                backImage: undefined,
                                                backImageFile: null,
                                              }
                                            : variant
                                        );
                                        setPendant((prev) => ({
                                          ...prev,
                                          pendantColorVariants: next,
                                        }));
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </>
                                ) : null}
                              </div>
                            </div>
                          )}
                        </div>

                        <div style={row}>
                          <label style={labelCss}>More images</label>
                          <input
                            type="file"
                            multiple
                            accept="image/*"
                            onChange={(e) => {
                              const files = Array.from(e.target.files || []);
                              if (!files.length) return;
                              const next = pendantColorVariants.map((variant) =>
                                variant.color === variantRow.color
                                  ? {
                                      ...variant,
                                      moreImageFiles: [
                                        ...(variant.moreImageFiles || []),
                                        ...files,
                                      ],
                                    }
                                  : variant
                              );
                              setPendant((prev) => ({
                                ...prev,
                                pendantColorVariants: next,
                              }));
                              e.target.value = "";
                            }}
                          />
                          <div style={galleryWrap}>
                            {existingMore.map((url) => (
                              <div key={url} style={{ display: "grid", gap: 6 }}>
                                <img src={assetUrl(url)} alt="" style={thumb} />
                                <button
                                  type="button"
                                  style={outlineBtn}
                                  onClick={() => {
                                    const next = pendantColorVariants.map((variant) =>
                                      variant.color === variantRow.color
                                        ? {
                                            ...variant,
                                            moreImages: (variant.moreImages || []).filter(
                                              (imageUrl) => imageUrl !== url
                                            ),
                                          }
                                        : variant
                                    );
                                    setPendant((prev) => ({
                                      ...prev,
                                      pendantColorVariants: next,
                                    }));
                                  }}
                                >
                                  Remove
                                </button>
                              </div>
                            ))}
                            {newMore.map((file, fileIndex) => {
                              const fileUrl = URL.createObjectURL(file);
                              return (
                                <div
                                  key={`${variantRow.color}-${file.name}-${fileIndex}`}
                                  style={{ display: "grid", gap: 6 }}
                                >
                                  <img src={fileUrl} alt="" style={thumb} />
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = pendantColorVariants.map((variant) =>
                                        variant.color === variantRow.color
                                          ? {
                                              ...variant,
                                              moreImageFiles: (
                                                variant.moreImageFiles || []
                                              ).filter((_f, idx) => idx !== fileIndex),
                                            }
                                          : variant
                                      );
                                      setPendant((prev) => ({
                                        ...prev,
                                        pendantColorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Add pendant colors first. Then upload front/back/more images for
                    each color.
                  </div>
                )}
              </div>
            </div>
            <div style={{ display: "grid", gap: 10 }}>
              <div style={{ ...sectionTitle, fontSize: 13 }}>Chain Types</div>
              {chainOptions.map((option, idx) => (
                <div
                  key={`chain-${idx}`}
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  {(() => {
                    const chainPreview = option.imageFile
                      ? URL.createObjectURL(option.imageFile)
                      : option.image
                      ? assetUrl(option.image)
                      : "";
                    return (
                      <div style={row}>
                        <label style={labelCss}>Chain photo</label>
                        <input
                          type="file"
                          accept="image/*"
                          onChange={(e) => {
                            const file = e.target.files?.[0] || null;
                            const next = [...chainOptions];
                            next[idx] = {
                              ...next[idx],
                              imageFile: file,
                            };
                            setChainOptions(next);
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 10,
                            flexWrap: "wrap",
                          }}
                        >
                          {chainPreview ? (
                            <img src={chainPreview} alt="" style={thumb} />
                          ) : (
                            <div
                              style={{
                                ...thumb,
                                display: "grid",
                                placeItems: "center",
                                color: "#bbb",
                              }}
                            >
                              —
                            </div>
                          )}
                          {(option.image || option.imageFile) ? (
                            <button
                              type="button"
                              style={outlineBtn}
                              onClick={() => {
                                const next = [...chainOptions];
                                next[idx] = {
                                  ...next[idx],
                                  image: undefined,
                                  imageFile: null,
                                };
                                setChainOptions(next);
                              }}
                            >
                              Remove Photo
                            </button>
                          ) : null}
                        </div>
                      </div>
                    );
                  })()}
                  <div style={grid3}>
                    <div style={row}>
                      <label style={labelCss}>Chain Type</label>
                      <input
                        style={inputCss}
                        value={option.chainType}
                        onChange={(e) => {
                          const next = [...chainOptions];
                          next[idx] = { ...next[idx], chainType: e.target.value };
                          setChainOptions(next);
                        }}
                        placeholder="Rope / Cuban / Figaro"
                      />
                    </div>
                    <div style={row}>
                      <label style={labelCss}>Lengths (in, comma separated)</label>
                      <input
                        style={inputCss}
                        value={
                          option.lengthsInchDraft ??
                          (option.lengthsInch || []).join(", ")
                        }
                        onChange={(e) => {
                          const next = [...chainOptions];
                          next[idx] = {
                            ...next[idx],
                            lengthsInchDraft: e.target.value,
                            lengthsInch: parseNumberCsv(e.target.value),
                          };
                          setChainOptions(next);
                        }}
                        placeholder="18, 20, 22, 24"
                      />
                    </div>
                    <div style={row}>
                      <label style={labelCss}>Colors (comma separated)</label>
                      <input
                        style={inputCss}
                        value={option.colorsDraft ?? (option.colors || []).join(", ")}
                        onChange={(e) => {
                          const next = [...chainOptions];
                          next[idx] = {
                            ...next[idx],
                            colorsDraft: e.target.value,
                            colors: parseTextCsv(e.target.value),
                          };
                          setChainOptions(next);
                        }}
                        placeholder="Gold, Silver, Rose Gold"
                      />
                    </div>
                  </div>
                  <div style={grid3}>
                    <div style={switchRow}>
                      <input
                        type="checkbox"
                        checked={!!option.defaultIncluded}
                        onChange={(e) => {
                          const next = chainOptions.map((row, rowIdx) => ({
                            ...row,
                            defaultIncluded: e.target.checked && rowIdx === idx,
                          }));
                          setChainOptions(next);
                        }}
                      />
                      <span>Default Included (free with pendant)</span>
                    </div>
                    <div style={row}>
                      <label style={labelCss}>Upgrade Price (with pendant)</label>
                      <input
                        style={inputCss}
                        type="number"
                        min={0}
                        step="0.01"
                        value={option.upgradePrice ?? 0}
                        onChange={(e) => {
                          const next = [...chainOptions];
                          next[idx] = {
                            ...next[idx],
                            upgradePrice: Math.max(0, Number(e.target.value || 0)),
                          };
                          setChainOptions(next);
                        }}
                      />
                    </div>
                    <div style={row}>
                      <label style={labelCss}>Standalone Chain Price</label>
                      <input
                        style={inputCss}
                        type="number"
                        min={0}
                        step="0.01"
                        value={option.standalonePrice ?? 0}
                        onChange={(e) => {
                          const next = [...chainOptions];
                          next[idx] = {
                            ...next[idx],
                            standalonePrice: Math.max(
                              0,
                              Number(e.target.value || 0)
                            ),
                          };
                          setChainOptions(next);
                        }}
                      />
                    </div>
                  </div>
                  <div style={{ display: "flex", justifyContent: "flex-end" }}>
                    <button
                      type="button"
                      style={outlineBtn}
                      onClick={() =>
                        setChainOptions(chainOptions.filter((_v, i) => i !== idx))
                      }
                    >
                      Remove Chain Type
                    </button>
                  </div>
                </div>
              ))}
              <div>
                <button
                  type="button"
                  style={outlineBtn}
                  onClick={() =>
                    setChainOptions([
                      ...chainOptions,
                      {
                        chainType: "",
                        lengthsInch: [18, 20, 22, 24],
                        lengthsInchDraft: "18, 20, 22, 24",
                        colors: [],
                        colorsDraft: "",
                        image: undefined,
                        imageFile: null,
                        defaultIncluded: chainOptions.length === 0,
                        upgradePrice: 0,
                        standalonePrice: 0,
                      },
                    ])
                  }
                >
                  Add Chain Type
                </button>
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Mark exactly one chain as included by default (for example Rope). The
                storefront uses the pendant&apos;s selected color as the default chain
                color when that chain supports it.
              </div>
            </div>

            <div style={switchRow}>
              <input
                type="checkbox"
                checked={!!pendant.photoUploadAllowed}
                onChange={(e) =>
                  setPendant((s) => ({
                    ...s,
                    photoUploadAllowed: e.target.checked,
                    photoCustomization: {
                      ...withPhotoCustomization(s),
                      enabled: e.target.checked,
                    },
                    backPhotoCustomization: {
                      ...withBackPhotoCustomization(s),
                      enabled: e.target.checked,
                    },
                  }))
                }
              />
              <span>Enable customer photo upload</span>
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Upload pendant color images, then click{" "}
              <strong>Draw Print Area</strong> (front) and{" "}
              <strong>
                {pendant.isDoubleSidedPendant
                  ? "Draw Back Print Area"
                  : "Draw Engraving Area"}
              </strong>{" "}
              {pendant.isDoubleSidedPendant
                ? "to define independent front/back photo zones on the same pendant image."
                : "(back) to define zones visually."}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Default fallback front area:{" "}
              {toPendantShape(withPhotoCustomization(pendant).shape) === "heart"
                ? "Heart"
                : "Circle"}
              {" "} | X {withPhotoCustomization(pendant).xPct}% | Y{" "}
              {withPhotoCustomization(pendant).yPct}% | W{" "}
              {withPhotoCustomization(pendant).widthPct}% | H{" "}
              {withPhotoCustomization(pendant).heightPct}% (used when a color
              variant does not have its own area)
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Default fallback back area:{" "}
              {toPendantShape(withBackPhotoCustomization(pendant).shape) === "heart"
                ? "Heart"
                : "Circle"}
              {" "} | X {withBackPhotoCustomization(pendant).xPct}% | Y{" "}
              {withBackPhotoCustomization(pendant).yPct}% | W{" "}
              {withBackPhotoCustomization(pendant).widthPct}% | H{" "}
              {withBackPhotoCustomization(pendant).heightPct}% (used when a color
              variant back image does not have its own area)
            </div>

            <div style={sectionTitle}>Engraving</div>
            {pendant.isDoubleSidedPendant ? (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Engraving is unavailable for double sided photo pendants.
              </div>
            ) : null}
            <div style={switchRow}>
              <input
                type="checkbox"
                checked={!!pendant.engraving?.enabled}
                disabled={!!pendant.isDoubleSidedPendant}
                onChange={(e) =>
                  setPendant((s) => ({
                    ...s,
                    engraving: {
                      ...withEngraving(s),
                      enabled: e.target.checked,
                    },
                  }))
                }
              />
              <span>Enable engraving</span>
            </div>
            {!pendant.isDoubleSidedPendant && !!pendant.engraving?.enabled && (
              <div style={grid2}>
                <div style={row}>
                  <label style={labelCss}>Max length</label>
                  <input
                    style={inputCss}
                    type="number"
                    value={pendant.engraving?.maxLength ?? 20}
                    onChange={(e) =>
                      setPendant((s) => ({
                        ...s,
                        engraving: {
                          ...withEngraving(s),
                          maxLength: num(e.target.value),
                        },
                      }))
                    }
                  />
                </div>
                <div style={row}>
                  <label style={labelCss}>Extra cost</label>
                  <input
                    style={inputCss}
                    type="number"
                    value={pendant.engraving?.extraCost ?? ""}
                    onChange={(e) =>
                      setPendant((s) => ({
                        ...s,
                        engraving: {
                          ...withEngraving(s),
                          extraCost: num(e.target.value),
                        },
                      }))
                    }
                  />
                </div>
                <div style={{ ...row, alignContent: "end" }}>
                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Customer chooses engraving text and font on storefront.
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      }

      case ProductCategory.Engraving:
        {
          const parsedEngravingColors = parseTextCsv(engravingColorsStr);
          const syncedEngraving = syncEngravingFieldsWithColors(
            engraving,
            parsedEngravingColors
          );
          const engravingColorStocks = syncPendantColorStocksWithColors(
            syncedEngraving.colorStocks,
            parsedEngravingColors
          );
          const engravingColorPrices = prunePendantColorPricesByColors(
            syncedEngraving.colorPrices,
            parsedEngravingColors
          );
          const engravingChainRows = syncEngravingChainByColorWithColors(
            syncedEngraving.defaultChainByColor,
            parsedEngravingColors
          );
          const engravingColorVariants = syncEngravingColorVariantsWithColors(
            syncedEngraving.engravingColorVariants,
            parsedEngravingColors,
            engravingChainRows
          ).map((variant) => ({
            ...variant,
            matchingChain:
              variant.matchingChain ||
              engravingChainRows.find(
                (row) => row.color.toLowerCase() === variant.color.toLowerCase()
              ),
          }));
          const engravingColorPriceMap = new Map(
            engravingColorPrices.map((rowItem) => [
              rowItem.color.toLowerCase(),
              rowItem.price,
            ])
          );
          const syncColors = (nextColors: string[]) =>
            setEngraving((prev) => syncEngravingFieldsWithColors(prev, nextColors));
          const renderAreaEditor = (
            side: "front" | "back",
            currentArea: EngravingArea | undefined
          ) => {
            const label = side === "front" ? "Front" : "Back";
            const area = sanitizeEngravingArea(currentArea, side);
            const previewImage = getEngravingAreaPreviewImage(
              side,
              engravingColorVariants
            );
            const canDraw =
              side === "front" || syncedEngraving.supportsBackEngraving;
            const hasEditorImage =
              Boolean(previewImage) ||
              engravingColorVariants.some((variant) =>
                side === "back"
                  ? Boolean(variant.backImageFile)
                  : Boolean(variant.frontImageFile)
              );
            return (
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  display: "grid",
                  gap: 10,
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 13 }}>
                  {label} engraving area
                </div>
                <div style={switchRow}>
                  <input
                    type="checkbox"
                    checked={area.enabled !== false}
                    onChange={(e) =>
                      setEngraving((prev) => ({
                        ...prev,
                        ...(side === "front"
                          ? {
                              frontEngravingArea: {
                                ...sanitizeEngravingArea(
                                  prev.frontEngravingArea,
                                  "front"
                                ),
                                enabled: e.target.checked,
                              },
                            }
                          : {
                              backEngravingArea: {
                                ...sanitizeEngravingArea(
                                  prev.backEngravingArea,
                                  "back"
                                ),
                                enabled: e.target.checked,
                              },
                            }),
                      }))
                    }
                  />
                  <span>Area enabled</span>
                </div>
                <div
                  style={{
                    display: "flex",
                    gap: 10,
                    alignItems: "center",
                    flexWrap: "wrap",
                  }}
                >
                  {previewImage ? (
                    <img src={previewImage} alt="" style={thumb} />
                  ) : (
                    <div
                      style={{
                        ...thumb,
                        display: "grid",
                        placeItems: "center",
                        color: "#bbb",
                      }}
                    >
                      —
                    </div>
                  )}
                  <div style={{ display: "grid", gap: 6, flex: 1, minWidth: 220 }}>
                    <div style={{ fontSize: 12, color: "#111827" }}>
                      Rectangle: X {Number(area.xPct ?? 0).toFixed(2)}% | Y{" "}
                      {Number(area.yPct ?? 0).toFixed(2)}% | W{" "}
                      {Number(area.widthPct ?? 0).toFixed(2)}% | H{" "}
                      {Number(area.heightPct ?? 0).toFixed(2)}%
                    </div>
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Draw the engraving zone visually on the uploaded image. The
                      saved rectangle is normalized relative to the source image.
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={outlineBtn}
                        disabled={!canDraw || !hasEditorImage}
                        onClick={() => {
                          const source = resolveEngravingAreaEditorSource(
                            side,
                            engravingColorVariants
                          );
                          if (!source?.imageUrl) return;
                          openEngravingAreaEditor(source.imageUrl, {
                            objectUrlToRevoke: source.objectUrlToRevoke,
                            side,
                            initialDraft: engravingAreaDraftFromArea(area, side),
                          });
                        }}
                      >
                        Draw Customizable Area
                      </button>
                    </div>
                    {!hasEditorImage ? (
                      <div style={{ fontSize: 12, color: "#b45309" }}>
                        Upload a {side} image first, or add a color variant image to
                        draw this area.
                      </div>
                    ) : null}
                    {side === "back" && !syncedEngraving.supportsBackEngraving ? (
                      <div style={{ fontSize: 12, color: "#6b7280" }}>
                        Turn on back engraving above to edit the back-side area.
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            );
          };
          const activeAdminEngravingLayout =
            adminEngravingPreviewLayoutBySide[adminEngravingPreviewSide];

          return (
            <div style={card}>
              <div style={sectionTitle}>Engraving Product</div>

              <div style={grid3}>
                <div style={row}>
                  <label style={labelCss}>Subtype</label>
                  <select
                    style={selectCss}
                    value={syncedEngraving.engravingSubtype ?? "necklace"}
                    onChange={(e) =>
                      setEngraving((prev) => ({
                        ...prev,
                        engravingSubtype: e.target.value || "necklace",
                      }))
                    }
                  >
                    {["necklace", "bracelet", "dog tag"].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={row}>
                  <label style={labelCss}>Base item</label>
                  <select
                    style={selectCss}
                    value={syncedEngraving.baseItem ?? ""}
                    onChange={(e) =>
                      setEngraving((prev) => ({
                        ...prev,
                        baseItem: (e.target.value ||
                          undefined) as EngravingFields["baseItem"],
                      }))
                    }
                  >
                    <option value="">—</option>
                    {["Plate", "Pendant", "Wallet", "Other"].map((value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    ))}
                  </select>
                </div>
                <div style={row}>
                  <label style={labelCss}>Base font size (minimum)</label>
                  <input
                    style={inputCss}
                    type="number"
                    min={8}
                    step={1}
                    value={syncedEngraving.defaultFontSize ?? 18}
                    onChange={(e) =>
                      setEngraving((prev) => ({
                        ...prev,
                        defaultFontSize: Math.max(
                          8,
                          Math.round(Number(e.target.value || 18))
                        ),
                      }))
                    }
                  />
                  <div style={{ fontSize: 12, color: "#64748b" }}>
                    Rendering auto-fits text to the area. This value acts as the minimum size.
                  </div>
                </div>
              </div>

              <div style={row}>
                <label style={labelCss}>Available colors (comma separated)</label>
                <input
                  style={inputCss}
                  value={engravingColorsStr}
                  onChange={(e) => setEngravingColorsStr(e.target.value)}
                  placeholder="Gold, Silver, Rose Gold"
                  onBlur={() => {
                    const parsed = parseTextCsv(engravingColorsStr);
                    syncColors(parsed);
                    if (parsed.length && !parsed.includes(color)) {
                      setColor(parsed[0]);
                    }
                  }}
                />
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Each color can have its own SKU, stock, image set, and matching
                  chain.
                </div>
              </div>

              <div style={row}>
                <label style={labelCss}>Stock and color price overrides</label>
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  {parsedEngravingColors.length ? (
                    <table style={{ borderCollapse: "collapse", minWidth: 480 }}>
                      <thead>
                        <tr>
                          {["Color", "Stock", "Price Override", "Matching Chain"].map(
                            (heading) => (
                              <th
                                key={heading}
                                style={{
                                  border: "1px solid #e5e7eb",
                                  background: "#f8fafc",
                                  padding: "8px 10px",
                                  textAlign:
                                    heading === "Color" ? "left" : "center",
                                  fontSize: 12,
                                }}
                              >
                                {heading}
                              </th>
                            )
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {parsedEngravingColors.map((rowColor) => {
                          const stockValue =
                            engravingColorStocks.find(
                              (rowItem) =>
                                rowItem.color.toLowerCase() ===
                                rowColor.toLowerCase()
                            )?.stockCount ?? 0;
                          const rowPrice = engravingColorPriceMap.get(
                            rowColor.toLowerCase()
                          );
                          const matchingChain = engravingChainRows.find(
                            (rowItem) =>
                              rowItem.color.toLowerCase() ===
                              rowColor.toLowerCase()
                          );
                          return (
                            <tr key={rowColor}>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "8px 10px",
                                  fontWeight: 600,
                                  fontSize: 13,
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 8,
                                  }}
                                >
                                  {isHexColor(rowColor) ? (
                                    <span
                                      style={{
                                        width: 16,
                                        height: 16,
                                        borderRadius: "999px",
                                        border: "1px solid #cbd5e1",
                                        background: rowColor,
                                        display: "inline-block",
                                      }}
                                    />
                                  ) : null}
                                  <span>{rowColor}</span>
                                </div>
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: 6,
                                  minWidth: 110,
                                }}
                              >
                                <input
                                  style={{
                                    ...inputCss,
                                    width: "100%",
                                    textAlign: "center",
                                  }}
                                  type="number"
                                  min={0}
                                  step={1}
                                  value={stockValue}
                                  onChange={(e) => {
                                    const nextStock = Math.max(
                                      0,
                                      Math.round(Number(e.target.value || 0))
                                    );
                                    const nextStocks = engravingColorStocks.map(
                                      (stockRow) =>
                                        stockRow.color.toLowerCase() ===
                                        rowColor.toLowerCase()
                                          ? {
                                              ...stockRow,
                                              stockCount: nextStock,
                                            }
                                          : stockRow
                                    );
                                    const nextVariants = engravingColorVariants.map(
                                      (variant) =>
                                        variant.color.toLowerCase() ===
                                        rowColor.toLowerCase()
                                          ? {
                                              ...variant,
                                              stockCount: nextStock,
                                            }
                                          : variant
                                    );
                                    setEngraving((prev) => ({
                                      ...prev,
                                      colorStocks: nextStocks,
                                      engravingColorVariants: nextVariants,
                                    }));
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: 6,
                                  minWidth: 150,
                                }}
                              >
                                <input
                                  style={{
                                    ...inputCss,
                                    width: "100%",
                                    textAlign: "center",
                                  }}
                                  type="number"
                                  min={0}
                                  step="0.01"
                                  value={rowPrice ?? ""}
                                  placeholder="Use base price"
                                  onChange={(e) => {
                                    const raw = e.target.value.trim();
                                    setEngraving((prev) => {
                                      const existing = prunePendantColorPricesByColors(
                                        prev.colorPrices,
                                        parsedEngravingColors
                                      );
                                      const nextVariants = syncEngravingColorVariantsWithColors(
                                        prev.engravingColorVariants,
                                        parsedEngravingColors,
                                        prev.defaultChainByColor
                                      ).map((variant) =>
                                        variant.color.toLowerCase() ===
                                        rowColor.toLowerCase()
                                          ? {
                                              ...variant,
                                              price: raw
                                                ? Number(
                                                    Number(raw).toFixed(2)
                                                  )
                                                : undefined,
                                            }
                                          : variant
                                      );
                                      if (!raw) {
                                        return {
                                          ...prev,
                                          colorPrices: existing.filter(
                                            (rowItem) =>
                                              rowItem.color.toLowerCase() !==
                                              rowColor.toLowerCase()
                                          ),
                                          engravingColorVariants: nextVariants,
                                        };
                                      }
                                      const priceValue = Number(raw);
                                      if (
                                        !Number.isFinite(priceValue) ||
                                        priceValue < 0
                                      ) {
                                        return prev;
                                      }
                                      const nextRows = existing.filter(
                                        (rowItem) =>
                                          rowItem.color.toLowerCase() !==
                                          rowColor.toLowerCase()
                                      );
                                      nextRows.push({
                                        color: rowColor,
                                        price: Number(priceValue.toFixed(2)),
                                      });
                                      return {
                                        ...prev,
                                        colorPrices: nextRows,
                                        engravingColorVariants: nextVariants,
                                      };
                                    });
                                  }}
                                />
                              </td>
                              <td
                                style={{
                                  border: "1px solid #e5e7eb",
                                  padding: "8px 10px",
                                  textAlign: "center",
                                  fontSize: 12,
                                }}
                              >
                                {matchingChain?.chainName || `${rowColor} chain`}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Add engraving colors first, then set stock, color pricing,
                      and matching chain details.
                    </div>
                  )}
                </div>
              </div>

              <div style={row}>
                <label style={labelCss}>
                  Color variants (SKU, swatch, chain, and images)
                </label>
                <div
                  style={{
                    display: "grid",
                    gap: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  {engravingColorVariants.length ? (
                    engravingColorVariants.map((variantRow) => {
                      const frontPreview = variantRow.frontImageFile
                        ? URL.createObjectURL(variantRow.frontImageFile)
                        : variantRow.frontImage
                        ? assetUrl(variantRow.frontImage)
                        : "";
                      const backPreview = variantRow.backImageFile
                        ? URL.createObjectURL(variantRow.backImageFile)
                        : variantRow.backImage
                        ? assetUrl(variantRow.backImage)
                        : "";
                      const existingMore = Array.isArray(variantRow.moreImages)
                        ? variantRow.moreImages
                        : [];
                      const newMore = Array.isArray(variantRow.moreImageFiles)
                        ? variantRow.moreImageFiles
                        : [];
                      const chainRow =
                        variantRow.matchingChain ||
                        engravingChainRows.find(
                          (rowItem) =>
                            rowItem.color.toLowerCase() ===
                            variantRow.color.toLowerCase()
                        ) || {
                          color: variantRow.color,
                          chainName: `${variantRow.color} chain`,
                          chainColor: variantRow.color,
                          chainIncluded: true,
                        };

                      return (
                        <div
                          key={variantRow.color}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: 10,
                            display: "grid",
                            gap: 10,
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 13 }}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: 8,
                              }}
                            >
                              {isHexColor(variantRow.swatchHex || variantRow.color) ? (
                                <span
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "999px",
                                    border: "1px solid #cbd5e1",
                                    background:
                                      variantRow.swatchHex || variantRow.color,
                                    display: "inline-block",
                                  }}
                                />
                              ) : null}
                              <span>{variantRow.color}</span>
                            </div>
                          </div>

                          <div style={grid3}>
                            <div style={row}>
                              <label style={labelCss}>Variant SKU</label>
                              <input
                                style={inputCss}
                                value={variantRow.sku ?? ""}
                                onChange={(e) => {
                                  const next = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          sku: e.target.value || undefined,
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    engravingColorVariants: next,
                                  }));
                                }}
                                placeholder={`${variantRow.color}-ENG`}
                              />
                            </div>
                            <div style={row}>
                              <label style={labelCss}>Swatch Hex</label>
                              <input
                                style={inputCss}
                                value={variantRow.swatchHex ?? ""}
                                onChange={(e) => {
                                  const next = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          swatchHex: e.target.value || undefined,
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    engravingColorVariants: next,
                                  }));
                                }}
                                placeholder="#D4AF37"
                              />
                            </div>
                            <div style={row}>
                              <label style={labelCss}>Variant stock hook</label>
                              <input
                                style={inputCss}
                                type="number"
                                min={0}
                                step={1}
                                value={variantRow.stockCount ?? ""}
                                onChange={(e) => {
                                  const nextValue = Math.max(
                                    0,
                                    Math.round(Number(e.target.value || 0))
                                  );
                                  const next = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          stockCount: nextValue,
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    engravingColorVariants: next,
                                  }));
                                }}
                                placeholder="Uses color stock row"
                              />
                            </div>
                          </div>

                          <div style={grid3}>
                            <div style={row}>
                              <label style={labelCss}>Matching chain name</label>
                              <input
                                style={inputCss}
                                value={chainRow.chainName ?? ""}
                                onChange={(e) => {
                                  const nextRows = engravingChainRows.map((rowItem) =>
                                    rowItem.color === variantRow.color
                                      ? {
                                          ...rowItem,
                                          chainName: e.target.value || undefined,
                                        }
                                      : rowItem
                                  );
                                  const nextVariants = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          matchingChain: {
                                            ...chainRow,
                                            chainName: e.target.value || undefined,
                                          },
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    defaultChainByColor: nextRows,
                                    engravingColorVariants: nextVariants,
                                  }));
                                }}
                                placeholder={`${variantRow.color} chain`}
                              />
                            </div>
                            <div style={row}>
                              <label style={labelCss}>Matching chain color</label>
                              <input
                                style={inputCss}
                                value={chainRow.chainColor ?? ""}
                                onChange={(e) => {
                                  const nextRows = engravingChainRows.map((rowItem) =>
                                    rowItem.color === variantRow.color
                                      ? {
                                          ...rowItem,
                                          chainColor: e.target.value || undefined,
                                        }
                                      : rowItem
                                  );
                                  const nextVariants = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          matchingChain: {
                                            ...chainRow,
                                            chainColor: e.target.value || undefined,
                                          },
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    defaultChainByColor: nextRows,
                                    engravingColorVariants: nextVariants,
                                  }));
                                }}
                                placeholder={variantRow.color}
                              />
                            </div>
                            <div style={row}>
                              <label style={labelCss}>Chain SKU</label>
                              <input
                                style={inputCss}
                                value={chainRow.chainSku ?? ""}
                                onChange={(e) => {
                                  const nextRows = engravingChainRows.map((rowItem) =>
                                    rowItem.color === variantRow.color
                                      ? {
                                          ...rowItem,
                                          chainSku: e.target.value || undefined,
                                        }
                                      : rowItem
                                  );
                                  const nextVariants = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          matchingChain: {
                                            ...chainRow,
                                            chainSku: e.target.value || undefined,
                                          },
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    defaultChainByColor: nextRows,
                                    engravingColorVariants: nextVariants,
                                  }));
                                }}
                                placeholder={`${variantRow.color}-CHAIN`}
                              />
                            </div>
                          </div>

                          <label style={{ ...switchRow, fontSize: 13 }}>
                            <input
                              type="checkbox"
                              checked={chainRow.chainIncluded !== false}
                              onChange={(e) => {
                                const nextRows = engravingChainRows.map((rowItem) =>
                                  rowItem.color === variantRow.color
                                    ? {
                                        ...rowItem,
                                        chainIncluded: e.target.checked,
                                      }
                                    : rowItem
                                );
                                const nextVariants = engravingColorVariants.map((variant) =>
                                  variant.color === variantRow.color
                                    ? {
                                        ...variant,
                                        matchingChain: {
                                          ...chainRow,
                                          chainIncluded: e.target.checked,
                                        },
                                      }
                                    : variant
                                );
                                setEngraving((prev) => ({
                                  ...prev,
                                  defaultChainByColor: nextRows,
                                  engravingColorVariants: nextVariants,
                                }));
                              }}
                            />
                            <span>Chain included by default with this color</span>
                          </label>

                          <div style={grid2}>
                            <div style={row}>
                              <label style={labelCss}>Front image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  const next = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          frontImageFile: file,
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    engravingColorVariants: next,
                                  }));
                                }}
                              />
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                }}
                              >
                                {frontPreview ? (
                                  <img src={frontPreview} alt="" style={thumb} />
                                ) : (
                                  <div
                                    style={{
                                      ...thumb,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    —
                                  </div>
                                )}
                                {(variantRow.frontImage || variantRow.frontImageFile) ? (
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = engravingColorVariants.map(
                                        (variant) =>
                                          variant.color === variantRow.color
                                            ? {
                                                ...variant,
                                                frontImage: undefined,
                                                frontImageFile: null,
                                              }
                                            : variant
                                      );
                                      setEngraving((prev) => ({
                                        ...prev,
                                        engravingColorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </div>
                            </div>

                            <div style={row}>
                              <label style={labelCss}>Back image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  const next = engravingColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          backImageFile: file,
                                        }
                                      : variant
                                  );
                                  setEngraving((prev) => ({
                                    ...prev,
                                    engravingColorVariants: next,
                                  }));
                                }}
                              />
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 10,
                                }}
                              >
                                {backPreview ? (
                                  <img src={backPreview} alt="" style={thumb} />
                                ) : (
                                  <div
                                    style={{
                                      ...thumb,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    —
                                  </div>
                                )}
                                {(variantRow.backImage || variantRow.backImageFile) ? (
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = engravingColorVariants.map(
                                        (variant) =>
                                          variant.color === variantRow.color
                                            ? {
                                                ...variant,
                                                backImage: undefined,
                                                backImageFile: null,
                                              }
                                            : variant
                                      );
                                      setEngraving((prev) => ({
                                        ...prev,
                                        engravingColorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div style={row}>
                            <label style={labelCss}>Other images</label>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (!files.length) return;
                                const next = engravingColorVariants.map((variant) =>
                                  variant.color === variantRow.color
                                    ? {
                                        ...variant,
                                        moreImageFiles: [
                                          ...(variant.moreImageFiles || []),
                                          ...files,
                                        ],
                                      }
                                    : variant
                                );
                                setEngraving((prev) => ({
                                  ...prev,
                                  engravingColorVariants: next,
                                }));
                                e.target.value = "";
                              }}
                            />
                            <div style={galleryWrap}>
                              {existingMore.map((url) => (
                                <div key={url} style={{ display: "grid", gap: 6 }}>
                                  <img src={assetUrl(url)} alt="" style={thumb} />
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = engravingColorVariants.map(
                                        (variant) =>
                                          variant.color === variantRow.color
                                            ? {
                                                ...variant,
                                                moreImages: (
                                                  variant.moreImages || []
                                                ).filter(
                                                  (imageUrl) => imageUrl !== url
                                                ),
                                              }
                                            : variant
                                      );
                                      setEngraving((prev) => ({
                                        ...prev,
                                        engravingColorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              {newMore.map((file, fileIndex) => {
                                const fileUrl = URL.createObjectURL(file);
                                return (
                                  <div
                                    key={`${variantRow.color}-${file.name}-${fileIndex}`}
                                    style={{ display: "grid", gap: 6 }}
                                  >
                                    <img src={fileUrl} alt="" style={thumb} />
                                    <button
                                      type="button"
                                      style={outlineBtn}
                                      onClick={() => {
                                        const next = engravingColorVariants.map(
                                          (variant) =>
                                            variant.color === variantRow.color
                                              ? {
                                                  ...variant,
                                                  moreImageFiles: (
                                                    variant.moreImageFiles || []
                                                  ).filter(
                                                    (_f, idx) => idx !== fileIndex
                                                  ),
                                                }
                                              : variant
                                        );
                                        setEngraving((prev) => ({
                                          ...prev,
                                          engravingColorVariants: next,
                                        }));
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Add engraving colors first. Then upload front/back/gallery
                      images and matching chain details per color.
                    </div>
                  )}
                </div>
              </div>

              <div
                style={{
                  display: "grid",
                  gap: 14,
                  gridTemplateColumns: "repeat(auto-fit,minmax(320px,1fr))",
                  alignItems: "start",
                }}
              >
                <div
                  style={{
                    border: "1px solid #e5e7eb",
                    borderRadius: 14,
                    padding: 14,
                    display: "grid",
                    gap: 12,
                    background: "#fff",
                  }}
                >
                  <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                    <div>
                      <div style={{ ...sectionTitle, fontSize: 13 }}>Admin Preview</div>
                      <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
                        Uses the same uploaded plate image and saved engraving area as
                        the storefront preview.
                      </div>
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                      <button
                        type="button"
                        style={{
                          ...outlineBtn,
                          background:
                            adminEngravingPreviewSide === "front" ? "#111827" : "#fff",
                          color:
                            adminEngravingPreviewSide === "front" ? "#fff" : "#111827",
                          borderColor:
                            adminEngravingPreviewSide === "front" ? "#111827" : "#d1d5db",
                        }}
                        onClick={() => setAdminEngravingPreviewSide("front")}
                      >
                        Front
                      </button>
                      {syncedEngraving.supportsBackEngraving ? (
                        <button
                          type="button"
                          style={{
                            ...outlineBtn,
                            background:
                              adminEngravingPreviewSide === "back" ? "#111827" : "#fff",
                            color:
                              adminEngravingPreviewSide === "back" ? "#fff" : "#111827",
                            borderColor:
                              adminEngravingPreviewSide === "back" ? "#111827" : "#d1d5db",
                          }}
                          onClick={() => setAdminEngravingPreviewSide("back")}
                        >
                          Back
                        </button>
                      ) : null}
                    </div>
                  </div>

                  <div
                    style={{
                      display: "grid",
                      gap: 12,
                      gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))",
                      alignItems: "start",
                    }}
                  >
                    <div style={{ display: "grid", gap: 10 }}>
                      <div style={row}>
                        <label style={labelCss}>Front preview text</label>
                        <input
                          style={inputCss}
                          value={adminEngravingPreviewFrontText}
                          onChange={(e) => setAdminEngravingPreviewFrontText(e.target.value)}
                          placeholder="Your text"
                        />
                      </div>
                      {syncedEngraving.supportsBackEngraving ? (
                        <div style={row}>
                          <label style={labelCss}>Back preview text</label>
                          <input
                            style={inputCss}
                            value={adminEngravingPreviewBackText}
                            onChange={(e) => setAdminEngravingPreviewBackText(e.target.value)}
                            placeholder="Back text"
                          />
                        </div>
                      ) : null}
                      <div style={{ fontSize: 12, color: "#64748b", lineHeight: 1.5 }}>
                        Area:{" "}
                        {adminEngravingPreviewSide === "back"
                          ? `X ${Number(
                              syncedEngraving.backEngravingArea?.xPct ?? 0
                            ).toFixed(2)}% · Y ${Number(
                              syncedEngraving.backEngravingArea?.yPct ?? 0
                            ).toFixed(2)}% · W ${Number(
                              syncedEngraving.backEngravingArea?.widthPct ?? 0
                            ).toFixed(2)}% · H ${Number(
                              syncedEngraving.backEngravingArea?.heightPct ?? 0
                            ).toFixed(2)}%`
                          : `X ${Number(
                              syncedEngraving.frontEngravingArea?.xPct ?? 0
                            ).toFixed(2)}% · Y ${Number(
                              syncedEngraving.frontEngravingArea?.yPct ?? 0
                            ).toFixed(2)}% · W ${Number(
                              syncedEngraving.frontEngravingArea?.widthPct ?? 0
                            ).toFixed(2)}% · H ${Number(
                              syncedEngraving.frontEngravingArea?.heightPct ?? 0
                            ).toFixed(2)}%`}
                      </div>
                      {activeAdminEngravingLayout ? (
                        <div style={{ fontSize: 12, color: "#1e293b", lineHeight: 1.5 }}>
                          Auto-fit size: {activeAdminEngravingLayout.fittedFontSize.toFixed(1)}px
                          {" • "}
                          Rendered: {activeAdminEngravingLayout.renderedFontSizePx.toFixed(1)}px
                          {" • "}
                          Lines: {activeAdminEngravingLayout.lineCount}
                        </div>
                      ) : null}
                    </div>

                    <EngravingAdminPreviewFace
                      label={
                        adminEngravingPreviewSide === "back"
                          ? "Back engraving preview"
                          : "Front engraving preview"
                      }
                      imageUrl={
                        adminEngravingPreviewSide === "back"
                          ? getEngravingAreaPreviewImage(
                              "back",
                              engravingColorVariants
                            ) || backSrc
                          : getEngravingAreaPreviewImage(
                              "front",
                              engravingColorVariants
                            ) || previewSrc
                      }
                      area={
                        adminEngravingPreviewSide === "back"
                          ? syncedEngraving.backEngravingArea
                          : syncedEngraving.frontEngravingArea
                      }
                      text={
                        adminEngravingPreviewSide === "back"
                          ? adminEngravingPreviewBackText
                          : adminEngravingPreviewFrontText
                      }
                      fontKey={String(syncedEngraving.defaultFont || "serif")}
                      fontSize={syncedEngraving.defaultFontSize ?? 18}
                      onSideLayout={(layout) =>
                        setAdminEngravingPreviewLayoutBySide((prev) =>
                          prev[layout.side] &&
                          prev[layout.side]!.renderedFontSizePx ===
                            layout.renderedFontSizePx &&
                          prev[layout.side]!.fittedFontSize === layout.fittedFontSize &&
                          prev[layout.side]!.lineCount === layout.lineCount
                            ? prev
                            : { ...prev, [layout.side]: layout }
                        )
                      }
                    />
                  </div>
                </div>

                <div style={{ display: "grid", gap: 10 }}>
                  <div style={sectionTitle}>Side Limits and Pricing</div>
                  <div style={switchRow}>
                    <input
                    type="checkbox"
                    checked={!!syncedEngraving.supportsBackEngraving}
                    onChange={(e) =>
                      setEngraving((prev) => ({
                        ...prev,
                        supportsBackEngraving: e.target.checked,
                        backEngravingArea: e.target.checked
                          ? sanitizeEngravingArea(prev.backEngravingArea, "back")
                          : undefined,
                      }))
                    }
                  />
                  <span>Enable back-side engraving</span>
                </div>

                <div style={grid2}>
                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 10,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13 }}>Front side</div>
                    <div style={grid2}>
                      <div style={row}>
                        <label style={labelCss}>Text limit</label>
                        <input
                          style={inputCss}
                          type="number"
                          min={0}
                          step={1}
                          value={syncedEngraving.frontTextLimit ?? 20}
                          onChange={(e) =>
                            setEngraving((prev) => ({
                              ...prev,
                              frontTextLimit: Math.max(
                                0,
                                Math.round(Number(e.target.value || 0))
                              ),
                            }))
                          }
                        />
                      </div>
                      <div style={row}>
                        <label style={labelCss}>Max lines</label>
                        <input
                          style={inputCss}
                          type="number"
                          min={1}
                          step={1}
                          value={syncedEngraving.frontMaxLines ?? 2}
                          onChange={(e) =>
                            setEngraving((prev) => ({
                              ...prev,
                              frontMaxLines: Math.max(
                                1,
                                Math.round(Number(e.target.value || 1))
                              ),
                            }))
                          }
                        />
                      </div>
                    </div>
                    {renderAreaEditor("front", syncedEngraving.frontEngravingArea)}
                  </div>

                  <div
                    style={{
                      border: "1px solid #e5e7eb",
                      borderRadius: 10,
                      padding: 10,
                      display: "grid",
                      gap: 10,
                    }}
                  >
                    <div style={{ fontWeight: 700, fontSize: 13 }}>Back side</div>
                    <div style={grid2}>
                      <div style={row}>
                        <label style={labelCss}>Text limit</label>
                        <input
                          style={inputCss}
                          type="number"
                          min={0}
                          step={1}
                          value={syncedEngraving.backTextLimit ?? 20}
                          disabled={!syncedEngraving.supportsBackEngraving}
                          onChange={(e) =>
                            setEngraving((prev) => ({
                              ...prev,
                              backTextLimit: Math.max(
                                0,
                                Math.round(Number(e.target.value || 0))
                              ),
                            }))
                          }
                        />
                      </div>
                      <div style={row}>
                        <label style={labelCss}>Max lines</label>
                        <input
                          style={inputCss}
                          type="number"
                          min={1}
                          step={1}
                          value={syncedEngraving.backMaxLines ?? 2}
                          disabled={!syncedEngraving.supportsBackEngraving}
                          onChange={(e) =>
                            setEngraving((prev) => ({
                              ...prev,
                              backMaxLines: Math.max(
                                1,
                                Math.round(Number(e.target.value || 1))
                              ),
                            }))
                          }
                        />
                      </div>
                    </div>
                    <div style={row}>
                      <label style={labelCss}>Back engraving extra cost</label>
                      <input
                        style={inputCss}
                        type="number"
                        min={0}
                        step="0.01"
                        value={syncedEngraving.backEngravingExtraCost ?? 0}
                        disabled={!syncedEngraving.supportsBackEngraving}
                        onChange={(e) =>
                          setEngraving((prev) => ({
                            ...prev,
                            backEngravingExtraCost: Math.max(
                              0,
                              Number(e.target.value || 0)
                            ),
                          }))
                        }
                      />
                    </div>
                    {renderAreaEditor("back", syncedEngraving.backEngravingArea)}
                  </div>
                </div>
              </div>

              <div style={{ display: "grid", gap: 10 }}>
                <div style={sectionTitle}>Fonts and Symbols</div>
                <div
                  style={{
                    display: "grid",
                    gap: 10,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  <div style={row}>
                    <label style={labelCss}>Allowed fonts</label>
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                      }}
                    >
                      {DEFAULT_ENGRAVING_FONTS.map((fontKey) => {
                        const active = (syncedEngraving.allowedFonts || []).includes(
                          fontKey
                        );
                        return (
                          <label
                            key={fontKey}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: 6,
                              border: "1px solid #d1d5db",
                              borderRadius: 999,
                              padding: "6px 10px",
                              background: active ? "#eef2ff" : "#fff",
                              fontSize: 13,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={active}
                              onChange={(e) =>
                                setEngraving((prev) => {
                                  const currentFonts = Array.isArray(prev.allowedFonts)
                                    ? prev.allowedFonts
                                    : DEFAULT_ENGRAVING_FONTS;
                                  const nextFonts = e.target.checked
                                    ? Array.from(new Set([...currentFonts, fontKey]))
                                    : currentFonts.filter((font) => font !== fontKey);
                                  return {
                                    ...prev,
                                    allowedFonts: nextFonts.length
                                      ? nextFonts
                                      : [DEFAULT_ENGRAVING_FONTS[0]],
                                    defaultFont: nextFonts.includes(
                                      String(prev.defaultFont || "")
                                    )
                                      ? prev.defaultFont
                                      : nextFonts[0] || DEFAULT_ENGRAVING_FONTS[0],
                                  };
                                })
                              }
                            />
                            <span>{fontKey}</span>
                          </label>
                        );
                      })}
                    </div>
                  </div>

                  <div style={grid2}>
                    <div style={row}>
                      <label style={labelCss}>Default font</label>
                      <select
                        style={selectCss}
                        value={String(syncedEngraving.defaultFont || "serif")}
                        onChange={(e) =>
                          setEngraving((prev) => ({
                            ...prev,
                            defaultFont: e.target.value || "serif",
                          }))
                        }
                      >
                        {(syncedEngraving.allowedFonts || DEFAULT_ENGRAVING_FONTS).map(
                          (fontKey) => (
                            <option key={fontKey} value={fontKey}>
                              {fontKey}
                            </option>
                          )
                        )}
                      </select>
                    </div>
                    <div style={row}>
                      <label style={labelCss}>Emoji / symbol mode</label>
                      <select
                        style={selectCss}
                        value={syncedEngraving.emojiSupportMode ?? "controlled"}
                        onChange={(e) =>
                          setEngraving((prev) => ({
                            ...prev,
                            emojiSupportMode:
                              e.target.value === "unicode" ? "unicode" : "controlled",
                          }))
                        }
                      >
                        <option value="controlled">
                          Controlled symbols only
                        </option>
                        <option value="unicode">
                          Broader unicode support
                        </option>
                      </select>
                    </div>
                  </div>

                  <div style={row}>
                    <label style={labelCss}>
                      Allowed symbols (comma separated)
                    </label>
                    <input
                      style={inputCss}
                      value={(syncedEngraving.allowedSymbols || []).join(", ")}
                      onChange={(e) =>
                        setEngraving((prev) => ({
                          ...prev,
                          allowedSymbols: parseTextCsv(e.target.value),
                        }))
                      }
                      placeholder="❤️, ♥, ★, ∞"
                    />
                  </div>

                  <div style={{ fontSize: 12, color: "#6b7280" }}>
                    Storefront font color is fixed by the engraving preview system.
                    Customers can choose text and font only.
                  </div>
                </div>
              </div>

              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: 10,
                  background: "#f8fafc",
                  fontSize: 12,
                  color: "#475569",
                  lineHeight: 1.6,
                }}
              >
                SEO copy tip: this structure supports product-specific titles and
                descriptions such as <strong>custom engraved necklace</strong>,{" "}
                <strong>personalized bracelet</strong>, and{" "}
                <strong>double sided engraving dog tag</strong> without hardcoding
                keywords into the UI.
              </div>
            </div>
            </div>
          );
        }

      case ProductCategory.Cup:
        return (
          <div style={card}>
            <div style={sectionTitle}>Cup</div>
            <div style={grid3}>
              <div style={row}>
                <label style={labelCss}>Size</label>
                <select
                  style={selectCss}
                  value={cup.size ?? ""}
                  onChange={(e) =>
                    setCup((s) => ({
                      ...s,
                      size: e.target.value || undefined,
                    }))
                  }
                >
                  <option value="">—</option>
                  {["11oz", "15oz", "20oz"].map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
        );

      case ProductCategory.LicensePlate:
        return (
          <div style={card}>
            <div style={sectionTitle}>License plate</div>
            <div style={grid2}>
              <div style={row}>
                <label style={labelCss}>Plate Type</label>
                <select
                  style={selectCss}
                  value={plate.plateType ?? ""}
                  onChange={(e) =>
                    setPlate((s) => ({
                      ...s,
                      plateType: (e.target.value ||
                        undefined) as LicensePlateFields["plateType"],
                    }))
                  }
                >
                  <option value="standard_car_12x6">Standard Car (12x6)</option>
                  <option value="motorcycle">Motorcycle</option>
                </select>
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  Customer can still fully customize image/text during checkout.
                </div>
              </div>
              <div style={row}>
                <label style={labelCss}>Material</label>
                <input
                  style={inputCss}
                  value={material}
                  onChange={(e) => setMaterial(e.target.value)}
                  placeholder="e.g. Aluminum"
                />
              </div>
            </div>
          </div>
        );

      case ProductCategory.Wallet:
        return (
          <div style={card}>
            <div style={sectionTitle}>Wallet</div>
            <div style={grid3}>
              <div style={row}>
                <label style={labelCss}>Slots</label>
                <input
                  style={inputCss}
                  type="number"
                  value={wallet.slots ?? ""}
                  onChange={(e) =>
                    setWallet((s) => ({ ...s, slots: num(e.target.value) }))
                  }
                />
              </div>
              <div style={switchRow}>
                <input
                  type="checkbox"
                  checked={!!wallet.hasCoinPocket}
                  onChange={(e) =>
                    setWallet((s) => ({
                      ...s,
                      hasCoinPocket: e.target.checked,
                    }))
                  }
                />
                <span>Coin pocket</span>
              </div>
              <div style={switchRow}>
                <input
                  type="checkbox"
                  checked={!!wallet.photoEmbedAllowed}
                  onChange={(e) =>
                    setWallet((s) => ({
                      ...s,
                      photoEmbedAllowed: e.target.checked,
                    }))
                  }
                />
                <span>Photo embed allowed</span>
              </div>
            </div>
          </div>
        );

      case ProductCategory.PhoneCover:
        return (
          <div style={card}>
            <div style={sectionTitle}>Phone cover</div>
            <div style={grid3}>
              <div style={row}>
                <label style={labelCss}>Phone model</label>
                <input
                  style={inputCss}
                  value={cover.phoneModel ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({ ...s, phoneModel: e.target.value }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Case size label</label>
                <input
                  style={inputCss}
                  placeholder='e.g. 6.3"'
                  value={cover.caseSize ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({ ...s, caseSize: e.target.value }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Finish</label>
                <select
                  style={selectCss}
                  value={cover.finish ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      finish: (e.target.value ||
                        undefined) as PhoneCoverFields["finish"],
                    }))
                  }
                >
                  <option value="">—</option>
                  {["Matte", "Glossy", "SoftTouch"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div style={switchRow}>
                <input
                  type="checkbox"
                  checked={!!cover.photoUploadAllowed}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      photoUploadAllowed: e.target.checked,
                    }))
                  }
                />
                <span>Photo upload allowed</span>
              </div>
            </div>
            <div style={sectionTitle}>Camera Cutout (% of case)</div>
            <div style={grid3}>
              <div style={row}>
                <label style={labelCss}>X (%)</label>
                <input
                  style={inputCss}
                  type="number"
                  value={cover.cameraCutout?.xPct ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      cameraCutout: {
                        ...(s.cameraCutout ?? {}),
                        xPct: num(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Y (%)</label>
                <input
                  style={inputCss}
                  type="number"
                  value={cover.cameraCutout?.yPct ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      cameraCutout: {
                        ...(s.cameraCutout ?? {}),
                        yPct: num(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Width (%)</label>
                <input
                  style={inputCss}
                  type="number"
                  value={cover.cameraCutout?.widthPct ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      cameraCutout: {
                        ...(s.cameraCutout ?? {}),
                        widthPct: num(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Height (%)</label>
                <input
                  style={inputCss}
                  type="number"
                  value={cover.cameraCutout?.heightPct ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      cameraCutout: {
                        ...(s.cameraCutout ?? {}),
                        heightPct: num(e.target.value),
                      },
                    }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Corner Radius (%)</label>
                <input
                  style={inputCss}
                  type="number"
                  value={cover.cameraCutout?.cornerRadiusPct ?? ""}
                  onChange={(e) =>
                    setCover((s) => ({
                      ...s,
                      cameraCutout: {
                        ...(s.cameraCutout ?? {}),
                        cornerRadiusPct: num(e.target.value),
                      },
                    }))
                  }
                />
              </div>
            </div>
          </div>
        );

      case ProductCategory.Tshirt:
        {
        const parsedTshirtColors = parseTags(tshirtColorsStr);
        const parsedTshirtSizes = parseSizeTags(tshirtSizesStr);
        const tshirtSizeStockByColor = syncSizeStockByColorWithColorAndSize(
          tee.sizeStockByColor,
          parsedTshirtColors,
          parsedTshirtSizes
        );
        const tshirtColorVariants = syncColorVariantsWithColors(
          sanitizeTshirtColorVariants(tee.colorVariants),
          parsedTshirtColors
        );
        return (
          <div style={card}>
            <div style={sectionTitle}>T‑shirt</div>
            <div style={grid3}>
              <div style={row}>
                <label style={labelCss}>Size</label>
                <select
                  style={selectCss}
                  value={tee.size ?? ""}
                  onChange={(e) =>
                    setTee((s) => ({
                      ...s,
                      size: (e.target.value ||
                        undefined) as TshirtFields["size"],
                    }))
                  }
                >
                  <option value="">—</option>
                  {["XS", "S", "M", "L", "XL", "XXL"].map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div style={row}>
                <label style={labelCss}>Type</label>
                <select
                  style={selectCss}
                  value={tee.tshirtType ?? ""}
                  onChange={(e) =>
                    setTee((s) => ({
                      ...s,
                      tshirtType: (e.target.value ||
                        undefined) as TshirtFields["tshirtType"],
                    }))
                  }
                >
                  <option value="Round">Round</option>
                  <option value="V">V Neck</option>
                </select>
              </div>
              <div style={row}>
                <label style={labelCss}>Material</label>
                <input
                  style={inputCss}
                  value={material}
                  onChange={(e) => setMaterial(e.target.value)}
                  placeholder="e.g. 100% Cotton"
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Back customization extra cost</label>
                <input
                  style={inputCss}
                  type="number"
                  min={0}
                  step="0.01"
                  value={tee.backCustomizationExtraCost ?? ""}
                  placeholder="0.00"
                  onChange={(e) =>
                    setTee((s) => ({
                      ...s,
                      backCustomizationExtraCost: num(e.target.value),
                    }))
                  }
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Available colors (comma separated)</label>
                <input
                  style={inputCss}
                  value={tshirtColorsStr}
                  onChange={(e) => setTshirtColorsStr(e.target.value)}
                  placeholder="#FFFFFF, #FF0000, White, Black"
                  onBlur={() => {
                    const parsed = parseTags(tshirtColorsStr);
                    setTee((prev) => ({
                      ...prev,
                      availableColors: parsed,
                      sizeStockByColor: syncSizeStockByColorWithColorAndSize(
                        prev.sizeStockByColor,
                        parsed,
                        parseSizeTags(tshirtSizesStr)
                      ),
                      colorVariants: syncColorVariantsWithColors(
                        sanitizeTshirtColorVariants(prev.colorVariants),
                        parsed
                      ),
                    }));
                    if (parsed.length && !parsed.includes(color)) {
                      setColor(parsed[0]);
                    }
                  }}
                />
              </div>
              <div style={row}>
                <label style={labelCss}>Available sizes (comma separated)</label>
                <input
                  style={inputCss}
                  value={tshirtSizesStr}
                  onChange={(e) => setTshirtSizesStr(e.target.value)}
                  placeholder="XS, S, M, L, XL, XXL"
                  onBlur={() => {
                    const parsed = parseSizeTags(tshirtSizesStr);
                    setTee((prev) => ({
                      ...prev,
                      availableSizes: parsed,
                      sizeStockByColor: syncSizeStockByColorWithColorAndSize(
                        prev.sizeStockByColor,
                        parseTags(tshirtColorsStr),
                        parsed
                      ),
                    }));
                  }}
                />
              </div>
              <div style={{ ...row, gridColumn: "1 / -1" }}>
                <label style={labelCss}>Stock by color and size</label>
                <div
                  style={{
                    display: "grid",
                    gap: 8,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                    overflowX: "auto",
                  }}
                >
                  {tshirtSizeStockByColor.length && parsedTshirtSizes.length ? (
                    <table
                      style={{
                        borderCollapse: "collapse",
                        minWidth: Math.max(480, 180 + parsedTshirtSizes.length * 120),
                      }}
                    >
                      <thead>
                        <tr>
                          <th
                            style={{
                              border: "1px solid #e5e7eb",
                              background: "#f8fafc",
                              padding: "8px 10px",
                              textAlign: "left",
                              fontSize: 12,
                            }}
                          >
                            Color
                          </th>
                          {parsedTshirtSizes.map((size) => (
                            <th
                              key={size}
                              style={{
                                border: "1px solid #e5e7eb",
                                background: "#f8fafc",
                                padding: "8px 10px",
                                textAlign: "center",
                                fontSize: 12,
                              }}
                            >
                              {size}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {tshirtSizeStockByColor.map((rowItem) => (
                          <tr key={rowItem.color}>
                            <td
                              style={{
                                border: "1px solid #e5e7eb",
                                padding: "8px 10px",
                                fontWeight: 600,
                                fontSize: 13,
                                minWidth: 150,
                              }}
                            >
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                {isHexColor(rowItem.color) ? (
                                  <span
                                    style={{
                                      width: 16,
                                      height: 16,
                                      borderRadius: "999px",
                                      border: "1px solid #cbd5e1",
                                      background: rowItem.color,
                                      display: "inline-block",
                                    }}
                                  />
                                ) : null}
                                <span>{rowItem.color}</span>
                              </div>
                            </td>
                            {parsedTshirtSizes.map((size) => {
                              const currentStock =
                                (rowItem.sizes || []).find(
                                  (sizeRow) =>
                                    String(sizeRow.size || "").trim().toUpperCase() === size
                                )?.stockCount ?? 0;
                              return (
                                <td
                                  key={`${rowItem.color}-${size}`}
                                  style={{
                                    border: "1px solid #e5e7eb",
                                    padding: 6,
                                    minWidth: 110,
                                  }}
                                >
                                  <input
                                    style={{ ...inputCss, width: "100%", textAlign: "center" }}
                                    type="number"
                                    min={0}
                                    step={1}
                                    value={currentStock}
                                    onChange={(e) => {
                                      const next = tshirtSizeStockByColor.map((stockRow) =>
                                        stockRow.color === rowItem.color
                                          ? {
                                              ...stockRow,
                                              sizes: parsedTshirtSizes.map((matrixSize) => ({
                                                size: matrixSize,
                                                stockCount:
                                                  matrixSize === size
                                                    ? Math.max(
                                                        0,
                                                        Math.round(Number(e.target.value || 0))
                                                      )
                                                    : (stockRow.sizes || []).find(
                                                        (sizeRow) =>
                                                          String(sizeRow.size || "")
                                                            .trim()
                                                            .toUpperCase() === matrixSize
                                                      )?.stockCount ?? 0,
                                              })),
                                            }
                                          : stockRow
                                      );
                                      setTee((prev) => ({
                                        ...prev,
                                        sizeStockByColor: next,
                                      }));
                                    }}
                                  />
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Add colors and sizes first. Then set stock for each size of each
                      color.
                    </div>
                  )}
                </div>
              </div>
              <div style={{ ...row, gridColumn: "1 / -1" }}>
                <label style={labelCss}>
                  Color templates (front, back, and gallery images per color)
                </label>
                <div
                  style={{
                    display: "grid",
                    gap: 12,
                    border: "1px solid #e5e7eb",
                    borderRadius: 10,
                    padding: 10,
                  }}
                >
                  {tshirtColorVariants.length ? (
                    tshirtColorVariants.map((variantRow) => {
                      const frontPreview = variantRow.frontImageFile
                        ? URL.createObjectURL(variantRow.frontImageFile)
                        : variantRow.frontImage
                        ? assetUrl(variantRow.frontImage)
                        : "";
                      const backPreview = variantRow.backImageFile
                        ? URL.createObjectURL(variantRow.backImageFile)
                        : variantRow.backImage
                        ? assetUrl(variantRow.backImage)
                        : "";
                      const existingMore = Array.isArray(variantRow.moreImages)
                        ? variantRow.moreImages
                        : [];
                      const newMore = Array.isArray(variantRow.moreImageFiles)
                        ? variantRow.moreImageFiles
                        : [];

                      return (
                        <div
                          key={variantRow.color}
                          style={{
                            border: "1px solid #e5e7eb",
                            borderRadius: 10,
                            padding: 10,
                            display: "grid",
                            gap: 10,
                          }}
                        >
                          <div style={{ fontWeight: 700, fontSize: 13 }}>
                            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              {isHexColor(variantRow.color) ? (
                                <span
                                  style={{
                                    width: 16,
                                    height: 16,
                                    borderRadius: "999px",
                                    border: "1px solid #cbd5e1",
                                    background: variantRow.color,
                                    display: "inline-block",
                                  }}
                                />
                              ) : null}
                              <span>{variantRow.color}</span>
                            </div>
                          </div>
                          <div style={grid2}>
                            <div style={row}>
                              <label style={labelCss}>Front image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  const next = tshirtColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          frontImageFile: file,
                                        }
                                      : variant
                                  );
                                  setTee((prev) => ({ ...prev, colorVariants: next }));
                                }}
                              />
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                {frontPreview ? (
                                  <img src={frontPreview} alt="" style={thumb} />
                                ) : (
                                  <div
                                    style={{
                                      ...thumb,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    —
                                  </div>
                                )}
                                {(variantRow.frontImage || variantRow.frontImageFile) ? (
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = tshirtColorVariants.map((variant) =>
                                        variant.color === variantRow.color
                                          ? {
                                              ...variant,
                                              frontImage: undefined,
                                              frontImageFile: null,
                                            }
                                          : variant
                                      );
                                      setTee((prev) => ({
                                        ...prev,
                                        colorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </div>
                            </div>

                            <div style={row}>
                              <label style={labelCss}>Back image</label>
                              <input
                                type="file"
                                accept="image/*"
                                onChange={(e) => {
                                  const file = e.target.files?.[0] || null;
                                  const next = tshirtColorVariants.map((variant) =>
                                    variant.color === variantRow.color
                                      ? {
                                          ...variant,
                                          backImageFile: file,
                                        }
                                      : variant
                                  );
                                  setTee((prev) => ({ ...prev, colorVariants: next }));
                                }}
                              />
                              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                                {backPreview ? (
                                  <img src={backPreview} alt="" style={thumb} />
                                ) : (
                                  <div
                                    style={{
                                      ...thumb,
                                      display: "grid",
                                      placeItems: "center",
                                      color: "#bbb",
                                    }}
                                  >
                                    —
                                  </div>
                                )}
                                {(variantRow.backImage || variantRow.backImageFile) ? (
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = tshirtColorVariants.map((variant) =>
                                        variant.color === variantRow.color
                                          ? {
                                              ...variant,
                                              backImage: undefined,
                                              backImageFile: null,
                                            }
                                          : variant
                                      );
                                      setTee((prev) => ({
                                        ...prev,
                                        colorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                ) : null}
                              </div>
                            </div>
                          </div>

                          <div style={row}>
                            <label style={labelCss}>Other images</label>
                            <input
                              type="file"
                              multiple
                              accept="image/*"
                              onChange={(e) => {
                                const files = Array.from(e.target.files || []);
                                if (!files.length) return;
                                const next = tshirtColorVariants.map((variant) =>
                                  variant.color === variantRow.color
                                    ? {
                                        ...variant,
                                        moreImageFiles: [
                                          ...(variant.moreImageFiles || []),
                                          ...files,
                                        ],
                                      }
                                    : variant
                                );
                                setTee((prev) => ({ ...prev, colorVariants: next }));
                                e.target.value = "";
                              }}
                            />
                            <div style={galleryWrap}>
                              {existingMore.map((url) => (
                                <div key={url} style={{ display: "grid", gap: 6 }}>
                                  <img src={assetUrl(url)} alt="" style={thumb} />
                                  <button
                                    type="button"
                                    style={outlineBtn}
                                    onClick={() => {
                                      const next = tshirtColorVariants.map((variant) =>
                                        variant.color === variantRow.color
                                          ? {
                                              ...variant,
                                              moreImages: (variant.moreImages || []).filter(
                                                (imageUrl) => imageUrl !== url
                                              ),
                                            }
                                          : variant
                                      );
                                      setTee((prev) => ({
                                        ...prev,
                                        colorVariants: next,
                                      }));
                                    }}
                                  >
                                    Remove
                                  </button>
                                </div>
                              ))}
                              {newMore.map((file, fileIndex) => {
                                const fileUrl = URL.createObjectURL(file);
                                return (
                                  <div
                                    key={`${variantRow.color}-${file.name}-${fileIndex}`}
                                    style={{ display: "grid", gap: 6 }}
                                  >
                                    <img src={fileUrl} alt="" style={thumb} />
                                    <button
                                      type="button"
                                      style={outlineBtn}
                                      onClick={() => {
                                        const next = tshirtColorVariants.map((variant) =>
                                          variant.color === variantRow.color
                                            ? {
                                                ...variant,
                                                moreImageFiles: (variant.moreImageFiles || []).filter(
                                                  (_f, idx) => idx !== fileIndex
                                                ),
                                              }
                                            : variant
                                        );
                                        setTee((prev) => ({
                                          ...prev,
                                          colorVariants: next,
                                        }));
                                      }}
                                    >
                                      Remove
                                    </button>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        </div>
                      );
                    })
                  ) : (
                    <div style={{ fontSize: 12, color: "#6b7280" }}>
                      Add colors first. Templates are uploaded per color and reused for all
                      sizes.
                    </div>
                  )}
                </div>
              </div>
              <div style={row}>
                <label style={labelCss}>Default color</label>
                <div style={{ display: "grid", gap: 8 }}>
                  <input
                    style={inputCss}
                    value={color}
                    onChange={(e) => setColor(e.target.value)}
                    placeholder="#FFFFFF or White"
                    list="tshirt-color-options"
                  />
                  <datalist id="tshirt-color-options">
                    {parsedTshirtColors.map((option) => (
                      <option key={option} value={option} />
                    ))}
                  </datalist>
                </div>
              </div>
              <div style={switchRow}>
                <input
                  type="checkbox"
                  checked={!!tee.photoUploadAllowed}
                  onChange={(e) =>
                    setTee((s) => ({
                      ...s,
                      photoUploadAllowed: e.target.checked,
                    }))
                  }
                />
                <span>Photo upload allowed</span>
              </div>
            </div>
          </div>
        );
        }

      case ProductCategory.Gift:
        return (
          <div style={card}>
            <div style={sectionTitle}>Gift Item (Non-customizable)</div>
            <div style={grid2}>
              <div style={row}>
                <label style={labelCss}>Gift Type</label>
                <input
                  style={inputCss}
                  value={gift.giftType ?? ""}
                  onChange={(e) =>
                    setGift((s) => ({ ...s, giftType: e.target.value || undefined }))
                  }
                  placeholder="Perfume, Dream Catcher, Toy, etc."
                />
              </div>
              <div style={switchRow}>
                <input type="checkbox" checked={isCustomizable} onChange={(e) => setIsCustomizable(e.target.checked)} />
                <span>Customizable</span>
              </div>
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Plot category on load for edit pages if missing
  useEffect(() => {
    if (!category && initial?.category) {
      setCategory(initial.category);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initial?._id]);

  const [tagsStr, setTagsStr] = useState((initial?.tags ?? []).join(", "));
  const parseTags = (s: string): string[] =>
    s
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);
  const parseSizeTags = (s: string): string[] =>
    s
      .split(",")
      .map((value) => value.trim().toUpperCase())
      .filter(Boolean);
  const pendantDerivedStockCount =
    category === ProductCategory.Pendant
      ? derivePendantTotalStock(pendant.colorStocks)
      : stockCount;
  const engravingDerivedStockCount =
    category === ProductCategory.Engraving
      ? derivePendantTotalStock(engraving.colorStocks)
      : stockCount;
  const showBaseMaterial =
    category === ProductCategory.Engraving ||
    category === ProductCategory.Tshirt ||
    category === ProductCategory.LicensePlate ||
    category === ProductCategory.Gift;
  const showDimensions = category === ProductCategory.PhoneCover;

  return (
    <form
      onSubmit={onSubmit}
      onFocusCapture={handleNumberFieldFocusCapture}
      style={{ display: "grid", gap: 16 }}
    >
      {/* Basics */}
      <div style={card}>
        <div style={sectionTitle}>Basics</div>
        <div style={grid2}>
          <div style={row}>
            <label style={labelCss}>Category *</label>
            <select
              required
              style={selectCss}
              value={category}
              onChange={(e) => {
                const next = (e.target.value as ProductCategory) || "";
                setCategory(next);
                if (next === ProductCategory.Gift) setIsCustomizable(false);
              }}
            >
              <option value="">Select category</option>
              {Object.values(ProductCategory).map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
          <div style={row}>
            <label style={labelCss}>Name *</label>
            <input
              required
              style={inputCss}
              value={name}
              onChange={(e) => setName(e.target.value)}
            />
          </div>
          <div style={row}>
            <label style={labelCss}>Slug *</label>
            <input
              required
              style={inputCss}
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
            />
          </div>
          <div style={row}>
            <label style={labelCss}>SKU</label>
            <input
              style={inputCss}
              value={sku}
              onChange={(e) => setSku(e.target.value)}
            />
          </div>
        </div>

        <div style={row}>
          <label style={labelCss}>Description</label>
          <textarea
            style={{ ...inputCss, minHeight: 80 }}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>

        <div style={row}>
          <label style={labelCss}>Jewelry Care HTML</label>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              gap: 8,
              alignItems: "center",
            }}
          >
            <button
              type="button"
              style={toolbarBtn}
              onClick={() =>
                wrapJewelryCareSelection("<strong>", "</strong>", "Bold text")
              }
            >
              Bold
            </button>
            <button
              type="button"
              style={toolbarBtn}
              onClick={() =>
                wrapJewelryCareSelection("<em>", "</em>", "Italic text")
              }
            >
              Italic
            </button>
            <button
              type="button"
              style={toolbarBtn}
              onClick={insertJewelryCareBulletList}
            >
              Bullets
            </button>
            <label
              style={{
                ...helperTextCss,
                display: "inline-flex",
                alignItems: "center",
                gap: 6,
              }}
            >
              Text color
              <input
                type="color"
                value={jewelryCareTextColor}
                style={colorInputCss}
                onChange={(e) => setJewelryCareTextColor(e.target.value)}
              />
            </label>
            <button
              type="button"
              style={toolbarBtn}
              onClick={() =>
                wrapJewelryCareSelection(
                  `<span style="color: ${jewelryCareTextColor};">`,
                  "</span>",
                  "Colored text"
                )
              }
            >
              Apply Color
            </button>
          </div>
          <textarea
            ref={jewelryCareHtmlInputRef}
            style={{ ...inputCss, minHeight: 100, fontFamily: "monospace" }}
            placeholder="<p>Store in a dry place. Clean with a soft cloth.</p>"
            value={jewelryCareHtml}
            onChange={(e) => setJewelryCareHtml(e.target.value)}
          />
          <div style={helperTextCss}>
            Optional product-specific care box shown on the storefront next to the
            product details. The buttons above insert HTML for bold, italics,
            bullet lists, and colored text.
          </div>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))",
            gap: 12,
          }}
        >
          {showBaseMaterial ? (
            <div style={row}>
              <label style={labelCss}>Material</label>
              <input
                style={inputCss}
                value={material}
                onChange={(e) => setMaterial(e.target.value)}
              />
            </div>
          ) : null}
          {category === ProductCategory.Pendant ? (
            <div style={row}>
              <label style={labelCss}>Pendant Inventory</label>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: "#f8fafc",
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                <div>
                  Default color:{" "}
                  <strong>{parseTextCsv(pendantColorsStr)[0] || "Not set yet"}</strong>
                </div>
                <div>
                  Total stock from color rows: <strong>{pendantDerivedStockCount}</strong>
                </div>
                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>
                  Pendant color and stock are controlled in the pendant section below.
                </div>
              </div>
            </div>
          ) : category === ProductCategory.Engraving ? (
            <div style={row}>
              <label style={labelCss}>Engraving Inventory</label>
              <div
                style={{
                  border: "1px solid #e5e7eb",
                  borderRadius: 10,
                  padding: "10px 12px",
                  background: "#f8fafc",
                  fontSize: 13,
                  lineHeight: 1.45,
                }}
              >
                <div>
                  Default color:{" "}
                  <strong>{parseTextCsv(engravingColorsStr)[0] || "Not set yet"}</strong>
                </div>
                <div>
                  Total stock from color rows:{" "}
                  <strong>{engravingDerivedStockCount}</strong>
                </div>
                <div style={{ marginTop: 4, color: "#64748b", fontSize: 12 }}>
                  Color-specific stock and pricing are controlled in the engraving
                  section below.
                </div>
              </div>
            </div>
          ) : (
            <div style={row}>
              <label style={labelCss}>Stock</label>
              <input
                style={inputCss}
                type="number"
                value={stockCount}
                onChange={(e) => setStockCount(Number(e.target.value || 0))}
              />
            </div>
          )}
        </div>

        <div style={grid3}>
          <div style={row}>
            <label style={labelCss}>Price *</label>
            <input
              style={inputCss}
              type="number"
              min={0}
              step="0.01"
              value={price ?? ""}
              onChange={(e) => updatePrice(e.target.value)}
            />
          </div>
          <div style={row}>
            <label style={labelCss}>Compare at</label>
            <input
              style={inputCss}
              type="number"
              min={0}
              step="0.01"
              value={compareAtPrice ?? ""}
              onChange={(e) => updateCompare(e.target.value)}
            />
          </div>
          <div style={row}>
            <label style={labelCss}>Discount %</label>
            <input
              style={inputCss}
              type="number"
              min={0}
              max={100}
              step="0.01"
              value={offerPercent ?? ""}
              onChange={(e) => updateDiscount(e.target.value)}
            />
          </div>
        </div>

        <div style={grid3}>
          <div style={switchRow}>
            <input
              type="checkbox"
              checked={isCustomizable}
              onChange={(e) => setIsCustomizable(e.target.checked)}
              disabled={category === ProductCategory.Gift}
            />
            <span>Customizable</span>
          </div>
          <div style={switchRow}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            <span>Active</span>
          </div>
          <div style={switchRow}>
            <input
              type="checkbox"
              checked={featured}
              onChange={(e) => setFeatured(e.target.checked)}
            />
            <span>Featured</span>
          </div>
          <div style={switchRow}>
            <input
              type="checkbox"
              checked={topSeller}
              onChange={(e) => setTopSeller(e.target.checked)}
            />
            <span>Top seller</span>
          </div>
          <div style={switchRow}>
            <input
              type="checkbox"
              checked={giftWrapEnabled}
              onChange={(e) => setGiftWrapEnabled(e.target.checked)}
            />
            <span>Allow gift option</span>
          </div>
          <div style={row}>
            <label style={labelCss}>Tags (comma separated)</label>
            <input
              style={inputCss}
              type="text"
              placeholder="tag1, tag2, tag3"
              value={tagsStr}
              onChange={(e) => setTagsStr(e.target.value)}
            />
          </div>
        </div>
      </div>

      <div style={card}>
        <div style={sectionTitle}>SEO & Product URL</div>
        <div style={grid2}>
          <div style={row}>
            <label style={labelCss}>SEO Title</label>
            <input
              style={inputCss}
              maxLength={MAX_SEO_TITLE_LENGTH}
              placeholder="2 Inch Stainless Steel Circle Pendant Necklace - Gold & Silver"
              value={seoTitle}
              onChange={(e) =>
                setSeoTitle(
                  normalizeSeoTextInput(e.target.value, MAX_SEO_TITLE_LENGTH)
                )
              }
            />
            <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
              Used for Google search results and the page title. Falls back to the
              product name when empty.
            </div>
          </div>
          <div style={row}>
            <label style={labelCss}>Custom Product URL</label>
            <input
              style={inputCss}
              maxLength={MAX_SEO_SLUG_LENGTH}
              placeholder="2-inch-circle-pendant-necklace"
              value={seoSlug}
              onChange={(e) => setSeoSlug(e.target.value)}
              onBlur={() => setSeoSlug(normalizeSeoSlugInput(seoSlug))}
            />
            <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
              Optional custom URL slug for this product. Leave blank to use the
              main catalog slug.
            </div>
            <div style={{ color: "#0f172a", fontSize: 12, lineHeight: 1.5 }}>
              Canonical URL:{" "}
              <strong>/products/{normalizeSeoSlugInput(seoSlug) || slug || "your-product"}</strong>
            </div>
          </div>
        </div>
        <div style={row}>
          <label style={labelCss}>SEO Description</label>
          <textarea
            style={{ ...inputCss, minHeight: 88 }}
            maxLength={MAX_SEO_DESCRIPTION_LENGTH}
            placeholder="Short description used in Google, Open Graph, and Twitter search previews."
            value={seoDescription}
            onChange={(e) =>
              setSeoDescription(
                normalizeSeoTextInput(
                  e.target.value,
                  MAX_SEO_DESCRIPTION_LENGTH
                )
              )
            }
          />
          <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
            Short description used in search results and social sharing. Falls
            back to the product description when empty.
          </div>
        </div>
        <div style={row}>
          <label style={labelCss}>SEO Keywords</label>
          <textarea
            style={{ ...inputCss, minHeight: 96 }}
            placeholder="custom pendant, photo pendant, valentines pendant gift"
            value={seoKeywordsStr}
            onChange={(e) => setSeoKeywordsStr(e.target.value)}
          />
          <div style={{ color: "#64748b", fontSize: 12, lineHeight: 1.5 }}>
            Used for structured data and meta keywords. Comma or new line
            separated, optional, deduped automatically, up to 100 keywords and
            50 characters per keyword.
          </div>
        </div>
      </div>

      {/* Dimensions */}
      {showDimensions ? (
        <>
          <div style={grid3}>
            <div style={row}>
              <label style={labelCss}>Width (cm)</label>
              <input
                style={inputCss}
                type="number"
                min={0}
                step="0.01"
                value={dimensions.width ?? ""}
                onChange={(e) => setDim("width", e.target.value)}
                placeholder="e.g., 5.5"
              />
            </div>
            <div style={row}>
              <label style={labelCss}>Height (cm)</label>
              <input
                style={inputCss}
                type="number"
                min={0}
                step="0.01"
                value={dimensions.height ?? ""}
                onChange={(e) => setDim("height", e.target.value)}
                placeholder="e.g., 7"
              />
            </div>
            <div style={row}>
              <label style={labelCss}>Depth (cm)</label>
              <input
                style={inputCss}
                type="number"
                min={0}
                step="0.01"
                value={dimensions.depth ?? ""}
                onChange={(e) => setDim("depth", e.target.value)}
                placeholder="e.g., 1.2"
              />
            </div>
          </div>

          <div style={grid3}>
            <div style={row}>
              <label style={labelCss}>Diameter (cm)</label>
              <input
                style={inputCss}
                type="number"
                min={0}
                step="0.01"
                value={dimensions.diameter ?? ""}
                onChange={(e) => setDim("diameter", e.target.value)}
                placeholder="For round items"
              />
              <div style={{ fontSize: 12, color: "#666", marginTop: 6 }}>
                Leave width/height/depth empty when using diameter, and vice-versa.
              </div>
            </div>
            <div />
            <div />
          </div>
        </>
      ) : null}

      {/* Category section */}
      {category ? renderCategoryFields() : null}

      {/* Images */}
      <div style={card}>
        <div style={sectionTitle}>Images</div>

        <div style={grid3}>
          <div style={row}>
            <label style={labelCss}>Hero photo</label>
            <input type="file" accept="image/*" onChange={onPickHero} />
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                flexWrap: "wrap",
              }}
            >
              {heroSrc ? (
                <img src={heroSrc} alt="hero" style={thumb} />
              ) : (
                <div
                  style={{
                    ...thumb,
                    display: "grid",
                    placeItems: "center",
                    color: "#bbb",
                  }}
                >
                  —
                </div>
              )}
              {(images.heroImage || images.heroImageFile) && (
                <button type="button" style={outlineBtn} onClick={onRemoveHero}>
                  Remove
                </button>
              )}
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Used on cards, featured sections, and product sharing previews when
              available.
            </div>
          </div>

          <div style={row}>
            <label style={labelCss}>YouTube product video</label>
            <input
              type="url"
              placeholder="https://www.youtube.com/watch?v=..."
              value={images.youtubeVideoUrl || ""}
              onChange={(event) =>
                setImages((prev) => ({
                  ...prev,
                  youtubeVideoUrl: event.target.value,
                  useYoutubeAsThumbnail: event.target.value.trim()
                    ? prev.useYoutubeAsThumbnail
                    : false,
                }))
              }
            />
            <label
              style={{
                display: "flex",
                gap: 8,
                alignItems: "center",
                color: "#334155",
                fontSize: 13,
              }}
            >
              <input
                type="checkbox"
                checked={Boolean(
                  images.useYoutubeAsThumbnail && images.youtubeVideoUrl?.trim(),
                )}
                disabled={!images.youtubeVideoUrl?.trim()}
                onChange={(event) =>
                  setImages((prev) => ({
                    ...prev,
                    useYoutubeAsThumbnail: event.target.checked,
                  }))
                }
              />
              Use this video as hover thumbnail on product cards
            </label>
            {getYoutubeEmbedUrl(images.youtubeVideoUrl) ? (
              <div style={{ aspectRatio: "16 / 9", border: "1px solid #e5e7eb" }}>
                <iframe
                  src={getYoutubeEmbedUrl(images.youtubeVideoUrl)}
                  title="YouTube product video preview"
                  style={{ width: "100%", height: "100%", border: 0 }}
                  loading="lazy"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                />
              </div>
            ) : (
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Add a YouTube link for product videos. When checked, product cards
                show a muted preview on hover.
              </div>
            )}
          </div>

          {category !== ProductCategory.Pendant ? (
            <div style={row}>
              <label style={labelCss}>
                {category === ProductCategory.Engraving
                  ? "Front plate image"
                  : "Preview image"}
              </label>
              <input type="file" accept="image/*" onChange={onPickPreview} />
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                {previewSrc ? (
                  <img src={previewSrc} alt="preview" style={thumb} />
                ) : (
                  <div
                    style={{
                      ...thumb,
                      display: "grid",
                      placeItems: "center",
                      color: "#bbb",
                    }}
                  >
                    —
                  </div>
                )}
                {(images.previewImage || images.previewImageFile) && (
                  <>
                    {category === ProductCategory.Engraving ? (
                      <button
                        type="button"
                        style={outlineBtn}
                        onClick={() => {
                          const source = resolveEngravingAreaEditorSource(
                            "front",
                            adminEngravingPreviewVariants
                          );
                          if (!source?.imageUrl) return;
                          openEngravingAreaEditor(source.imageUrl, {
                            objectUrlToRevoke: source.objectUrlToRevoke,
                            side: "front",
                            initialDraft: engravingAreaDraftFromArea(
                              adminSyncedEngraving.frontEngravingArea,
                              "front"
                            ),
                          });
                        }}
                      >
                        Draw Customizable Area
                      </button>
                    ) : null}
                    <button
                      type="button"
                      style={outlineBtn}
                      onClick={onRemovePreview}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
              {category === ProductCategory.Engraving ? (
                <div style={{ fontSize: 12, color: "#6b7280" }}>
                  This same front image is used for admin preview, area drawing, and
                  storefront customization.
                </div>
              ) : null}
            </div>
          ) : null}

          {category === ProductCategory.Engraving ? (
            <div style={row}>
              <label style={labelCss}>Back image</label>
              <input type="file" accept="image/*" onChange={onPickBack} />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                {backSrc ? (
                  <img src={backSrc} alt="back" style={thumb} />
                ) : (
                  <div
                    style={{
                      ...thumb,
                      display: "grid",
                      placeItems: "center",
                      color: "#bbb",
                    }}
                  >
                    —
                  </div>
                )}
                {(images.backImage || images.backImageFile) && (
                  <>
                    <button
                      type="button"
                      style={outlineBtn}
                      onClick={() => {
                        const source = resolveEngravingAreaEditorSource(
                          "back",
                          adminEngravingPreviewVariants
                        );
                        if (!source?.imageUrl) return;
                        openEngravingAreaEditor(source.imageUrl, {
                          objectUrlToRevoke: source.objectUrlToRevoke,
                          side: "back",
                          initialDraft: engravingAreaDraftFromArea(
                            adminSyncedEngraving.backEngravingArea,
                            "back"
                          ),
                        });
                      }}
                    >
                      Draw Back Area
                    </button>
                    <button
                      type="button"
                      style={outlineBtn}
                      onClick={onRemoveBack}
                    >
                      Remove
                    </button>
                  </>
                )}
              </div>
              <div style={{ fontSize: 12, color: "#6b7280" }}>
                Use the same back image for preview and optional back-side engraving.
              </div>
            </div>
          ) : category !== ProductCategory.Pendant ? (
            <div style={row}>
              <label style={labelCss}>AR image / 3D</label>
              <input
                type="file"
                accept="image/*,.glb,.usdz"
                onChange={onPickAR}
              />
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 10,
                  flexWrap: "wrap",
                }}
              >
                {arSrc ? (
                  <img src={arSrc} alt="ar" style={thumb} />
                ) : (
                  <>
                    {/* Show chip if we have a non-image AR asset (glb/usdz) */}
                    {images.arImage && isThreeUrl(images.arImage) ? (
                      <a
                        href={assetUrl(images.arImage)}
                        target="_blank"
                        rel="noreferrer"
                        style={{
                          padding: "8px 10px",
                          border: "1px solid #ddd",
                          borderRadius: 999,
                          textDecoration: "none",
                          color: "#111",
                          background: "#fafafa",
                          fontSize: 12,
                        }}
                        title="Open AR asset"
                      >
                        {images.arImage.split("/").pop()}
                      </a>
                    ) : (
                      <div
                        style={{
                          ...thumb,
                          display: "grid",
                          placeItems: "center",
                          color: "#bbb",
                        }}
                      >
                        —
                      </div>
                    )}
                  </>
                )}
                {(images.arImage || images.arImageFile) && (
                  <button type="button" style={outlineBtn} onClick={onRemoveAR}>
                    Remove
                  </button>
                )}
              </div>
            </div>
          ) : category === ProductCategory.Pendant ? (
            <>
              <fieldset style={row}>
                <legend style={labelCss}>Optional pendant 3D model</legend>
                <p style={helperTextCss}>Upload a pendant-only GLB, front facing +Z, upright +Y. Existing photo customization remains available without a model. Maximum 25 MB.</p>
                <label>GLB file<input type="file" accept=".glb,model/gltf-binary" onChange={(event) => {
                  const file = event.target.files?.[0];
                  if (!file) return;
                  if (!/\.glb$/i.test(file.name) || file.size > 25 * 1024 * 1024) {
                    setModel3dError("Choose a GLB file no larger than 25 MB.");
                    event.target.value = ""; return;
                  }
                  setModel3dFile(file); setClearModel3d(false); setModel3dError("");
                }} /></label>
                {model3dError && <p role="alert">{model3dError}</p>}
                {(model3dFile || model3dUrl) && !clearModel3d && <div>
                  {model3dFile?.name || model3dUrl?.split("/").pop()}
                  <button type="button" style={outlineBtn} onClick={() => {
                    setModel3dFile(undefined); setClearModel3d(true);
                  }}>Remove 3D model</button>
                </div>}
                <label>Physical pendant width / diameter (mm)
                  <input type="number" min={1} max={500} step="0.1" value={model3dWidthMm ?? ""}
                    onChange={(e) => setModel3dWidthMm(e.target.value ? Number(e.target.value) : undefined)} />
                </label>
                <p style={helperTextCss}>Use the full wing span for wing pendants, or the diameter for round pendants (e.g. 70). Exclude the chain.</p>
                <label>Photo material name in GLB
                  <input value={model3dPhotoMaterial} onChange={(e) => setModel3dPhotoMaterial(e.target.value)} />
                </label>
                <p style={helperTextCss}>The photo surface must have its own UV-mapped material with this exact name. Customers’ cropped photos replace this material. </p>
                <label>Engraving material name in GLB
                  <input value={model3dEngravingMaterial} onChange={(e) => setModel3dEngravingMaterial(e.target.value)} />
                </label>
                <p style={helperTextCss}>Use a separate UV-mapped back engraving surface (default: Engraving). Map the full texture square onto the engraving area. Customer text, font, size and position appear here in 3D. Camera try-on is no longer offered.</p>
              </fieldset>
              <div style={row}>
                <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                  <div>
                    <div style={labelCss}>Legacy try-on assets (not shown to customers)</div>
                    <div style={helperTextCss}>
                      Select an existing product image or upload a transparent PNG
                      that includes the chain and pendant. Virtual try-on
                      calibration stays separate from the photo print area.
                    </div>
                    <div style={{ ...helperTextCss, marginTop: 4 }}>
                      Add separate try-on views for each pendant color and for front/back sides.
                    </div>
                  </div>
                  <label style={switchRow}>
                    <input
                      type="checkbox"
                      checked={Boolean(activeVirtualTryOn.enabled)}
                      disabled={!virtualTryOnAssetSrc}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          enabled: event.target.checked,
                        })
                      }
                    />
                    <span style={labelCss}>Enabled</span>
                  </label>
                </div>

                <div style={{ display: "grid", gap: 8 }}>
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    <button
                      type="button"
                      onClick={() => setActiveVirtualTryOnIndex(-1)}
                      style={{
                        ...outlineBtn,
                        border:
                          activeVirtualTryOnIndex === -1
                            ? "2px solid #111827"
                            : outlineBtn.border,
                      }}
                    >
                      Default try-on
                    </button>
                    {virtualTryOnVariants.map((variant, index) => (
                      <button
                        key={`try-on-variant-${index}`}
                        type="button"
                        onClick={() => setActiveVirtualTryOnIndex(index)}
                        style={{
                          ...outlineBtn,
                          border:
                            activeVirtualTryOnIndex === index
                              ? "2px solid #111827"
                              : outlineBtn.border,
                        }}
                      >
                        {variant.label ||
                          [variant.color, variant.side]
                            .filter(Boolean)
                            .join(" ") ||
                          `Try-on ${index + 1}`}
                      </button>
                    ))}
                    <button type="button" style={outlineBtn} onClick={addVirtualTryOnVariant}>
                      Add color/side try-on
                    </button>
                    {activeVirtualTryOnIndex >= 0 ? (
                      <button
                        type="button"
                        style={{ ...outlineBtn, color: "#b91c1c", border: "1px solid #fecaca" }}
                        onClick={removeActiveVirtualTryOnVariant}
                      >
                        Remove selected
                      </button>
                    ) : null}
                  </div>

                  <div style={grid3}>
                    <label style={row}>
                      <span style={labelCss}>Try-on label</span>
                      <input
                        style={inputCss}
                        value={activeVirtualTryOn.label || ""}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            label: event.target.value,
                          })
                        }
                        placeholder="Gold front, Silver back..."
                      />
                    </label>
                    <label style={row}>
                      <span style={labelCss}>Applies to color</span>
                      <input
                        style={inputCss}
                        value={activeVirtualTryOn.color || ""}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            color: event.target.value,
                          })
                        }
                        placeholder="Leave blank for all colors"
                      />
                    </label>
                    <label style={row}>
                      <span style={labelCss}>Side</span>
                      <select
                        style={inputCss}
                        value={activeVirtualTryOn.side || "default"}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            side: event.target.value as VirtualTryOnConfig["side"],
                          })
                        }
                      >
                        <option value="default">Default / any side</option>
                        <option value="front">Front</option>
                        <option value="back">Back</option>
                      </select>
                    </label>
                  </div>
                </div>

                <label style={row}>
                  <span style={labelCss}>Use existing product image</span>
                  <select
                    style={inputCss}
                    value={
                      activeVirtualTryOnIndex < 0 && images.virtualTryOnAssetFile
                        ? ""
                        : String(activeVirtualTryOn.assetUrl || "")
                    }
                    disabled={!existingVirtualTryOnImageOptions.length}
                    onChange={(event) => {
                      const nextAssetUrl = event.target.value;
                      if (!nextAssetUrl) return;
                      const selectedOption = existingVirtualTryOnImageOptions.find(
                        (option) => option.value === nextAssetUrl
                      );
                      if (activeVirtualTryOnIndex < 0) {
                        setImages((current) => ({
                          ...current,
                          virtualTryOnAssetFile: null,
                        }));
                      }
                      updateActiveVirtualTryOn({
                        ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                        assetUrl: nextAssetUrl,
                        enabled: true,
                        label: selectedOption?.label || activeVirtualTryOn.label,
                        color: selectedOption?.color || activeVirtualTryOn.color,
                        side: selectedOption?.side || activeVirtualTryOn.side || "front",
                      });
                    }}
                  >
                    <option value="">
                      {existingVirtualTryOnImageOptions.length
                        ? "Select saved image..."
                        : "No saved product images available"}
                    </option>
                    {existingVirtualTryOnImageOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                  <span style={helperTextCss}>
                    Use this when the same pendant image already has the
                    customer photo area configured. You can still set separate
                    Virtual Try-On anchors and measurement here.
                  </span>
                </label>

                {existingVirtualTryOnImageOptions.length ? (
                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                    {existingVirtualTryOnImageOptions.map((option) => (
                      <button
                        key={option.value}
                        type="button"
                        title={option.label}
                        onClick={() => {
                          if (activeVirtualTryOnIndex < 0) {
                            setImages((current) => ({
                              ...current,
                              virtualTryOnAssetFile: null,
                            }));
                          }
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            assetUrl: option.value,
                            enabled: true,
                            label: option.label,
                            color: option.color || activeVirtualTryOn.color,
                            side: option.side,
                          });
                        }}
                        style={{
                          border:
                            activeVirtualTryOn.assetUrl === option.value &&
                            !(activeVirtualTryOnIndex < 0 && images.virtualTryOnAssetFile)
                              ? "2px solid #111827"
                              : "1px solid #d1d5db",
                          borderRadius: 10,
                          background: "#fff",
                          padding: 4,
                          display: "grid",
                          gap: 4,
                          width: 84,
                          textAlign: "center",
                          cursor: "pointer",
                        }}
                      >
                        <img
                          src={option.src}
                          alt=""
                          style={{
                            width: "100%",
                            aspectRatio: "1 / 1",
                            objectFit: "contain",
                            background: "#f8fafc",
                            borderRadius: 8,
                          }}
                        />
                        <span
                          style={{
                            fontSize: 10,
                            lineHeight: 1.15,
                            color: "#475569",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {option.label}
                        </span>
                      </button>
                    ))}
                  </div>
                ) : null}

                {activeVirtualTryOnIndex < 0 ? (
                  <label style={row}>
                    <span style={labelCss}>Or upload separate try-on PNG</span>
                    <input
                      type="file"
                      accept="image/png,image/webp,image/*"
                      onChange={onPickVirtualTryOnAsset}
                    />
                  </label>
                ) : (
                  <div style={helperTextCss}>
                    Variant try-on views use saved product images. Upload the color/front/back image above first, then select it here.
                  </div>
                )}

                <div
                  style={{
                    display: "flex",
                    gap: 12,
                    flexWrap: "wrap",
                    alignItems: "flex-start",
                  }}
                >
                  <VirtualTryOnAnchorEditor
                    imageUrl={virtualTryOnAssetSrc}
                    value={activeVirtualTryOn}
                    onChange={updateActiveVirtualTryOn}
                  />
                  {(activeVirtualTryOn.assetUrl ||
                    (activeVirtualTryOnIndex < 0 && images.virtualTryOnAssetFile)) ? (
                    <button
                      type="button"
                      style={outlineBtn}
                      onClick={() => {
                        if (activeVirtualTryOnIndex < 0) {
                          onRemoveVirtualTryOnAsset();
                          return;
                        }
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          enabled: false,
                          assetUrl: undefined,
                        });
                      }}
                    >
                      Remove try-on asset
                    </button>
                  ) : null}
                </div>

                <div style={grid2}>
                  <label style={row}>
                    <span style={labelCss}>
                      Approximate fallback scale ({Number(activeVirtualTryOn.defaultScale ?? 1).toFixed(2)})
                    </span>
                    <input
                      type="range"
                      min={0.6}
                      max={1.6}
                      step={0.01}
                      value={activeVirtualTryOn.defaultScale ?? 1}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          defaultScale: Number(event.target.value),
                        })
                      }
                    />
                    <span style={helperTextCss}>
                      Ignored when A/B physical measurement or pendant millimeter size is set.
                    </span>
                  </label>
                  <label style={row}>
                    <span style={labelCss}>
                      Default vertical offset (
                      {Number(
                        activeVirtualTryOn.defaultVerticalOffset ??
                          activeVirtualTryOn.verticalOffset ??
                          0
                      ).toFixed(2)}
                      )
                    </span>
                    <input
                      type="range"
                      min={-0.25}
                      max={0.25}
                      step={0.005}
                      value={
                        activeVirtualTryOn.defaultVerticalOffset ??
                        activeVirtualTryOn.verticalOffset ??
                        0
                      }
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          defaultVerticalOffset: Number(event.target.value),
                          verticalOffset: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label style={row}>
                    <span style={labelCss}>
                      Default horizontal offset (
                      {Number(
                        activeVirtualTryOn.defaultHorizontalOffset ??
                          activeVirtualTryOn.horizontalOffset ??
                          0
                      ).toFixed(2)}
                      )
                    </span>
                    <input
                      type="range"
                      min={-0.25}
                      max={0.25}
                      step={0.005}
                      value={
                        activeVirtualTryOn.defaultHorizontalOffset ??
                        activeVirtualTryOn.horizontalOffset ??
                        0
                      }
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          defaultHorizontalOffset: Number(event.target.value),
                          horizontalOffset: Number(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label style={row}>
                    <span style={labelCss}>
                      Default rotation correction (
                      {Number(
                        activeVirtualTryOn.defaultRotationOffset ??
                          activeVirtualTryOn.rotationOffset ??
                          0
                      ).toFixed(1)}
                      °)
                    </span>
                    <input
                      type="range"
                      min={-20}
                      max={20}
                      step={0.5}
                      value={
                        activeVirtualTryOn.defaultRotationOffset ??
                        activeVirtualTryOn.rotationOffset ??
                        0
                      }
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          defaultRotationOffset: Number(event.target.value),
                          rotationOffset: Number(event.target.value),
                        })
                      }
                      />
                  </label>
                </div>

                <div
                  style={{
                    border: "1px solid #dbeafe",
                    borderRadius: 12,
                    background: "#eff6ff",
                    padding: 12,
                    display: "grid",
                    gap: 10,
                  }}
                >
                  <div>
                    <div style={{ ...labelCss, color: "#1e3a8a" }}>
                      Wear Position
                    </div>
                    <div style={helperTextCss}>
                      Product-specific corrections applied after body placement.
                      Millimeter offsets use the current camera px/mm estimate.
                    </div>
                  </div>
                  <div style={grid2}>
                    <label style={row}>
                      <span style={labelCss}>Neck X offset (mm)</span>
                      <input
                        style={inputCss}
                        type="number"
                        step={1}
                        value={activeVirtualTryOn.wearOffsetXMm ?? ""}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            wearOffsetXMm: num(event.target.value),
                          })
                        }
                        placeholder="0"
                      />
                    </label>
                    <label style={row}>
                      <span style={labelCss}>Neck Y offset (mm)</span>
                      <input
                        style={inputCss}
                        type="number"
                        step={1}
                        value={activeVirtualTryOn.wearOffsetYMm ?? ""}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            wearOffsetYMm: num(event.target.value),
                          })
                        }
                        placeholder="0"
                      />
                    </label>
                    <label style={row}>
                      <span style={labelCss}>Wear drop to pendant top (mm)</span>
                      <input
                        style={inputCss}
                        type="number"
                        min={0}
                        step={1}
                        value={activeVirtualTryOn.wearDropMm ?? ""}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            wearDropMm: num(event.target.value),
                          })
                        }
                        placeholder="65"
                      />
                    </label>
                    <label style={row}>
                      <span style={labelCss}>Rotation correction (deg)</span>
                      <input
                        style={inputCss}
                        type="number"
                        step={0.5}
                        value={activeVirtualTryOn.wearRotationDeg ?? ""}
                        onChange={(event) =>
                          updateActiveVirtualTryOn({
                            ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                            wearRotationDeg: num(event.target.value),
                          })
                        }
                        placeholder="0"
                      />
                    </label>
                  </div>
                </div>

                <div style={grid3}>
                  <label style={row}>
                    <span style={labelCss}>Pendant width (mm)</span>
                    <input
                      style={inputCss}
                      type="number"
                      min={0}
                      step={0.1}
                      value={activeVirtualTryOn.pendantWidthMm ?? ""}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          pendantWidthMm: num(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label style={row}>
                    <span style={labelCss}>Pendant height (mm)</span>
                    <input
                      style={inputCss}
                      type="number"
                      min={0}
                      step={0.1}
                      value={activeVirtualTryOn.pendantHeightMm ?? ""}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          pendantHeightMm: num(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label style={row}>
                    <span style={labelCss}>Thickness (mm)</span>
                    <input
                      style={inputCss}
                      type="number"
                      min={0}
                      step={0.1}
                      value={activeVirtualTryOn.pendantThicknessMm ?? ""}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          pendantThicknessMm: num(event.target.value),
                        })
                      }
                    />
                  </label>
                </div>

                <div style={grid3}>
                  <label style={switchRow}>
                    <input
                      type="checkbox"
                      checked={activeVirtualTryOn.chainIncluded !== false}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          chainIncluded: event.target.checked,
                        })
                      }
                    />
                    <span style={labelCss}>Chain included in try-on asset</span>
                  </label>
                  <label style={row}>
                    <span style={labelCss}>Chain length (in)</span>
                    <input
                      style={inputCss}
                      type="number"
                      min={0}
                      step={0.25}
                      value={activeVirtualTryOn.chainLengthInches ?? ""}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          chainLengthInches: num(event.target.value),
                        })
                      }
                    />
                  </label>
                  <label style={row}>
                    <span style={labelCss}>Chain length (mm)</span>
                    <input
                      style={inputCss}
                      type="number"
                      min={0}
                      step={1}
                      value={activeVirtualTryOn.chainLengthMm ?? ""}
                      onChange={(event) =>
                        updateActiveVirtualTryOn({
                          ...defaultVirtualTryOnConfig(activeVirtualTryOn),
                          chainLengthMm: num(event.target.value),
                        })
                      }
                    />
                  </label>
                </div>

                <div style={helperTextCss}>
                  Asset geometry:{" "}
                  {activeVirtualTryOn.imageWidthPx && activeVirtualTryOn.imageHeightPx
                    ? `${activeVirtualTryOn.imageWidthPx} x ${activeVirtualTryOn.imageHeightPx}px`
                    : "image dimensions will appear after analysis"}
                  {activeVirtualTryOn.assetPixelsPerMm
                    ? ` | ${Number(activeVirtualTryOn.assetPixelsPerMm).toFixed(3)} px/mm`
                    : ""}
                </div>
              </div>

              <div style={row}>
                <label style={labelCss}>More photos (preview only)</label>
                <input
                  type="file"
                  multiple
                  accept="image/*"
                  onChange={onPickMore}
                />
              </div>
            </>
          ) : (
            <div style={row}>
              <label style={labelCss}>More photos (preview only)</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={onPickMore}
              />
            </div>
          )}

          {/* Gallery add */}
          {category !== ProductCategory.Pendant ? (
            <div style={row}>
              <label style={labelCss}>More images</label>
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={onPickMore}
              />
            </div>
          ) : null}
        </div>

        {/* Existing gallery */}
        <div style={galleryWrap}>
          {images.moreImages.map((u) => (
            <div key={u} style={{ display: "grid", gap: 6 }}>
              <img src={assetUrl(u)} alt="" style={thumb} />
              <button
                type="button"
                style={outlineBtn}
                onClick={() => removeExistingGallery(u)}
              >
                Remove
              </button>
            </div>
          ))}
          {/* Newly picked gallery files (not yet uploaded) */}
          {images.moreImageFiles.map((f, idx) => {
            const url = URL.createObjectURL(f);
            return (
              <div key={`${f.name}-${idx}`} style={{ display: "grid", gap: 6 }}>
                <img src={url} alt="" style={thumb} />
                <button
                  type="button"
                  style={outlineBtn}
                  onClick={() => removePickedGallery(idx)}
                >
                  Remove
                </button>
              </div>
            );
          })}
        </div>
      </div>

      {maskEditorOpen && maskEditorImageUrl ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "grid",
            placeItems: "center",
            zIndex: 80,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(92vw, 560px)",
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
              padding: 14,
              display: "grid",
              gap: 12,
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                {maskEditorTargetSide === "back"
                  ? "Draw Pendant Back Engraving Area"
                  : "Draw Pendant Front Print Area"}
                {maskEditorTargetColor ? ` (${maskEditorTargetColor})` : ""}
              </div>
              <button
                type="button"
                style={outlineBtn}
                onClick={closePendantAreaEditor}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gap: 10,
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 10,
                background: "#f8fafc",
              }}
            >
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                <button
                  type="button"
                  style={{
                    ...outlineBtn,
                    background:
                      maskEditorDraft.shape === "circle" ? "#111827" : "#fff",
                    color:
                      maskEditorDraft.shape === "circle" ? "#fff" : "#111827",
                    borderColor:
                      maskEditorDraft.shape === "circle" ? "#111827" : "#d1d5db",
                  }}
                  onClick={() =>
                    setMaskEditorDraft((prev) =>
                      normalizeCircleAreaDraft({
                        ...prev,
                        shape: "circle",
                        lockAspectRatio: true,
                      })
                    )
                  }
                >
                  Circle
                </button>
                <button
                  type="button"
                  style={{
                    ...outlineBtn,
                    background:
                      maskEditorDraft.shape === "heart" ? "#111827" : "#fff",
                    color:
                      maskEditorDraft.shape === "heart" ? "#fff" : "#111827",
                    borderColor:
                      maskEditorDraft.shape === "heart" ? "#111827" : "#d1d5db",
                  }}
                  onClick={() =>
                    setMaskEditorDraft((prev) =>
                      normalizeCircleAreaDraft({
                        ...prev,
                        shape: "heart",
                      })
                    )
                  }
                >
                  Heart
                </button>
                <label
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    marginLeft: 4,
                    fontSize: 12,
                    color: "#334155",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={maskEditorDraft.lockAspectRatio}
                    onChange={(e) =>
                      setMaskEditorDraft((prev) =>
                        normalizeCircleAreaDraft({
                          ...prev,
                          lockAspectRatio: e.target.checked,
                        })
                      )
                    }
                  />
                  Lock Aspect Ratio
                </label>
              </div>

              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 12, color: "#334155" }}>
                  Width: {maskEditorDraft.widthPct.toFixed(2)}%
                  <input
                    type="range"
                    min={5}
                    max={95}
                    step={0.1}
                    value={maskEditorDraft.widthPct}
                    onChange={(e) =>
                      setMaskEditorDraft((prev) =>
                        applyCircleAreaWidthValue(prev, Number(e.target.value))
                      )
                    }
                  />
                </label>
                <label style={{ fontSize: 12, color: "#334155" }}>
                  Height: {maskEditorDraft.heightPct.toFixed(2)}%
                  <input
                    type="range"
                    min={5}
                    max={95}
                    step={0.1}
                    value={maskEditorDraft.heightPct}
                    onChange={(e) =>
                      setMaskEditorDraft((prev) =>
                        applyCircleAreaHeightValue(prev, Number(e.target.value))
                      )
                    }
                  />
                </label>
              </div>

              {maskEditorDraft.shape === "heart" ? (
                <div style={{ display: "grid", gap: 8 }}>
                  <label style={{ fontSize: 12, color: "#334155" }}>
                    Heart Top Width: {maskEditorDraft.heartTopWidthPct.toFixed(2)}%
                    <input
                      type="range"
                      min={18}
                      max={70}
                      step={0.1}
                      value={maskEditorDraft.heartTopWidthPct}
                      onChange={(e) =>
                        setMaskEditorDraft((prev) =>
                          normalizeCircleAreaDraft({
                            ...prev,
                            heartTopWidthPct: Number(e.target.value),
                          })
                        )
                      }
                    />
                  </label>
                  <label style={{ fontSize: 12, color: "#334155" }}>
                    Heart Notch Depth: {maskEditorDraft.heartNotchDepthPct.toFixed(2)}%
                    <input
                      type="range"
                      min={8}
                      max={55}
                      step={0.1}
                      value={maskEditorDraft.heartNotchDepthPct}
                      onChange={(e) =>
                        setMaskEditorDraft((prev) =>
                          normalizeCircleAreaDraft({
                            ...prev,
                            heartNotchDepthPct: Number(e.target.value),
                          })
                        )
                      }
                    />
                  </label>
                  <label style={{ fontSize: 12, color: "#334155" }}>
                    Heart Notch Width: {maskEditorDraft.heartNotchWidthPct.toFixed(2)}%
                    <input
                      type="range"
                      min={2}
                      max={24}
                      step={0.1}
                      value={maskEditorDraft.heartNotchWidthPct}
                      onChange={(e) =>
                        setMaskEditorDraft((prev) =>
                          normalizeCircleAreaDraft({
                            ...prev,
                            heartNotchWidthPct: Number(e.target.value),
                          })
                        )
                      }
                    />
                  </label>
                </div>
              ) : null}

            </div>

            <CircleAreaEditor
              imageUrl={maskEditorImageUrl}
              value={maskEditorDraft}
              onChange={setMaskEditorDraft}
            />

            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Drag inside the selected shape to move it. Drag the bottom-right
              handle to resize. Hold Shift while resizing to force a perfect
              circle. Double-click anywhere to recenter.
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Shape: {maskEditorDraft.shape === "heart" ? "Heart" : "Circle"} |{" "}
              Space: {maskEditorDraft.coordinateSpace} |{" "}
              X: {maskEditorDraft.xPct.toFixed(2)}% | Y:{" "}
              {maskEditorDraft.yPct.toFixed(2)}% | W:{" "}
              {maskEditorDraft.widthPct.toFixed(2)}% | H:{" "}
              {maskEditorDraft.heightPct.toFixed(2)}%
              {maskEditorDraft.shape === "circle" &&
              Number.isFinite(maskEditorDraft.centerXPct) &&
              Number.isFinite(maskEditorDraft.centerYPct) &&
              Number.isFinite(maskEditorDraft.radiusPct)
                ? ` | Cx: ${Number(maskEditorDraft.centerXPct).toFixed(
                    2
                  )}% | Cy: ${Number(maskEditorDraft.centerYPct).toFixed(
                    2
                  )}% | Radius: ${Number(maskEditorDraft.radiusPct).toFixed(2)}%`
                : ""}
              {maskEditorDraft.shape === "heart"
                ? ` | Top W: ${maskEditorDraft.heartTopWidthPct.toFixed(
                    2
                  )}% | Notch D: ${maskEditorDraft.heartNotchDepthPct.toFixed(
                    2
                  )}% | Notch W: ${maskEditorDraft.heartNotchWidthPct.toFixed(2)}%`
                : ""}
            </div>

            <div style={actions}>
              <button
                type="button"
                style={outlineBtn}
                onClick={closePendantAreaEditor}
              >
                Cancel
              </button>
              <button
                type="button"
                style={primaryBtn}
                onClick={applyPendantAreaEditor}
              >
                Apply Area
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {engravingAreaEditorOpen && engravingAreaEditorImageUrl ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15, 23, 42, 0.55)",
            display: "grid",
            placeItems: "center",
            zIndex: 80,
            padding: 16,
          }}
        >
          <div
            style={{
              width: "min(92vw, 560px)",
              background: "#fff",
              borderRadius: 14,
              border: "1px solid #e5e7eb",
              boxShadow: "0 20px 50px rgba(15,23,42,0.25)",
              padding: 14,
              display: "grid",
              gap: 12,
              maxHeight: "calc(100vh - 32px)",
              overflowY: "auto",
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                Draw Engraving Customizable Area (
                {engravingAreaEditorTargetSide === "back" ? "Back" : "Front"})
              </div>
              <button
                type="button"
                style={outlineBtn}
                onClick={closeEngravingAreaEditor}
              >
                Close
              </button>
            </div>

            <div
              style={{
                display: "grid",
                gap: 10,
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 10,
                background: "#f8fafc",
              }}
            >
              <label
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: 6,
                  fontSize: 12,
                  color: "#334155",
                }}
              >
                <input
                  type="checkbox"
                  checked={engravingAreaEditorDraft.lockSquare}
                  onChange={(e) =>
                    setEngravingAreaEditorDraft((prev) =>
                      normalizeEngravingAreaDraft(
                        {
                          ...prev,
                          lockSquare: e.target.checked,
                          ...(e.target.checked
                            ? {
                                heightPct: prev.widthPct,
                              }
                            : {}),
                        },
                        prev.side
                      )
                    )
                  }
                />
                Keep square while resizing
              </label>

              <div style={{ display: "grid", gap: 8 }}>
                <label style={{ fontSize: 12, color: "#334155" }}>
                  Width: {Number(engravingAreaEditorDraft.widthPct ?? 0).toFixed(2)}%
                  <input
                    type="range"
                    min={5}
                    max={95}
                    step={0.1}
                    value={engravingAreaEditorDraft.widthPct ?? 70}
                    onChange={(e) =>
                      setEngravingAreaEditorDraft((prev) =>
                        applyEngravingAreaWidthValue(prev, Number(e.target.value))
                      )
                    }
                  />
                </label>
                <label style={{ fontSize: 12, color: "#334155" }}>
                  Height: {Number(engravingAreaEditorDraft.heightPct ?? 0).toFixed(2)}%
                  <input
                    type="range"
                    min={5}
                    max={95}
                    step={0.1}
                    value={engravingAreaEditorDraft.heightPct ?? 22}
                    onChange={(e) =>
                      setEngravingAreaEditorDraft((prev) =>
                        applyEngravingAreaHeightValue(prev, Number(e.target.value))
                      )
                    }
                  />
                </label>
              </div>
            </div>

            <RectangleAreaEditor
              imageUrl={engravingAreaEditorImageUrl}
              value={engravingAreaEditorDraft}
              onChange={setEngravingAreaEditorDraft}
            />

            <div
              style={{
                display: "grid",
                gap: 10,
                border: "1px solid #e5e7eb",
                borderRadius: 10,
                padding: 10,
                background: "#fff",
              }}
            >
              <label style={{ ...labelCss, fontSize: 12 }}>
                Preview text for fit check
              </label>
              <input
                style={inputCss}
                value={engravingAreaEditorSampleText}
                onChange={(event) => setEngravingAreaEditorSampleText(event.target.value)}
                placeholder="Type sample text to check auto-fit size"
              />
              <EngravingAdminPreviewFace
                imageUrl={engravingAreaEditorImageUrl}
                area={{
                  ...sanitizeEngravingArea(
                    engravingAreaEditorDraft,
                    engravingAreaEditorTargetSide,
                  ),
                  side: engravingAreaEditorTargetSide,
                }}
                text={engravingAreaEditorSampleText}
                fontKey={String(engraving.defaultFont || "serif")}
                fontSize={engraving.defaultFontSize ?? 18}
                label="Live area fit preview"
                onSideLayout={(layout) => setEngravingAreaEditorLayout(layout)}
              />
              {engravingAreaEditorLayout ? (
                <div style={{ fontSize: 12, color: "#1e293b" }}>
                  Auto-fit size: {engravingAreaEditorLayout.fittedFontSize.toFixed(1)}px
                  {" • "}
                  Rendered: {engravingAreaEditorLayout.renderedFontSizePx.toFixed(1)}px
                  {" • "}
                  Lines: {engravingAreaEditorLayout.lineCount}
                </div>
              ) : null}
            </div>

            <div style={{ fontSize: 12, color: "#6b7280" }}>
              Drag inside the rectangle to move it. Drag the bottom-right handle to
              resize. Hold Shift while resizing to keep it square. Double-click to
              recenter it.
            </div>
            <div style={{ fontSize: 12, color: "#6b7280" }}>
              X: {Number(engravingAreaEditorDraft.xPct ?? 0).toFixed(2)}% | Y:{" "}
              {Number(engravingAreaEditorDraft.yPct ?? 0).toFixed(2)}% | W:{" "}
              {Number(engravingAreaEditorDraft.widthPct ?? 0).toFixed(2)}% | H:{" "}
              {Number(engravingAreaEditorDraft.heightPct ?? 0).toFixed(2)}%
            </div>

            <div style={actions}>
              <button
                type="button"
                style={outlineBtn}
                onClick={closeEngravingAreaEditor}
              >
                Cancel
              </button>
              <button
                type="button"
                style={primaryBtn}
                onClick={applyEngravingAreaEditor}
              >
                Apply Area
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Actions */}
      <div style={actions}>
        <button
          type="button"
          style={outlineBtn}
          onClick={() => window.history.back()}
        >
          Cancel
        </button>
        <button type="submit" style={primaryBtn} disabled={saving}>
          {saving
            ? "Saving…"
            : mode === "edit"
            ? "Save changes"
            : "Create product"}
        </button>
      </div>
    </form>
  );
}
